import React, { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

import { ImageDisplay } from "@/components/ui/image-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MultiSelectOrientation,
  toOrientationArray,
} from "@/components/ui/multi-select-orientation";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
} from "lucide-react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { toUrlSafeSlug } from "../../convex/lib/slug";
import { toast } from "sonner";
import { ImageUpload } from "./ui/image-upload";
import { MultiSelectTags } from "./ui/multi-select-tags";
import { TiptapEditor } from "./tiptap-editor";
import { useDebounce } from "@/hooks/use-debounce";
import { useVideoUpload } from "@/hooks/useVideoUpload";
import { Progress } from "@/components/ui/progress";
import { Video, X, Upload, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// component to display video thumbnail (webm/gif) or preview
function VideoThumbnailDisplay({
  thumbnailVideo,
  previewUrl,
  onRemove,
}: {
  thumbnailVideo?: {
    webm_key?: string; // optional for backward compatibility
    gif_key: string;
    original_filename: string;
  };
  previewUrl: string | null;
  onRemove: () => void;
}) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // get gif URL (primary - like tiktok thumbnails)
  const gifUrl = useQuery(
    api.r2.getVideoThumbnailUrl,
    thumbnailVideo?.gif_key
      ? { key: thumbnailVideo.gif_key, expiresIn: 60 * 60 * 24 }
      : "skip",
  );

  const handleRemoveClick = () => {
    // only show confirmation if there's an actual thumbnail (not just preview)
    if (thumbnailVideo) {
      setShowDeleteDialog(true);
    } else {
      // for preview only, remove immediately
      onRemove();
    }
  };

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    onRemove();
  };

  return (
    <>
      <div className="mb-3">
        <Label className="text-xs text-muted-foreground mb-2 block">
          {thumbnailVideo
            ? "Current GIF Thumbnail:"
            : "Selected Video Preview:"}
        </Label>
        <div className="relative w-full max-w-xs aspect-[9/16] rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
          {previewUrl ? (
            <video
              src={previewUrl}
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
          ) : thumbnailVideo ? (
            gifUrl && typeof gifUrl === "string" ? (
              <img
                src={gifUrl}
                alt={thumbnailVideo.original_filename}
                className="w-full h-full object-cover"
              />
            ) : gifUrl === undefined ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-4">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 text-gray-400 animate-spin" />
                  <p className="text-xs text-gray-500">Loading gif...</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-4">
                  <Video className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-xs text-gray-500">
                    {thumbnailVideo.original_filename}
                  </p>
                  <p className="text-xs text-red-500 mt-1">
                    Failed to load gif URL
                  </p>
                </div>
              </div>
            )
          ) : null}
          <button
            type="button"
            onClick={handleRemoveClick}
            className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
            aria-label="Remove thumbnail"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete GIF Thumbnail?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this GIF thumbnail? This action
              cannot be undone and the thumbnail will be removed from the
              property.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// property type definitions
type PropertyType =
  | "rentals"
  | "freehold_properties"
  | "freehold_villas"
  | "leasehold_villas"
  | "freehold_lands"
  | "leasehold_lands"
  | "freehold_commercial"
  | "leasehold_commercial";

// form data interfaces
interface BasePropertyData {
  property_name: string;
  property_name_idn?: string;
  description: string;
  description_idn?: string;
  subtitle?: string;
  subtitle_idn?: string;
  reff_id: string;
  latitude: string;
  longitude: string;
  map_url: string;
  area_id?: Id<"area">;
  slug: string;
  social_media_link?: string;
  // legal and zoning checklist, required before a listing can be posted
  shm_condition?: string;
  slf_pbg_condition?: string;
  zoning?: string;
  thumbnail_video?: {
    webm_key?: string; // optional for backward compatibility
    gif_key: string;
    original_filename: string;
  };
  featured: boolean;
  draft: boolean;
  hidden: boolean;
  price_hidden: boolean;
  tags: Id<"tag">[];
  images: Array<{
    key: string;
    filename: string;
    contentType?: string;
    size?: number;
    blurhash?: string;
  }>;
}

interface FacilitiesData {
  living_room_type?: string;
  dining_rooms?: number;
  kitchens?: number;
  extra_rooms?: number;
  electricity_power?: number;
  water_source?: string;
  parking?: number;
  internet?: boolean;
  swimming_pool?: boolean;
  storage?: boolean;
  garden?: boolean;
  security?: boolean;
  view?: string;
  furniture?: string;
}

interface RentalsData {
  bedrooms: string;
  bathrooms: string;
  land_size: string;
  build_size: string;
  price_per_year: string;
  minimum_duration: number;
  safety_deposit_amount: string;
  pet_allowed: string;
  orientation?: string[];
}

interface FreeholdPropertyData {
  bedrooms: string;
  bathrooms: string;
  build_size: string;
  land_size: string;
  year_built: number;
  total_price: string;
  dp_minimal: string;
  pks_bank: string;
  orientation?: string[];
}

interface FreeholdVillaData {
  bedrooms: string;
  bathrooms: string;
  build_size: string;
  land_size: string;
  year_built: number;
  total_price: string;
  dp_minimal: string;
  pks_bank: string;
  orientation?: string[];
}

interface LeaseholdVillaData {
  bedrooms: string;
  bathrooms: string;
  build_size: string;
  land_size: string;
  year_built: number;
  price_for_duration: string;
  extension_duration?: number;
  leasehold_duration: number;
  orientation?: string[];
}

interface FreeholdLandData {
  land_size: string;
  price_per_are: string;
  total_price: string;
  road_access_width: string;
  land_zoning: string;
  view: string;
  orientation?: string[];
}

interface LeaseholdLandData {
  total_price: string;
  lease_duration: number;
  land_size: string;
  price_per_are: string;
  land_zoning: string;
  view: string;
  orientation?: string[];
}

interface FreeholdCommercialPropertyData {
  bedrooms: string;
  bathrooms: string;
  land_size: string;
  build_size: string;
  title: string;
  building_permit: string;
  frontage: string;
  orientation: string[];
  accessibility: string;
  year_built: number;
  bank_partnership: string;
  down_payment: string;
  total_price: string;
}

interface LeaseholdCommercialPropertyData {
  bedrooms: string;
  bathrooms: string;
  land_size: string;
  build_size: string;
  title: string;
  building_permit: string;
  frontage: string;
  orientation: string[];
  accessibility: string;
  year_built: number;
  price_for_duration: string;
  extension_duration: string;
  leasehold_duration: string;
}

interface MultiStepPropertyFormProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit" | "view";
  propertyId?: Id<"base_properties">;
  draftId?: Id<"property_drafts">;
  draftData?: {
    base: BasePropertyData;
    types: {
      selected: PropertyType[];
      data: Record<PropertyType, any>;
    };
    facilities: FacilitiesData;
    contact: {
      name: string;
      phone: string;
      email: string;
      country: string;
      address: string;
      qualification?: string;
      group_id?: string;
      type_id?: string;
      note?: string;
    };
    step: number;
  };
}

const STEPS = [
  {
    id: 1,
    title: "Basic Information",
    description: "Property name, location, and basic details",
  },
  {
    id: 2,
    title: "Property Type",
    description: "Select the property category",
  },
  {
    id: 3,
    title: "Type Details",
    description: "Specific information for your property type",
  },
  {
    id: 4,
    title: "Facilities",
    description: "Property facilities and amenities",
  },
  {
    id: 5,
    title: "Images",
    description: "Upload property photos",
  },
  { id: 6, title: "Review", description: "Review and submit your property" },
  {
    id: 7,
    title: "Contact (Optional)",
    description: "Add a contact for this property",
  },
];

// ---------------------------------------------------------------------------
// zod schemas for type details validation
// ---------------------------------------------------------------------------
const currentYear = new Date().getFullYear();

// step 1: basic info
const basePropertySchema = z.object({
  property_name: z.string().min(1, "Property name is required"),
  property_name_idn: z.string().min(1, "Indonesian name is required"),
  description: z
    .string()
    .min(1, "Description is required")
    .refine(
      (val) => stripHtml(val).trim().length > 0,
      "Description cannot be empty",
    ),
  description_idn: z.string().min(1, "Indonesian description is required"),
  subtitle: z.string().optional(),
  subtitle_idn: z.string().optional(),
  reff_id: z.string().min(1, "Reference ID is required"),
  latitude: z.string().min(1, "Latitude is required"),
  longitude: z.string().min(1, "Longitude is required"),
  map_url: z.string().min(1, "Map URL is required"),
  area_id: z.string().min(1, "Area is required").optional(),
  slug: z.string().min(1, "Slug is required"),
  social_media_link: z.string().url("Invalid URL").optional().or(z.literal("")),
  shm_condition: z
    .string()
    .trim()
    .min(1, "SHM condition is required (status, atas nama, agunan)"),
  slf_pbg_condition: z
    .string()
    .trim()
    .min(1, "SLF/PBG condition is required"),
  zoning: z.string().trim().min(1, "Zoning is required"),
  featured: z.boolean(),
  draft: z.boolean(),
  hidden: z.boolean(),
  price_hidden: z.boolean(),
  tags: z.array(z.string()),
  images: z.array(
    z.object({
      key: z.string(),
      filename: z.string(),
      contentType: z.string().optional(),
      size: z.number().optional(),
      blurhash: z.string().optional(),
    }),
  ),
});

// step 2: property type selection
const propertyTypeSchema = z.object({
  selectedTypes: z
    .array(
      z.enum([
        "rentals",
        "freehold_properties",
        "freehold_villas",
        "leasehold_villas",
        "freehold_lands",
        "leasehold_lands",
        "freehold_commercial",
        "leasehold_commercial",
      ]),
    )
    .min(1, "Please select at least one property type"),
});

const rentalsSchema = z.object({
  bedrooms: z.coerce
    .number({ message: "Rentals: Number of bedrooms is required" })
    .int()
    .gt(0, "Rentals: Number of bedrooms must be greater than 0"),
  bathrooms: z.coerce
    .number({ message: "Rentals: Number of bathrooms is required" })
    .int()
    .gt(0, "Rentals: Number of bathrooms must be greater than 0"),
  land_size: z.coerce
    .number({ message: "Rentals: Land size is required" })
    .gt(0, "Rentals: Land size must be a valid positive number"),
  build_size: z.coerce
    .number({ message: "Rentals: Build size is required" })
    .gt(0, "Rentals: Build size must be a valid positive number"),
  price_per_year: z.coerce
    .number({ message: "Rentals: Price per year is required" })
    .gt(0, "Rentals: Price per year must be a valid positive number"),
  minimum_duration: z.coerce
    .number()
    .gt(0, "Rentals: Minimum duration must be greater than 0"),
  safety_deposit_amount: z.coerce
    .number({ message: "Rentals: Safety deposit amount is required" })
    .gte(
      0,
      "Rentals: Safety deposit amount must be a valid non-negative number",
    ),
  pet_allowed: z.string().min(1, "Rentals: Pet policy is required"),
  orientation: z
    .array(z.string())
    .min(1, "Rentals: Orientation is required"),
});

const freeholdPropertySchema = z.object({
  bedrooms: z.coerce
    .number({ message: "Freehold Property: Number of bedrooms is required" })
    .int()
    .gt(0, "Freehold Property: Number of bedrooms must be greater than 0"),
  bathrooms: z.coerce
    .number({ message: "Freehold Property: Number of bathrooms is required" })
    .int()
    .gt(0, "Freehold Property: Number of bathrooms must be greater than 0"),
  build_size: z.coerce
    .number({ message: "Freehold Property: Build size is required" })
    .gt(0, "Freehold Property: Build size must be a valid positive number"),
  land_size: z.coerce
    .number({ message: "Freehold Property: Land size is required" })
    .gt(0, "Freehold Property: Land size must be a valid positive number"),
  year_built: z.coerce
    .number()
    .gt(1800, "Freehold Property: Year built must be greater than 1800")
    .lte(currentYear + 5, "Freehold Property: Year built must be reasonable"),
  total_price: z.coerce
    .number({ message: "Freehold Property: Total price is required" })
    .gt(0, "Freehold Property: Total price must be a valid positive number"),
  dp_minimal: z.coerce
    .number({ message: "Freehold Property: Minimal down payment is required" })
    .gte(
      0,
      "Freehold Property: Minimal down payment must be a valid non-negative number",
    ),
  pks_bank: z.string().min(1, "Freehold Property: PKS bank is required"),
  orientation: z
    .array(z.string())
    .min(1, "Freehold Property: Orientation is required"),
});

const freeholdVillaSchema = z.object({
  bedrooms: z.coerce
    .number({ message: "Freehold Villa: Number of bedrooms is required" })
    .int()
    .gt(0, "Freehold Villa: Number of bedrooms must be greater than 0"),
  bathrooms: z.coerce
    .number({ message: "Freehold Villa: Number of bathrooms is required" })
    .int()
    .gt(0, "Freehold Villa: Number of bathrooms must be greater than 0"),
  build_size: z.coerce
    .number({ message: "Freehold Villa: Build size is required" })
    .gt(0, "Freehold Villa: Build size must be a valid positive number"),
  land_size: z.coerce
    .number({ message: "Freehold Villa: Land size is required" })
    .gt(0, "Freehold Villa: Land size must be a valid positive number"),
  year_built: z.coerce
    .number()
    .gt(1800, "Freehold Villa: Year built must be greater than 1800")
    .lte(currentYear + 5, "Freehold Villa: Year built must be reasonable"),
  total_price: z.coerce
    .number({ message: "Freehold Villa: Total price is required" })
    .gt(0, "Freehold Villa: Total price must be a valid positive number"),
  dp_minimal: z.coerce
    .number({ message: "Freehold Villa: Minimal down payment is required" })
    .gte(
      0,
      "Freehold Villa: Minimal down payment must be a valid non-negative number",
    ),
  pks_bank: z.string().min(1, "Freehold Villa: PKS bank is required"),
  orientation: z
    .array(z.string())
    .min(1, "Freehold Villa: Orientation is required"),
});

const leaseholdVillaSchema = z.object({
  bedrooms: z.coerce
    .number({ message: "Leasehold Villa: Number of bedrooms is required" })
    .int()
    .gt(0, "Leasehold Villa: Number of bedrooms must be greater than 0"),
  bathrooms: z.coerce
    .number({ message: "Leasehold Villa: Number of bathrooms is required" })
    .int()
    .gt(0, "Leasehold Villa: Number of bathrooms must be greater than 0"),
  build_size: z.coerce
    .number({ message: "Leasehold Villa: Build size is required" })
    .gt(0, "Leasehold Villa: Build size must be a valid positive number"),
  land_size: z.coerce
    .number({ message: "Leasehold Villa: Land size is required" })
    .gt(0, "Leasehold Villa: Land size must be a valid positive number"),
  year_built: z.coerce
    .number()
    .gt(1800, "Leasehold Villa: Year built must be greater than 1800")
    .lte(currentYear + 5, "Leasehold Villa: Year built must be reasonable"),
  price_for_duration: z.coerce
    .number({ message: "Leasehold Villa: Price for duration is required" })
    .gt(
      0,
      "Leasehold Villa: Price for duration must be a valid positive number",
    ),
  leasehold_duration: z.coerce
    .number()
    .gt(0, "Leasehold Villa: Leasehold duration must be greater than 0"),
  orientation: z
    .array(z.string())
    .min(1, "Leasehold Villa: Orientation is required"),
});

const freeholdLandSchema = z.object({
  land_size: z.coerce
    .number({ message: "Freehold Land: Land size is required" })
    .gt(0, "Freehold Land: Land size must be a valid positive number"),
  price_per_are: z.coerce
    .number({ message: "Freehold Land: Price per are is required" })
    .gt(0, "Freehold Land: Price per are must be a valid positive number"),
  total_price: z.coerce
    .number({ message: "Freehold Land: Total price is required" })
    .gt(0, "Freehold Land: Total price must be a valid positive number"),
  road_access_width: z.coerce
    .number({ message: "Freehold Land: Road access width is required" })
    .gt(0, "Freehold Land: Road access width must be a valid positive number"),
  land_zoning: z.string().min(1, "Freehold Land: Land zoning is required"),
  view: z.string().min(1, "Freehold Land: View description is required"),
  orientation: z
    .array(z.string())
    .min(1, "Freehold Land: Orientation is required"),
});

const leaseholdLandSchema = z.object({
  total_price: z.coerce
    .number({ message: "Leasehold Land: Total price is required" })
    .gt(0, "Leasehold Land: Total price must be a valid positive number"),
  lease_duration: z.coerce
    .number()
    .gt(0, "Leasehold Land: Lease duration must be greater than 0"),
  land_size: z.coerce
    .number({ message: "Leasehold Land: Land size is required" })
    .gt(0, "Leasehold Land: Land size must be a valid positive number"),
  price_per_are: z.coerce
    .number({ message: "Leasehold Land: Price per are is required" })
    .gt(0, "Leasehold Land: Price per are must be a valid positive number"),
  land_zoning: z.string().min(1, "Leasehold Land: Land zoning is required"),
  view: z.string().min(1, "Leasehold Land: View description is required"),
  orientation: z
    .array(z.string())
    .min(1, "Leasehold Land: Orientation is required"),
});

const freeholdCommercialSchema = z.object({
  bedrooms: z
    .union([
      z.coerce
        .number({
          message:
            "Freehold Commercial: Number of bedrooms must be a valid number",
        })
        .int()
        .gt(
          0,
          "Freehold Commercial: Number of bedrooms must be greater than 0",
        ),
      z.literal(""),
      z.undefined(),
    ])
    .optional(),
  bathrooms: z.coerce
    .number({ message: "Freehold Commercial: Number of bathrooms is required" })
    .int()
    .gt(0, "Freehold Commercial: Number of bathrooms must be greater than 0"),
  land_size: z.coerce
    .number({ message: "Freehold Commercial: Land size is required" })
    .gt(0, "Freehold Commercial: Land size must be a valid positive number"),
  build_size: z.coerce
    .number({ message: "Freehold Commercial: Build size is required" })
    .gt(0, "Freehold Commercial: Build size must be a valid positive number"),
  title: z.string().min(1, "Freehold Commercial: Title is required"),
  building_permit: z
    .string()
    .min(1, "Freehold Commercial: Building permit is required"),
  frontage: z.coerce
    .number({ message: "Freehold Commercial: Frontage is required" })
    .gt(0, "Freehold Commercial: Frontage must be a valid positive number"),
  orientation: z
    .array(z.string())
    .min(1, "Freehold Commercial: Orientation is required"),
  accessibility: z
    .string()
    .min(1, "Freehold Commercial: Accessibility is required"),
  year_built: z.coerce
    .number()
    .gt(1800, "Freehold Commercial: Year built must be greater than 1800")
    .lte(currentYear + 5, "Freehold Commercial: Year built must be reasonable"),
  bank_partnership: z
    .string()
    .min(1, "Freehold Commercial: Bank partnership is required"),
  down_payment: z.coerce
    .number({ message: "Freehold Commercial: Down payment is required" })
    .gte(
      0,
      "Freehold Commercial: Down payment must be a valid non-negative number",
    ),
  total_price: z.coerce
    .number({ message: "Freehold Commercial: Total price is required" })
    .gt(0, "Freehold Commercial: Total price must be a valid positive number"),
});

const leaseholdCommercialSchema = z.object({
  bedrooms: z
    .union([
      z.coerce
        .number({
          message:
            "Leasehold Commercial: Number of bedrooms must be a valid number",
        })
        .int()
        .gt(
          0,
          "Leasehold Commercial: Number of bedrooms must be greater than 0",
        ),
      z.literal(""),
      z.undefined(),
    ])
    .optional(),
  bathrooms: z.coerce
    .number({
      message: "Leasehold Commercial: Number of bathrooms is required",
    })
    .int()
    .gt(0, "Leasehold Commercial: Number of bathrooms must be greater than 0"),
  land_size: z.coerce
    .number({ message: "Leasehold Commercial: Land size is required" })
    .gt(0, "Leasehold Commercial: Land size must be a valid positive number"),
  build_size: z.coerce
    .number({ message: "Leasehold Commercial: Build size is required" })
    .gt(0, "Leasehold Commercial: Build size must be a valid positive number"),
  title: z.string().min(1, "Leasehold Commercial: Title is required"),
  building_permit: z
    .string()
    .min(1, "Leasehold Commercial: Building permit is required"),
  frontage: z.coerce
    .number({ message: "Leasehold Commercial: Frontage is required" })
    .gt(0, "Leasehold Commercial: Frontage must be a valid positive number"),
  orientation: z
    .array(z.string())
    .min(1, "Leasehold Commercial: Orientation is required"),
  accessibility: z
    .string()
    .min(1, "Leasehold Commercial: Accessibility is required"),
  year_built: z.coerce
    .number()
    .gt(1800, "Leasehold Commercial: Year built must be greater than 1800")
    .lte(
      currentYear + 5,
      "Leasehold Commercial: Year built must be reasonable",
    ),
  price_for_duration: z.coerce
    .number({ message: "Leasehold Commercial: Price for duration is required" })
    .gt(
      0,
      "Leasehold Commercial: Price for duration must be a valid positive number",
    ),
  extension_duration: z.coerce
    .number({ message: "Leasehold Commercial: Extension duration is required" })
    .gte(
      0,
      "Leasehold Commercial: Extension duration must be a valid non-negative number",
    )
    .optional(),
  leasehold_duration: z.coerce
    .number({ message: "Leasehold Commercial: Leasehold duration is required" })
    .gt(
      0,
      "Leasehold Commercial: Leasehold duration must be a valid positive number",
    ),
});

const TYPE_SCHEMAS = {
  rentals: rentalsSchema,
  freehold_properties: freeholdPropertySchema,
  freehold_villas: freeholdVillaSchema,
  leasehold_villas: leaseholdVillaSchema,
  freehold_lands: freeholdLandSchema,
  leasehold_lands: leaseholdLandSchema,
  freehold_commercial: freeholdCommercialSchema,
  leasehold_commercial: leaseholdCommercialSchema,
} as const;

// type definitions for each form
type RentalsFormData = z.infer<typeof rentalsSchema>;
type FreeholdPropertyFormData = z.infer<typeof freeholdPropertySchema>;
type FreeholdVillaFormData = z.infer<typeof freeholdVillaSchema>;
type LeaseholdVillaFormData = z.infer<typeof leaseholdVillaSchema>;
type FreeholdLandFormData = z.infer<typeof freeholdLandSchema>;
type LeaseholdLandFormData = z.infer<typeof leaseholdLandSchema>;
type FreeholdCommercialFormData = z.infer<typeof freeholdCommercialSchema>;
type LeaseholdCommercialFormData = z.infer<typeof leaseholdCommercialSchema>;

type TypeFormDataMap = {
  rentals: RentalsFormData;
  freehold_properties: FreeholdPropertyFormData;
  freehold_villas: FreeholdVillaFormData;
  leasehold_villas: LeaseholdVillaFormData;
  freehold_lands: FreeholdLandFormData;
  leasehold_lands: LeaseholdLandFormData;
  freehold_commercial: FreeholdCommercialFormData;
  leasehold_commercial: LeaseholdCommercialFormData;
};

// step 4 facilities (optional)
const facilitiesSchema = z.object({
  living_room_type: z.string().optional(),
  dining_rooms: z.coerce.number().optional(),
  kitchens: z.coerce.number().optional(),
  extra_rooms: z.coerce.number().optional(),
  electricity_power: z.coerce.number().optional(),
  water_source: z.string().optional(),
  parking: z.coerce.number().optional(),
  internet: z.boolean().optional(),
  swimming_pool: z.boolean().optional(),
  storage: z.boolean().optional(),
  garden: z.boolean().optional(),
  security: z.boolean().optional(),
  view: z.string().optional(),
  furniture: z.string().optional(),
});

// step 7 contact (conditional)
const contactSchema = z.object({
  phone: z.string().regex(/^\d+$/, "Phone must be numbers only"),
  country: z.string().min(1, "Country is required"),
  name: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  address: z.string().optional(),
});

const PROPERTY_TYPES = [
  {
    value: "rentals",
    label: "Rentals",
    description: "Rental properties with facilities",
    hasFacilities: true,
  },
  {
    value: "freehold_properties",
    label: "Freehold Property",
    description: "Freehold properties with facilities",
    hasFacilities: true,
  },
  {
    value: "freehold_villas",
    label: "Freehold Villa",
    description: "Freehold villas with facilities",
    hasFacilities: true,
  },
  {
    value: "leasehold_villas",
    label: "Leasehold Villa",
    description: "Leasehold villas with facilities",
    hasFacilities: true,
  },
  {
    value: "freehold_lands",
    label: "Freehold Land",
    description: "Freehold land without facilities",
    hasFacilities: false,
  },
  {
    value: "leasehold_lands",
    label: "Leasehold Land",
    description: "Leasehold land without facilities",
    hasFacilities: false,
  },
  {
    value: "freehold_commercial",
    label: "Freehold Commercial",
    description: "Freehold commercial properties with facilities",
    hasFacilities: true,
  },
  {
    value: "leasehold_commercial",
    label: "Leasehold Commercial",
    description: "Leasehold commercial properties with facilities",
    hasFacilities: true,
  },
];

// helper function to strip HTML tags for validation
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

export function MultiStepPropertyForm({
  open,
  onClose,
  mode,
  propertyId,
  draftId: initialDraftId,
  draftData,
}: MultiStepPropertyFormProps) {
  // CSS for scroll-driven scrollfade effect
  // sync effects must come after state declarations
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @property --left-fade {
        syntax: "<length>";
        inherits: false;
        initial-value: 0;
      }

      @property --right-fade {
        syntax: "<length>";
        inherits: false;
        initial-value: 0;
      }

      @keyframes scrollfade {
        0% {
          --left-fade: 0;
        }
        10%, 100% {
          --left-fade: 3rem;
        }
        0%, 90% {
          --right-fade: 3rem;
        }
        100% {
          --right-fade: 0;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<
    PropertyType[]
  >([]);
  const [selectedPropertyType, setSelectedPropertyType] =
    useState<PropertyType | null>(null); // keep for backward compatibility

  // per-type form data for multi-type support
  const [typeFormData, setTypeFormData] = useState<Record<PropertyType, any>>({
    rentals: {},
    freehold_properties: {},
    freehold_villas: {},
    leasehold_villas: {},
    freehold_lands: {},
    leasehold_lands: {},
    freehold_commercial: {},
    leasehold_commercial: {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingEnglishSubtitle, setIsGeneratingEnglishSubtitle] =
    useState(false);
  const [isGeneratingIndonesianSubtitle, setIsGeneratingIndonesianSubtitle] =
    useState(false);
  const hasInitialized = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formKey, setFormKey] = useState(0);
  const stepsContainerRef = useRef<HTMLDivElement>(null);
  const slugManuallyChanged = useRef(false);

  // ---------------------------------------------------------------------------
  // auto-save state and mutations
  // ---------------------------------------------------------------------------
  const [draftId, setDraftId] = useState<Id<"property_drafts"> | null>(
    initialDraftId || null,
  );
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isManuallySaving, setIsManuallySaving] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const videoPreviewUrlRef = useRef<string | null>(null);
  const originalThumbnailVideoRef = useRef<
    typeof basePropertyData.thumbnail_video | null
  >(null);

  const saveDraft = useMutation(api.drafts.saveDraft);
  const deleteDraft = useMutation(api.drafts.deleteDraft);

  // ---------------------------------------------------------------------------
  // react-hook-form: main form and dynamic nested type forms
  // ---------------------------------------------------------------------------
  const mainSchema = z.object({
    base: basePropertySchema,
    propertyTypes: propertyTypeSchema,
    facilities: facilitiesSchema,
    contact: contactSchema.optional(),
  });

  type MainFormShape = z.infer<typeof mainSchema>;

  const mainForm = useForm<MainFormShape>({
    resolver: zodResolver(mainSchema) as unknown as Resolver<MainFormShape>,
    mode: "onChange",
    defaultValues: {
      base: {
        property_name: "",
        property_name_idn: "",
        description: "",
        description_idn: "",
        subtitle: "",
        subtitle_idn: "",
        reff_id: "",
        latitude: "",
        longitude: "",
        map_url: "",
        area_id: undefined,
        slug: "",
        social_media_link: "",
        shm_condition: "",
        slf_pbg_condition: "",
        zoning: "",
        featured: false,
        draft: true,
        hidden: false,
        price_hidden: false,
        tags: [],
        images: [],
      },
      propertyTypes: {
        selectedTypes: [],
      },
      facilities: {},
      contact: undefined,
    },
  });

  // helper to get default values per type (must be declared before form hooks below)
  const getDefaultsForType = (type: PropertyType) => {
    const src = typeFormData[type] || {};
    switch (type) {
      case "rentals":
        return {
          bedrooms: src.bedrooms || "",
          bathrooms: src.bathrooms || "",
          land_size: src.land_size || "",
          build_size: src.build_size || "",
          price_per_year: src.price_per_year || "",
          minimum_duration: src.minimum_duration || 0,
          safety_deposit_amount: src.safety_deposit_amount || "",
          pet_allowed: src.pet_allowed || "",
          orientation: toOrientationArray(src.orientation),
        };
      case "freehold_properties":
      case "freehold_villas":
        return {
          bedrooms: src.bedrooms || "",
          bathrooms: src.bathrooms || "",
          build_size: src.build_size || "",
          land_size: src.land_size || "",
          year_built: src.year_built || new Date().getFullYear(),
          total_price: src.total_price || "",
          dp_minimal: src.dp_minimal || "",
          pks_bank: src.pks_bank || "",
          orientation: toOrientationArray(src.orientation),
        };
      case "leasehold_villas":
        return {
          bedrooms: src.bedrooms || "",
          bathrooms: src.bathrooms || "",
          build_size: src.build_size || "",
          land_size: src.land_size || "",
          year_built: src.year_built || new Date().getFullYear(),
          price_for_duration: src.price_for_duration || "",
          extension_duration: src.extension_duration || "",
          leasehold_duration: src.leasehold_duration || "",
          orientation: toOrientationArray(src.orientation),
        };
      case "freehold_lands":
        return {
          land_size: src.land_size || "",
          price_per_are: src.price_per_are || "",
          total_price: src.total_price || "",
          road_access_width: src.road_access_width || "",
          land_zoning: src.land_zoning || "",
          view: src.view || "",
          orientation: toOrientationArray(src.orientation),
        };
      case "leasehold_lands":
        return {
          total_price: src.total_price || "",
          lease_duration: src.lease_duration || "",
          land_size: src.land_size || "",
          price_per_are: src.price_per_are || "",
          land_zoning: src.land_zoning || "",
          view: src.view || "",
          orientation: toOrientationArray(src.orientation),
        };
      case "freehold_commercial":
        return {
          bedrooms: src.bedrooms || "",
          bathrooms: src.bathrooms || "",
          land_size: src.land_size || "",
          build_size: src.build_size || "",
          title: src.title || "",
          building_permit: src.building_permit || "",
          frontage: src.frontage || "",
          orientation: toOrientationArray(src.orientation),
          accessibility: src.accessibility || "",
          year_built: src.year_built || new Date().getFullYear(),
          bank_partnership: src.bank_partnership || "",
          down_payment: src.down_payment || "",
          total_price: src.total_price || "",
        };
      case "leasehold_commercial":
        return {
          bedrooms: src.bedrooms || "",
          bathrooms: src.bathrooms || "",
          land_size: src.land_size || "",
          build_size: src.build_size || "",
          title: src.title || "",
          building_permit: src.building_permit || "",
          frontage: src.frontage || "",
          orientation: toOrientationArray(src.orientation),
          accessibility: src.accessibility || "",
          year_built: src.year_built || new Date().getFullYear(),
          price_for_duration: src.price_for_duration || "",
          extension_duration: src.extension_duration || "",
          leasehold_duration: src.leasehold_duration || "",
        };
      default:
        return {};
    }
  };

  // real react-hook-form instances for each property type (hooks must not be conditional)
  const rentalsForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["rentals"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("rentals"),
  });
  const freeholdPropertiesForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["freehold_properties"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("freehold_properties"),
  });
  const freeholdVillasForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["freehold_villas"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("freehold_villas"),
  });
  const leaseholdVillasForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["leasehold_villas"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("leasehold_villas"),
  });
  const freeholdLandsForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["freehold_lands"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("freehold_lands"),
  });
  const leaseholdLandsForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["leasehold_lands"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("leasehold_lands"),
  });
  const freeholdCommercialForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["freehold_commercial"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("freehold_commercial"),
  });
  const leaseholdCommercialForm = useForm({
    resolver: zodResolver(TYPE_SCHEMAS["leasehold_commercial"]) as any,
    mode: "onChange",
    defaultValues: getDefaultsForType("leasehold_commercial"),
  });

  const typeForms = {
    rentals: rentalsForm,
    freehold_properties: freeholdPropertiesForm,
    freehold_villas: freeholdVillasForm,
    leasehold_villas: leaseholdVillasForm,
    freehold_lands: freeholdLandsForm,
    leasehold_lands: leaseholdLandsForm,
    freehold_commercial: freeholdCommercialForm,
    leasehold_commercial: leaseholdCommercialForm,
  };

  // keep RHF forms in sync with any preloaded type data
  // removed automatic form reset on property type changes to support multiple types

  // force form re-render when formKey changes
  useEffect(() => {
    // this effect will trigger re-rendering of form components
    console.log("Form key changed, forcing re-render:", formKey);
  }, [formKey]);

  // sync commercial form values to typeFormData state for review section
  useEffect(() => {
    if (selectedPropertyTypes.includes("freehold_commercial")) {
      const subscription = freeholdCommercialForm.watch((value) => {
        setTypeFormData((prev) => ({
          ...prev,
          freehold_commercial: value,
        }));
      });
      return () => subscription.unsubscribe();
    }
  }, [selectedPropertyTypes, freeholdCommercialForm]);

  useEffect(() => {
    if (selectedPropertyTypes.includes("leasehold_commercial")) {
      const subscription = leaseholdCommercialForm.watch((value) => {
        setTypeFormData((prev) => ({
          ...prev,
          leasehold_commercial: value,
        }));
      });
      return () => subscription.unsubscribe();
    }
  }, [selectedPropertyTypes, leaseholdCommercialForm]);

  // removed automatic form re-render on property type changes to support multiple types

  // ---------------------------------------------------------------------------
  // sync local state -> main form values for validation
  // ---------------------------------------------------------------------------
  // sync local state -> main form values for validation
  // moved below state declarations to avoid TDZ issues
  const syncBaseToForm = () => {
    mainForm.setValue("base.property_name", basePropertyData.property_name, {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue(
      "base.property_name_idn",
      basePropertyData.property_name_idn || "",
      { shouldValidate: true, shouldDirty: true },
    );
    mainForm.setValue("base.description", basePropertyData.description, {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue(
      "base.description_idn",
      basePropertyData.description_idn || "",
      { shouldValidate: true, shouldDirty: true },
    );
    mainForm.setValue("base.subtitle", basePropertyData.subtitle || "", {
      shouldValidate: false,
      shouldDirty: true,
    });
    mainForm.setValue(
      "base.subtitle_idn",
      basePropertyData.subtitle_idn || "",
      {
        shouldValidate: false,
        shouldDirty: true,
      },
    );
    mainForm.setValue("base.reff_id", basePropertyData.reff_id, {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue("base.latitude", basePropertyData.latitude, {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue("base.longitude", basePropertyData.longitude, {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue("base.map_url", basePropertyData.map_url, {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue(
      "base.area_id",
      (basePropertyData.area_id as unknown as string) || undefined,
      { shouldValidate: true, shouldDirty: true },
    );
    mainForm.setValue("base.slug", basePropertyData.slug, {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue(
      "base.social_media_link",
      basePropertyData.social_media_link || "",
      {
        shouldValidate: false,
        shouldDirty: true,
      },
    );
    mainForm.setValue(
      "base.shm_condition",
      basePropertyData.shm_condition || "",
      { shouldValidate: true, shouldDirty: true },
    );
    mainForm.setValue(
      "base.slf_pbg_condition",
      basePropertyData.slf_pbg_condition || "",
      { shouldValidate: true, shouldDirty: true },
    );
    mainForm.setValue("base.zoning", basePropertyData.zoning || "", {
      shouldValidate: true,
      shouldDirty: true,
    });
    mainForm.setValue("base.featured", basePropertyData.featured, {
      shouldValidate: false,
      shouldDirty: true,
    });
    mainForm.setValue("base.draft", basePropertyData.draft, {
      shouldValidate: false,
      shouldDirty: true,
    });
    mainForm.setValue("base.hidden", basePropertyData.hidden, {
      shouldValidate: false,
      shouldDirty: true,
    });
    mainForm.setValue("base.price_hidden", basePropertyData.price_hidden, {
      shouldValidate: false,
      shouldDirty: true,
    });
    mainForm.setValue(
      "base.tags",
      (basePropertyData.tags as unknown as string[]) || [],
      { shouldValidate: false, shouldDirty: true },
    );
    mainForm.setValue("base.images", basePropertyData.images as any, {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  const syncTypesToForm = () => {
    mainForm.setValue(
      "propertyTypes.selectedTypes",
      selectedPropertyTypes as any,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const syncFacilitiesToForm = () => {
    mainForm.setValue("facilities", facilitiesData as any, {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  const syncContactToForm = () => {
    if (addContact) {
      mainForm.setValue(
        "contact",
        {
          phone: contactData.phone,
          country: contactData.country,
          name: contactData.name || "",
          email: contactData.email || "",
          address: contactData.address || "",
        } as any,
        { shouldValidate: false, shouldDirty: true },
      );
    } else {
      mainForm.setValue("contact", undefined as any, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }
  };

  // form data states
  const [basePropertyData, setBasePropertyData] = useState<BasePropertyData>({
    property_name: "",
    property_name_idn: "",
    description: "",
    description_idn: "",
    subtitle: "",
    subtitle_idn: "",
    reff_id: "",
    latitude: "",
    longitude: "",
    map_url: "",
    slug: "",
    social_media_link: "",
    shm_condition: "",
    slf_pbg_condition: "",
    zoning: "",
    featured: false,
    draft: true,
    hidden: false,
    price_hidden: false,
    tags: [],
    images: [],
  });

  // run syncs after state is available
  useEffect(() => {
    syncBaseToForm();
  }, [basePropertyData]);
  useEffect(() => {
    syncTypesToForm();
  }, [selectedPropertyTypes]);
  // defer facilities & contact sync until their states are declared

  const [facilitiesData, setFacilitiesData] = useState<FacilitiesData>({
    living_room_type: "",
    dining_rooms: 0,
    kitchens: 0,
    extra_rooms: 0,
    electricity_power: 0,
    water_source: "",
    parking: 0,
    internet: false,
    swimming_pool: false,
    storage: false,
    garden: false,
    security: false,
    view: "",
    furniture: "",
  });

  const [rentalsData, setRentalsData] = useState<RentalsData>({
    bedrooms: "",
    bathrooms: "",
    land_size: "",
    build_size: "",
    price_per_year: "",
    minimum_duration: 0,
    safety_deposit_amount: "",
    pet_allowed: "",
  });

  const [freeholdPropertyData, setFreeholdPropertyData] =
    useState<FreeholdPropertyData>({
      bedrooms: "",
      bathrooms: "",
      build_size: "",
      land_size: "",
      year_built: new Date().getFullYear(),
      total_price: "",
      dp_minimal: "",
      pks_bank: "",
    });

  const [freeholdVillaData, setFreeholdVillaData] = useState<FreeholdVillaData>(
    {
      bedrooms: "",
      bathrooms: "",
      build_size: "",
      land_size: "",
      year_built: new Date().getFullYear(),
      total_price: "",
      dp_minimal: "",
      pks_bank: "",
    },
  );

  const [leaseholdVillaData, setLeaseholdVillaData] =
    useState<LeaseholdVillaData>({
      bedrooms: "",
      bathrooms: "",
      build_size: "",
      land_size: "",
      year_built: new Date().getFullYear(),
      price_for_duration: "",
      extension_duration: 0,
      leasehold_duration: 0,
    });

  const [freeholdLandData, setFreeholdLandData] = useState<FreeholdLandData>({
    land_size: "",
    price_per_are: "",
    total_price: "",
    road_access_width: "",
    land_zoning: "",
    view: "",
  });

  const [leaseholdLandData, setLeaseholdLandData] = useState<LeaseholdLandData>(
    {
      total_price: "",
      lease_duration: 0,
      land_size: "",
      price_per_are: "",
      land_zoning: "",
      view: "",
    },
  );

  // commercialData state removed - using form instances instead

  // now safe to sync facilities/contact with form
  useEffect(() => {
    syncFacilitiesToForm();
  }, [facilitiesData]);
  // defer contact sync until addContact/contactData declared below

  // contact (optional)
  const [addContact, setAddContact] = useState(false);
  const [contactData, setContactData] = useState({
    name: "",
    phone: "",
    email: "",
    country: "",
    address: "",
    qualification: "" as "qualified" | "unqualified" | "not approve" | "",
    group_id: "" as string,
    type_id: "" as string,
    note: "",
  });

  useEffect(() => {
    syncContactToForm();
  }, [addContact, contactData]);

  // realtime field validation for contact step
  useEffect(() => {
    if (!addContact) {
      setFieldErrors((prev) => ({
        ...prev,
        contact_phone: "",
        contact_country: "",
      }));
      return;
    }

    const trimmedPhone = contactData.phone.trim();
    const trimmedCountry = contactData.country.trim();

    const newErrors: Record<string, string> = {};

    if (trimmedPhone.length === 0) {
      newErrors["contact_phone"] = "phone is required";
    } else if (contactData.phone !== trimmedPhone) {
      newErrors["contact_phone"] = "no leading or trailing spaces allowed";
    } else if (!/^\d+$/.test(contactData.phone)) {
      newErrors["contact_phone"] = "only numbers allowed";
    }

    if (trimmedCountry.length === 0) {
      newErrors["contact_country"] = "country is required";
    } else if (contactData.country !== trimmedCountry) {
      newErrors["contact_country"] = "no leading or trailing spaces allowed";
    }

    setFieldErrors((prev) => ({
      ...prev,
      contact_phone: newErrors["contact_phone"] || "",
      contact_country: newErrors["contact_country"] || "",
    }));
  }, [addContact, contactData.phone, contactData.country]);

  // convex queries and mutations
  const areas = useQuery(api.contacts.getAllAreas, {});
  const tags = useQuery(api.tags.getAllTags, {});
  const currentUser = useQuery(api.users.current);
  const createProperty = useMutation(api.properties.createPropertyWithType);
  const updateProperty = useMutation(api.properties.updatePropertyWithType);
  const createContact = useMutation(api.contacts.createContact);
  const generateSubtitle = useAction(api.properties.generatePropertySubtitle);

  // video upload hook
  const {
    upload: uploadVideo,
    isUploading: isVideoUploading,
    conversionProgress,
    uploadProgress: videoUploadProgress,
    error: videoUploadError,
    reset: resetVideoUpload,
  } = useVideoUpload({
    reffId: basePropertyData.reff_id || undefined,
    onSuccess: (result) => {
      setBasePropertyData((prev) => ({
        ...prev,
        thumbnail_video: {
          gif_key: result.gif_key,
          original_filename: result.original_filename,
        },
      }));
      // clear preview and selected file after upload completes
      // GIF will be displayed from thumbnail_video.gif_key instead
      // revoke object url to prevent memory leak
      if (videoPreviewUrlRef.current) {
        URL.revokeObjectURL(videoPreviewUrlRef.current);
        videoPreviewUrlRef.current = null;
      }
      setSelectedVideoFile(null);
      setVideoPreviewUrl(null);
    },
    onError: (error) => {
      console.error("video upload error:", error);
    },
  });

  // cleanup object url on unmount
  useEffect(() => {
    return () => {
      if (videoPreviewUrlRef.current) {
        URL.revokeObjectURL(videoPreviewUrlRef.current);
      }
    };
  }, []);

  const contactGroups = useQuery(api.contacts.getAllContactGroups, {});
  const contactTypes = useQuery(api.contacts.getAllContactTypes, {});

  // reference ID validation (only checks non-deleted properties)
  const [reffIdDebounce, setReffIdDebounce] = useState("");
  const reffIdExists = useQuery(
    api.properties.checkReferenceIdExists,
    reffIdDebounce ? { reffId: reffIdDebounce } : "skip",
  );

  // slug validation (only checks non-deleted properties)
  const [slugDebounce, setSlugDebounce] = useState("");
  const slugExists = useQuery(
    api.properties.checkSlugExists,
    slugDebounce ? { slug: slugDebounce } : "skip",
  );

  // property name validation (only checks non-deleted properties)
  const [propertyNameDebounce, setPropertyNameDebounce] = useState("");
  const propertyNameExists = useQuery(
    api.properties.checkPropertyNameExists,
    propertyNameDebounce ? { propertyName: propertyNameDebounce } : "skip",
  );

  // fetch existing property data for edit mode
  const existingProperty = useQuery(
    api.properties.getPropertyWithRelations,
    mode === "edit" && propertyId
      ? { id: propertyId, showDrafts: true, showHidden: true }
      : "skip",
  );

  // ---------------------------------------------------------------------------
  // auto-save effect
  // ---------------------------------------------------------------------------
  const currentFormState = {
    base: basePropertyData,
    types: {
      selected: selectedPropertyTypes,
      data: typeFormData,
    },
    facilities: facilitiesData,
    contact: contactData,
    step: currentStep,
  };

  const debouncedFormState = useDebounce(currentFormState, 1000);

  useEffect(() => {
    const autoSave = async () => {
      // Only auto-save in create mode and if we have at least a name
      if (
        mode !== "create" ||
        !debouncedFormState.base.property_name ||
        debouncedFormState.base.property_name.length < 3
      ) {
        return;
      }

      setIsAutoSaving(true);
      try {
        const id = await saveDraft({
          id: draftId || undefined,
          property_name: debouncedFormState.base.property_name,
          data: JSON.stringify(debouncedFormState),
          step: debouncedFormState.step,
        });
        setDraftId(id);
        setLastSaved(new Date());
      } catch (error) {
        console.error("Auto-save failed:", error);
      } finally {
        setIsAutoSaving(false);
      }
    };

    autoSave();
  }, [debouncedFormState, mode, saveDraft, draftId]);

  // reset hasInitialized when form opens with draft data
  useEffect(() => {
    if (open && draftData && mode === "create") {
      hasInitialized.current = false;
    }
  }, [open, draftData, mode]);

  // load draft data when provided
  useEffect(() => {
    if (open && draftData && !hasInitialized.current && mode === "create") {
      const draft = draftData;

      setBasePropertyData(draft.base);
      setSelectedPropertyTypes(draft.types.selected);
      setSelectedPropertyType(draft.types.selected[0] || null);
      setTypeFormData(draft.types.data);
      setFacilitiesData(draft.facilities);
      setCurrentStep(draft.step);

      let hasContactData = false;
      if (draft.contact) {
        hasContactData = !!(
          draft.contact.name ||
          draft.contact.phone ||
          draft.contact.email ||
          draft.contact.country ||
          draft.contact.address
        );

        if (hasContactData) {
          setAddContact(true);
          setContactData({
            name: draft.contact.name || "",
            phone: draft.contact.phone || "",
            email: draft.contact.email || "",
            country: draft.contact.country || "",
            address: draft.contact.address || "",
            qualification:
              (draft.contact.qualification as
                | "qualified"
                | "unqualified"
                | "not approve"
                | "") || "",
            group_id: draft.contact.group_id || "",
            type_id: draft.contact.type_id || "",
            note: draft.contact.note || "",
          });
        }
      }

      const mainFormData = {
        base: {
          property_name: draft.base.property_name || "",
          property_name_idn: draft.base.property_name_idn || "",
          description: draft.base.description || "",
          description_idn: draft.base.description_idn || "",
          subtitle: draft.base.subtitle || "",
          subtitle_idn: draft.base.subtitle_idn || "",
          reff_id: draft.base.reff_id || "",
          latitude: draft.base.latitude?.toString() || "",
          longitude: draft.base.longitude?.toString() || "",
          map_url: draft.base.map_url || "",
          area_id: draft.base.area_id?.toString() || "",
          slug: toUrlSafeSlug(draft.base.slug || ""),
          shm_condition: draft.base.shm_condition || "",
          slf_pbg_condition: draft.base.slf_pbg_condition || "",
          zoning: draft.base.zoning || "",
          featured: draft.base.featured || false,
          draft: draft.base.draft !== undefined ? draft.base.draft : true,
          hidden: draft.base.hidden || false,
          price_hidden: draft.base.price_hidden || false,
          tags: (draft.base.tags as unknown as string[]) || [],
          images: draft.base.images || [],
        },
        propertyTypes: {
          selectedTypes: draft.types.selected || [],
        },
        facilities: draft.facilities || {},
        contact: hasContactData && draft.contact ? draft.contact : undefined,
      };

      mainForm.reset(mainFormData);

      // drafts from dash have no type yet, so unselected types load too: their
      // price is then already filled in on whichever type staff picks
      (Object.keys(typeForms) as PropertyType[]).forEach((type) => {
        const formData = draft.types.data[type] || {};
        if (
          draft.types.selected.includes(type) ||
          Object.keys(formData).length > 0
        ) {
          typeForms[type].reset({ ...getDefaultsForType(type), ...formData });
        }
      });

      hasInitialized.current = true;
      setFormKey((prev) => prev + 1);
      return;
    }

    if (mode === "edit" && existingProperty && !hasInitialized.current) {
      console.log("Loading existing property data:", existingProperty);
      console.log(
        "Raw existingProperty structure:",
        JSON.stringify(existingProperty, null, 2),
      );
      const baseProperty = existingProperty.baseProperty;
      console.log("Base property data:", baseProperty);
      console.log(
        "thumbnail_video from baseProperty:",
        baseProperty.thumbnail_video,
      );
      console.log("thumbnail_video type:", typeof baseProperty.thumbnail_video);
      console.log("thumbnail_video truthy?", !!baseProperty.thumbnail_video);

      // set base property data
      const thumbnailVideoData = baseProperty.thumbnail_video
        ? {
            webm_key: baseProperty.thumbnail_video.webm_key,
            gif_key: baseProperty.thumbnail_video.gif_key,
            original_filename: baseProperty.thumbnail_video.original_filename,
          }
        : undefined;

      console.log("Setting thumbnail_video to state:", thumbnailVideoData);

      // store original thumbnail_video to detect if it was cleared
      originalThumbnailVideoRef.current = thumbnailVideoData;

      setBasePropertyData({
        property_name: baseProperty.property_name || "",
        property_name_idn: baseProperty.property_name_idn || "",
        description: baseProperty.description || "",
        description_idn: baseProperty.description_idn || "",
        subtitle: baseProperty.subtitle || "",
        subtitle_idn: baseProperty.subtitle_idn || "",
        reff_id: baseProperty.reff_id || "",
        latitude: baseProperty.latitude?.toString() || "",
        longitude: baseProperty.longitude?.toString() || "",
        map_url: baseProperty.map_url || "",
        area_id: baseProperty.area_id,
        slug: baseProperty.slug || "",
        social_media_link: baseProperty.social_media_link || "",
        shm_condition: baseProperty.shm_condition || "",
        slf_pbg_condition: baseProperty.slf_pbg_condition || "",
        zoning: baseProperty.zoning || "",
        thumbnail_video: thumbnailVideoData,
        featured: baseProperty.featured || false,
        draft: baseProperty.draft || false,
        hidden: baseProperty.hidden || false,
        price_hidden: baseProperty.price_hidden || false,
        tags: baseProperty.tags || [],
        images: baseProperty.images || [],
      });

      console.log("State set with thumbnail_video:", thumbnailVideoData);

      // social media thumbnail fetching removed - not used for video thumbnail

      // reset main form with base property data
      const mainFormData = {
        base: {
          property_name: baseProperty.property_name || "",
          property_name_idn: baseProperty.property_name_idn || "",
          description: baseProperty.description || "",
          description_idn: baseProperty.description_idn || "",
          subtitle: baseProperty.subtitle || "",
          subtitle_idn: baseProperty.subtitle_idn || "",
          reff_id: baseProperty.reff_id || "",
          latitude: baseProperty.latitude?.toString() || "",
          longitude: baseProperty.longitude?.toString() || "",
          map_url: baseProperty.map_url || "",
          area_id: baseProperty.area_id?.toString() || "",
          slug: baseProperty.slug || "",
          social_media_link: baseProperty.social_media_link || "",
          shm_condition: baseProperty.shm_condition || "",
          slf_pbg_condition: baseProperty.slf_pbg_condition || "",
          zoning: baseProperty.zoning || "",
          thumbnail_video: thumbnailVideoData, // include thumbnail_video
          featured: baseProperty.featured || false,
          draft: baseProperty.draft || false,
          hidden: baseProperty.hidden || false,
          price_hidden: baseProperty.price_hidden || false,
          tags: (baseProperty.tags as unknown as string[]) || [],
          images: baseProperty.images || [],
        },
        propertyTypes: { selectedTypes: [] }, // will be set below
        facilities: {},
        contact: undefined,
      };

      mainForm.reset(mainFormData);

      // also update the old state system for backward compatibility
      setBasePropertyData({
        ...mainFormData.base,
        area_id: mainFormData.base.area_id as Id<"area">,
        tags: mainFormData.base.tags as Id<"tag">[],
        thumbnail_video: thumbnailVideoData, // preserve thumbnail_video
      });

      // set property type based on existing data
      console.log(
        "Checking property type - existingProperty.propertyType:",
        existingProperty.propertyType,
      );
      // handle multiple property types
      if (
        (existingProperty as any).types &&
        (existingProperty as any).types.length > 0
      ) {
        console.log(
          "Multiple property types from data:",
          (existingProperty as any).types,
        );
        const typeMap: { [key: string]: PropertyType } = {
          // handle raw database names (lowercase with underscores)
          rentals: "rentals",
          freehold_property: "freehold_properties",
          freehold_villa: "freehold_villas",
          leasehold_villa: "leasehold_villas",
          freehold_land: "freehold_lands",
          leasehold_land: "leasehold_lands",
          commercial: "freehold_commercial", // legacy mapping - old commercial becomes freehold
          freehold_commercial: "freehold_commercial",
          leasehold_commercial: "leasehold_commercial",

          // handle formatted display names (title case with spaces)
          Rentals: "rentals",
          "Freehold Property": "freehold_properties",
          "Freehold Properties": "freehold_properties",
          "Freehold Villa": "freehold_villas",
          "Freehold Villas": "freehold_villas",
          "Leasehold Villa": "leasehold_villas",
          "Leasehold Villas": "leasehold_villas",
          "Freehold Land": "freehold_lands",
          "Leasehold Land": "leasehold_lands",
          Commercial: "freehold_commercial", // legacy mapping
          "Freehold Commercial": "freehold_commercial",
          "Leasehold Commercial": "leasehold_commercial",
        };

        const mappedTypes: PropertyType[] = (
          existingProperty as unknown as { types: Array<{ type: string }> }
        ).types
          .map((type: { type: string }) => {
            let mappedType = typeMap[type.type] || null;

            // fallback: try to convert unrecognized formats
            if (!mappedType && type.type) {
              const normalizedType = type.type
                .toLowerCase()
                .replace(/\s+/g, "_")
                .replace(/ies$/, "y") // "properties" -> "property"
                .replace(/s$/, ""); // remove trailing "s" from plurals

              mappedType = typeMap[normalizedType] || null;

              // if still no match, try adding plural "s" to match our form types
              if (!mappedType && !normalizedType.endsWith("s")) {
                const pluralType = normalizedType + "s";
                const pluralMatch = PROPERTY_TYPES.find(
                  (t) => t.value === pluralType,
                );
                if (pluralMatch) {
                  mappedType = pluralMatch.value as PropertyType;
                }
              }
            }

            return mappedType;
          })
          .filter(Boolean);

        console.log("Final mapped property types:", mappedTypes);
        setSelectedPropertyTypes(mappedTypes);

        // also set the first type for backward compatibility
        if (mappedTypes.length > 0) {
          setSelectedPropertyType(mappedTypes[0]);
        }

        // update main form with selected property types
        mainForm.setValue("propertyTypes.selectedTypes", mappedTypes);
      } else if (existingProperty.propertyType) {
        // fallback to single type for backward compatibility
        console.log(
          "Single property type from data:",
          existingProperty.propertyType,
        );
        const typeMap: { [key: string]: PropertyType } = {
          rentals: "rentals",
          freehold_property: "freehold_properties",
          freehold_villa: "freehold_villas",
          leasehold_villa: "leasehold_villas",
          freehold_land: "freehold_lands",
          leasehold_land: "leasehold_lands",
          commercial: "freehold_commercial",
          Rentals: "rentals",
          "Freehold Property": "freehold_properties",
          "Freehold Properties": "freehold_properties",
          "Freehold Villa": "freehold_villas",
          "Freehold Villas": "freehold_villas",
          "Leasehold Villa": "leasehold_villas",
          "Leasehold Villas": "leasehold_villas",
          "Freehold Land": "freehold_lands",
          "Leasehold Land": "leasehold_lands",
          Commercial: "freehold_commercial", // legacy mapping
          "Freehold Commercial": "freehold_commercial",
          "Leasehold Commercial": "leasehold_commercial",
        };

        let mappedType = typeMap[existingProperty.propertyType] || null;
        if (!mappedType && existingProperty.propertyType) {
          const normalizedType = existingProperty.propertyType
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/ies$/, "y")
            .replace(/s$/, "");
          mappedType = typeMap[normalizedType] || null;
          if (!mappedType && !normalizedType.endsWith("s")) {
            const pluralType = normalizedType + "s";
            const pluralMatch = PROPERTY_TYPES.find(
              (t) => t.value === pluralType,
            );
            if (pluralMatch) {
              mappedType = pluralMatch.value as PropertyType;
            }
          }
        }

        console.log("Final mapped property type:", mappedType);
        setSelectedPropertyType(mappedType);
        setSelectedPropertyTypes(mappedType ? [mappedType] : []);

        // update main form with selected property type
        mainForm.setValue(
          "propertyTypes.selectedTypes",
          mappedType ? [mappedType] : [],
        );

        // reset react-hook-form instance for single type
        if (mappedType) {
          const form = typeForms[mappedType as keyof typeof typeForms];
          if (form) {
            // get the data from the existing property data
            const existingData = existingProperty.specificPropertyData;
            let data: any = {};
            switch (mappedType) {
              case "rentals":
                data = {
                  bedrooms: existingData.bedrooms || 0,
                  bathrooms: existingData.bathrooms || 0,
                  land_size: existingData.land_size || 0,
                  build_size: existingData.build_size || 0,
                  price_per_year: existingData.price_per_year || 0,
                  minimum_duration: existingData.minimum_duration || 0,
                  safety_deposit_amount:
                    existingData.safety_deposit_amount || 0,
                  pet_allowed: existingData.pet_allowed || "",
                  orientation: toOrientationArray(existingData.orientation),
                };
                break;
              case "freehold_properties":
                data = {
                  bedrooms: existingData.bedrooms || 0,
                  bathrooms: existingData.bathrooms || 0,
                  build_size: existingData.build_size || 0,
                  land_size: existingData.land_size || 0,
                  year_built:
                    existingData.year_built || new Date().getFullYear(),
                  total_price: existingData.total_price || 0,
                  dp_minimal: existingData.dp_minimal || 0,
                  pks_bank: existingData.pks_bank || "",
                  orientation: toOrientationArray(existingData.orientation),
                };
                break;
              case "freehold_villas":
                data = {
                  bedrooms: existingData.bedrooms || 0,
                  bathrooms: existingData.bathrooms || 0,
                  build_size: existingData.build_size || 0,
                  land_size: existingData.land_size || 0,
                  year_built:
                    existingData.year_built || new Date().getFullYear(),
                  total_price: existingData.total_price || 0,
                  dp_minimal: existingData.dp_minimal || 0,
                  pks_bank: existingData.pks_bank || "",
                  orientation: toOrientationArray(existingData.orientation),
                };
                break;
              case "leasehold_villas":
                data = {
                  bedrooms: existingData.bedrooms || 0,
                  bathrooms: existingData.bathrooms || 0,
                  build_size: existingData.build_size || 0,
                  land_size: existingData.land_size || 0,
                  year_built:
                    existingData.year_built || new Date().getFullYear(),
                  price_for_duration: existingData.price_for_duration || 0,
                  extension_duration: existingData.extension_duration || 0,
                  leasehold_duration: existingData.leasehold_duration || 0,
                  orientation: toOrientationArray(existingData.orientation),
                };
                break;
              case "freehold_lands":
                data = {
                  land_size: existingData.land_size || 0,
                  price_per_are: existingData.price_per_are || 0,
                  total_price: existingData.total_price || 0,
                  road_access_width: existingData.road_access_width || 0,
                  land_zoning: existingData.land_zoning || "",
                  view: existingData.view || "",
                  orientation: toOrientationArray(existingData.orientation),
                };
                break;
              case "leasehold_lands":
                data = {
                  total_price: existingData.total_price || 0,
                  lease_duration: existingData.lease_duration || 0,
                  land_size: existingData.land_size || 0,
                  price_per_are: existingData.price_per_are || 0,
                  land_zoning: existingData.land_zoning || "",
                  view: existingData.view || "",
                  orientation: toOrientationArray(existingData.orientation),
                };
                break;
              case "freehold_commercial":
                data = {
                  bedrooms: existingData.bedrooms || 0,
                  bathrooms: existingData.bathrooms || 0,
                  land_size: existingData.land_size || 0,
                  build_size: existingData.build_size || 0,
                  title: existingData.title || "",
                  building_permit: existingData.building_permit || "",
                  frontage: existingData.frontage || 0,
                  orientation: toOrientationArray(existingData.orientation),
                  accessibility: existingData.accessibility || "",
                  year_built:
                    existingData.year_built || new Date().getFullYear(),
                  bank_partnership: existingData.bank_partnership || "",
                  down_payment: existingData.down_payment || 0,
                  total_price: existingData.total_price || 0,
                };
                break;
              case "leasehold_commercial":
                data = {
                  bedrooms: existingData.bedrooms || 0,
                  bathrooms: existingData.bathrooms || 0,
                  land_size: existingData.land_size || 0,
                  build_size: existingData.build_size || 0,
                  title: existingData.title || "",
                  building_permit: existingData.building_permit || "",
                  frontage: existingData.frontage || 0,
                  orientation: toOrientationArray(existingData.orientation),
                  accessibility: existingData.accessibility || "",
                  year_built:
                    existingData.year_built || new Date().getFullYear(),
                  price_for_duration: existingData.price_for_duration || 0,
                  extension_duration: existingData.extension_duration || 0,
                  leasehold_duration: existingData.leasehold_duration || 0,
                };
                break;
              case "freehold_commercial":
                // handle freehold_commercial (legacy "commercial" already mapped)
                data = {
                  bedrooms: existingData.bedrooms || 0,
                  bathrooms: existingData.bathrooms || 0,
                  land_size: existingData.land_size || 0,
                  build_size: existingData.build_size || 0,
                  title: existingData.title || "",
                  building_permit: existingData.building_permit || "",
                  frontage: existingData.frontage || 0,
                  orientation: toOrientationArray(existingData.orientation),
                  accessibility: existingData.accessibility || "",
                  year_built:
                    existingData.year_built || new Date().getFullYear(),
                  bank_partnership: existingData.bank_partnership || "",
                  down_payment: existingData.down_payment || 0,
                  total_price: existingData.total_price || 0,
                };
                break;
            }
            console.log(
              `Resetting single type ${mappedType} form with data:`,
              data,
            );

            // reset the form with the data
            form.reset(data as Record<string, unknown>);

            // force re-render immediately
            setFormKey((prev) => prev + 1);

            // use setTimeout to ensure the form reset happens after render
            setTimeout(() => {
              console.log(
                "Setting form values for",
                mappedType,
                "with data:",
                data,
              );

              // also set individual field values to ensure they update
              Object.keys(data).forEach((key) => {
                console.log(`Setting ${key} to:`, data[key]);
                form.setValue(key as any, data[key], {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              });

              // check form values after setting
              console.log("Form values after setting:", form.getValues());
            }, 100);
          }
        }
      } else {
        console.log("No property type found in existing property data");
        console.log("existingProperty keys:", Object.keys(existingProperty));
      }

      // set facilities data
      if (existingProperty.facilities) {
        console.log("Facilities data:", existingProperty.facilities);
        // map furniture database values to form values
        const furnitureMapping: { [key: string]: string } = {
          "Full furnished": "fully_furnished",
          "Full-Furnished": "fully_furnished",
          "Fully Furnished": "fully_furnished",
          "Semi furnished": "semi_furnished",
          "Semi-Furnished": "semi_furnished",
          "Semi Furnished": "semi_furnished",
          Unfurnished: "unfurnished",
          "Un-furnished": "unfurnished",
          "Un furnished": "unfurnished",
        };

        const furnitureValue = existingProperty.facilities.furniture
          ? furnitureMapping[existingProperty.facilities.furniture] ||
            existingProperty.facilities.furniture
              .toLowerCase()
              .replace(/[^a-z]/g, "_")
          : "";

        const facilitiesData = {
          living_room_type: existingProperty.facilities.living_room_type || "",
          dining_rooms: existingProperty.facilities.dining_rooms || 0,
          kitchens: existingProperty.facilities.kitchens || 0,
          extra_rooms: existingProperty.facilities.extra_rooms || 0,
          electricity_power: existingProperty.facilities.electricity_power || 0,
          water_source: existingProperty.facilities.water_source || "",
          parking: existingProperty.facilities.parking || 0,
          internet: existingProperty.facilities.internet || false,
          swimming_pool: existingProperty.facilities.swimming_pool || false,
          storage: existingProperty.facilities.storage || false,
          garden: existingProperty.facilities.garden || false,
          security: existingProperty.facilities.security || false,
          view: existingProperty.facilities.view || "",
          furniture: furnitureValue,
        };

        setFacilitiesData(facilitiesData);

        // update main form with facilities data
        mainForm.setValue("facilities", facilitiesData);
      }

      // set property type specific data for multiple types
      if (
        existingProperty.propertyTypes &&
        existingProperty.propertyTypes.length > 0
      ) {
        console.log(
          "Loading data for multiple property types:",
          existingProperty.propertyTypes,
        );

        // set selected property types - deduplicate in case of duplicate entries
        const uniqueTypes = Array.from(new Set(existingProperty.propertyTypes));
        const mappedTypes = uniqueTypes
          .map((type: string) => {
            const typeMap: { [key: string]: PropertyType } = {
              rentals: "rentals",
              freehold_property: "freehold_properties",
              freehold_villa: "freehold_villas",
              leasehold_villa: "leasehold_villas",
              freehold_land: "freehold_lands",
              leasehold_land: "leasehold_lands",
              commercial: "freehold_commercial", // legacy support
              freehold_commercial: "freehold_commercial",
              leasehold_commercial: "leasehold_commercial",
              Rentals: "rentals",
              "Freehold Property": "freehold_properties",
              "Freehold Properties": "freehold_properties",
              "Freehold Villa": "freehold_villas",
              "Freehold Villas": "freehold_villas",
              "Leasehold Villa": "leasehold_villas",
              "Leasehold Villas": "leasehold_villas",
              "Freehold Land": "freehold_lands",
              "Leasehold Land": "leasehold_lands",
              "Freehold Commercial": "freehold_commercial",
              "Leasehold Commercial": "leasehold_commercial",
            };
            return typeMap[type] || (type as PropertyType);
          })
          .filter(Boolean);
        // deduplicate mapped types
        const deduplicatedMappedTypes = Array.from(new Set(mappedTypes));

        console.log("Mapped property types:", deduplicatedMappedTypes);
        setSelectedPropertyTypes(deduplicatedMappedTypes);
        setSelectedPropertyType(deduplicatedMappedTypes[0] || null);

        // update main form with selected property types
        mainForm.setValue("propertyTypes.selectedTypes", mappedTypes);

        // initialize type form data
        const newTypeFormData: Record<PropertyType, any> = {
          rentals: {},
          freehold_properties: {},
          freehold_villas: {},
          leasehold_villas: {},
          freehold_lands: {},
          leasehold_lands: {},
          freehold_commercial: {},
          leasehold_commercial: {},
        };

        // load data for each type from specificPropertyDataMap
        if (existingProperty.specificPropertyDataMap) {
          Object.entries(existingProperty.specificPropertyDataMap).forEach(
            ([type, data]) => {
              console.log(`Loading data for type ${type}:`, data);

              // normalize property type for consistent matching
              const normalizedType = type.toLowerCase().replace(/\s+/g, "_");

              // type assertion to fix typescript errors
              const typedData = data as Record<string, unknown>;

              switch (normalizedType) {
                case "rentals":
                case "rental":
                  newTypeFormData.rentals = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    price_per_year: (typedData.price_per_year as number) || 0,
                    minimum_duration:
                      (typedData.minimum_duration as number) || 0,
                    safety_deposit_amount:
                      (typedData.safety_deposit_amount as number) || 0,
                    pet_allowed: (typedData.pet_allowed as string) || "",
                    orientation: toOrientationArray(typedData.orientation),
                  };
                  break;

                case "freehold_property":
                case "freehold_properties":
                  newTypeFormData.freehold_properties = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    year_built:
                      (typedData.year_built as number) ||
                      new Date().getFullYear(),
                    total_price: (typedData.total_price as number) || 0,
                    dp_minimal: (typedData.dp_minimal as number) || 0,
                    pks_bank: (typedData.pks_bank as string) || "",
                    orientation: toOrientationArray(typedData.orientation),
                  };
                  break;

                case "freehold_villa":
                case "freehold_villas":
                  newTypeFormData.freehold_villas = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    year_built:
                      (typedData.year_built as number) ||
                      new Date().getFullYear(),
                    total_price: (typedData.total_price as number) || 0,
                    dp_minimal: (typedData.dp_minimal as number) || 0,
                    pks_bank: (typedData.pks_bank as string) || "",
                    orientation: toOrientationArray(typedData.orientation),
                  };
                  break;

                case "leasehold_villa":
                case "leasehold_villas":
                  console.log("Loading leasehold villa data:", data);
                  newTypeFormData.leasehold_villas = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    year_built:
                      (typedData.year_built as number) ||
                      new Date().getFullYear(),
                    price_for_duration:
                      (typedData.price_for_duration as number) || 0,
                    extension_duration:
                      (typedData.extension_duration as number) || 0,
                    leasehold_duration:
                      (typedData.leasehold_duration as number) || 0,
                    orientation: toOrientationArray(typedData.orientation),
                  };
                  break;

                case "freehold_land":
                case "freehold_lands":
                  newTypeFormData.freehold_lands = {
                    land_size: (typedData.land_size as number) || 0,
                    price_per_are: (typedData.price_per_are as number) || 0,
                    total_price: (typedData.total_price as number) || 0,
                    road_access_width:
                      (typedData.road_access_width as number) || 0,
                    land_zoning: (typedData.land_zoning as string) || "",
                    view: (typedData.view as string) || "",
                    orientation: toOrientationArray(typedData.orientation),
                  };
                  break;

                case "leasehold_land":
                case "leasehold_lands":
                  newTypeFormData.leasehold_lands = {
                    total_price: (typedData.total_price as number) || 0,
                    lease_duration: (typedData.lease_duration as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    price_per_are: (typedData.price_per_are as number) || 0,
                    land_zoning: (typedData.land_zoning as string) || "",
                    view: (typedData.view as string) || "",
                    orientation: toOrientationArray(typedData.orientation),
                  };
                  break;

                case "freehold_commercial":
                  newTypeFormData.freehold_commercial = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    title: (typedData.title as string) || "",
                    building_permit:
                      (typedData.building_permit as string) || "",
                    frontage: (typedData.frontage as number) || 0,
                    orientation: toOrientationArray(typedData.orientation),
                    accessibility: (typedData.accessibility as string) || "",
                    year_built:
                      (typedData.year_built as number) ||
                      new Date().getFullYear(),
                    bank_partnership:
                      (typedData.bank_partnership as string) || "",
                    down_payment: (typedData.down_payment as number) || 0,
                    total_price: (typedData.total_price as number) || 0,
                  };
                  break;
                case "leasehold_commercial":
                  newTypeFormData.leasehold_commercial = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    title: (typedData.title as string) || "",
                    building_permit:
                      (typedData.building_permit as string) || "",
                    frontage: (typedData.frontage as number) || 0,
                    orientation: toOrientationArray(typedData.orientation),
                    accessibility: (typedData.accessibility as string) || "",
                    year_built:
                      (typedData.year_built as number) ||
                      new Date().getFullYear(),
                    price_for_duration:
                      (typedData.price_for_duration as number) || 0,
                    extension_duration:
                      (typedData.extension_duration as number) || 0,
                    leasehold_duration:
                      (typedData.leasehold_duration as number) || 0,
                  };
                  break;
                case "commercial":
                  // legacy support - map to freehold_commercial (type is string, not PropertyType)
                  newTypeFormData.freehold_commercial = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    title: (typedData.title as string) || "",
                    building_permit:
                      (typedData.building_permit as string) || "",
                    frontage: (typedData.frontage as number) || 0,
                    orientation: toOrientationArray(typedData.orientation),
                    accessibility: (typedData.accessibility as string) || "",
                    year_built:
                      (typedData.year_built as number) ||
                      new Date().getFullYear(),
                    bank_partnership:
                      (typedData.bank_partnership as string) || "",
                    down_payment: (typedData.down_payment as number) || 0,
                    total_price: (typedData.total_price as number) || 0,
                  };
                  // also initialize leasehold_commercial with shared fields so user can switch to it
                  newTypeFormData.leasehold_commercial = {
                    bedrooms: (typedData.bedrooms as number) || 0,
                    bathrooms: (typedData.bathrooms as number) || 0,
                    land_size: (typedData.land_size as number) || 0,
                    build_size: (typedData.build_size as number) || 0,
                    title: (typedData.title as string) || "",
                    building_permit:
                      (typedData.building_permit as string) || "",
                    frontage: (typedData.frontage as number) || 0,
                    orientation: toOrientationArray(typedData.orientation),
                    accessibility: (typedData.accessibility as string) || "",
                    year_built:
                      (typedData.year_built as number) ||
                      new Date().getFullYear(),
                    price_for_duration: 0,
                    extension_duration: 0,
                    leasehold_duration: 0,
                  };
                  break;
              }
            },
          );
        }

        // set the type form data
        setTypeFormData(newTypeFormData);
        console.log("Set type form data:", newTypeFormData);

        // reset the actual form instances with the loaded data
        Object.entries(newTypeFormData).forEach(([type, data]) => {
          if (data && Object.keys(data).length > 0) {
            const form = typeForms[type as keyof typeof typeForms];
            if (form) {
              console.log(`Resetting ${type} form with data:`, data);
              form.reset(data as Record<string, unknown>);

              // force re-render for this specific form
              setFormKey((prev) => prev + 1);
            }
          }
        });
      } else if (existingProperty.specificPropertyData) {
        // fallback to single type for backward compatibility
        const data = existingProperty.specificPropertyData;
        console.log("Specific property data:", data);
        console.log(
          "Processing for property type:",
          existingProperty.propertyType,
        );

        // normalize property type for consistent matching
        const normalizedPropertyType = existingProperty.propertyType
          .toLowerCase()
          .replace(/\s+/g, "_");

        console.log(
          "Normalized property type for data loading:",
          normalizedPropertyType,
        );
        // prepare data for react-hook-form reset
        let formData: Record<string, unknown> = {};

        switch (normalizedPropertyType) {
          case "rentals":
          case "rental":
            formData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              land_size: data.land_size?.toString() || "",
              build_size: data.build_size?.toString() || "",
              price_per_year: data.price_per_year?.toString() || "",
              minimum_duration: data.minimum_duration || 0,
              safety_deposit_amount:
                data.safety_deposit_amount?.toString() || "",
              pet_allowed: data.pet_allowed || "",
              orientation: toOrientationArray(data.orientation),
            };
            break;

          case "freehold_property":
          case "freehold_properties":
            formData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              build_size: data.build_size?.toString() || "",
              land_size: data.land_size?.toString() || "",
              year_built: data.year_built || new Date().getFullYear(),
              total_price: data.total_price?.toString() || "",
              dp_minimal: data.dp_minimal?.toString() || "",
              pks_bank: data.pks_bank || "",
              orientation: toOrientationArray(data.orientation),
            };
            break;

          case "freehold_villa":
          case "freehold_villas":
            formData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              build_size: data.build_size?.toString() || "",
              land_size: data.land_size?.toString() || "",
              year_built: data.year_built || new Date().getFullYear(),
              total_price: data.total_price?.toString() || "",
              dp_minimal: data.dp_minimal?.toString() || "",
              pks_bank: data.pks_bank || "",
              orientation: toOrientationArray(data.orientation),
            };
            break;

          case "leasehold_villa":
          case "leasehold_villas":
            formData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              build_size: data.build_size?.toString() || "",
              land_size: data.land_size?.toString() || "",
              year_built: data.year_built || new Date().getFullYear(),
              price_for_duration: data.price_for_duration?.toString() || "",
              extension_duration: data.extension_duration || 0,
              leasehold_duration: data.leasehold_duration || 0,
              orientation: toOrientationArray(data.orientation),
            };
            break;

          case "freehold_land":
          case "freehold_lands":
            formData = {
              land_size: data.land_size?.toString() || "",
              price_per_are: data.price_per_are?.toString() || "",
              total_price: data.total_price?.toString() || "",
              road_access_width: data.road_access_width?.toString() || "",
              land_zoning: data.land_zoning || "",
              view: data.view || "",
              orientation: toOrientationArray(data.orientation),
            };
            break;

          case "leasehold_land":
          case "leasehold_lands":
            formData = {
              total_price: data.total_price?.toString() || "",
              lease_duration: data.lease_duration || 0,
              land_size: data.land_size?.toString() || "",
              price_per_are: data.price_per_are?.toString() || "",
              land_zoning: data.land_zoning || "",
              view: data.view || "",
              orientation: toOrientationArray(data.orientation),
            };
            break;

          case "freehold_commercial":
            formData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              land_size: data.land_size?.toString() || "",
              build_size: data.build_size?.toString() || "",
              title: data.title || "",
              building_permit: data.building_permit || "",
              frontage: data.frontage?.toString() || "",
              orientation: toOrientationArray(data.orientation),
              accessibility: data.accessibility || "",
              year_built: data.year_built || new Date().getFullYear(),
              bank_partnership: data.bank_partnership || "",
              down_payment: data.down_payment?.toString() || "",
              total_price: data.total_price?.toString() || "",
            };
            break;
          case "leasehold_commercial":
            formData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              land_size: data.land_size?.toString() || "",
              build_size: data.build_size?.toString() || "",
              title: data.title || "",
              building_permit: data.building_permit || "",
              frontage: data.frontage?.toString() || "",
              orientation: toOrientationArray(data.orientation),
              accessibility: data.accessibility || "",
              year_built: data.year_built || new Date().getFullYear(),
              price_for_duration: data.price_for_duration?.toString() || "",
              extension_duration: data.extension_duration?.toString() || "",
              leasehold_duration: data.leasehold_duration?.toString() || "",
            };
            break;
          case "commercial":
            // legacy support - map to freehold_commercial (type is string from DB, not PropertyType)
            formData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              land_size: data.land_size?.toString() || "",
              build_size: data.build_size?.toString() || "",
              title: data.title || "",
              building_permit: data.building_permit || "",
              frontage: data.frontage?.toString() || "",
              orientation: toOrientationArray(data.orientation),
              accessibility: data.accessibility || "",
              year_built: data.year_built || new Date().getFullYear(),
              bank_partnership: data.bank_partnership || "",
              down_payment: data.down_payment?.toString() || "",
              total_price: data.total_price?.toString() || "",
            };
            // also initialize leasehold_commercial form data so user can switch to it
            // this allows users to switch from Commercial/Freehold Commercial to Leasehold Commercial
            const leaseholdCommercialFormData = {
              bedrooms: data.bedrooms?.toString() || "",
              bathrooms: data.bathrooms?.toString() || "",
              land_size: data.land_size?.toString() || "",
              build_size: data.build_size?.toString() || "",
              title: data.title || "",
              building_permit: data.building_permit || "",
              frontage: data.frontage?.toString() || "",
              orientation: toOrientationArray(data.orientation),
              accessibility: data.accessibility || "",
              year_built: data.year_built || new Date().getFullYear(),
              price_for_duration: "",
              extension_duration: "",
              leasehold_duration: "",
            };
            setTypeFormData((prev) => ({
              ...prev,
              leasehold_commercial:
                leaseholdCommercialFormData as LeaseholdCommercialPropertyData,
            }));
            // reset the leasehold_commercial form instance if it exists
            const leaseholdCommercialForm = typeForms.leasehold_commercial;
            if (leaseholdCommercialForm) {
              leaseholdCommercialForm.reset(
                leaseholdCommercialFormData as Record<string, unknown>,
              );
            }
            break;
        }
      }

      hasInitialized.current = true;
      console.log("Property data initialization completed");

      // clear any existing field errors when loading data
      setFieldErrors({});

      // reset manual change flags when loading existing data
      slugManuallyChanged.current = false;
    }
  }, [mode, existingProperty?.baseProperty?.id]);

  // track selectedPropertyType changes
  useEffect(() => {
    console.log("selectedPropertyType changed to:", selectedPropertyType);
  }, [selectedPropertyType]);

  // reset initialization when modal opens/closes or propertyId changes
  useEffect(() => {
    if (!open) {
      hasInitialized.current = false;
      slugManuallyChanged.current = false;
    } else {
      // reset to step 1 when opening
      setCurrentStep(1);
    }
  }, [open, propertyId]);

  // generate slug from property name - simple 1:1 following
  useEffect(() => {
    if (basePropertyData.property_name && mode !== "edit") {
      const baseSlug = basePropertyData.property_name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-");

      const currentSlug = basePropertyData.slug;

      // if slug was manually changed, don't auto-generate
      if (slugManuallyChanged.current) {
        return;
      }

      // always generate slug when property name changes (unless manually changed)
      // let validation handle conflicts - don't auto-fix them
      setBasePropertyData((prev) => ({ ...prev, slug: baseSlug }));
    }
  }, [basePropertyData.property_name, mode]);

  // debounced reference ID checking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (basePropertyData.reff_id.trim()) {
        setReffIdDebounce(basePropertyData.reff_id);
      } else {
        setReffIdDebounce("");
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [basePropertyData.reff_id]);

  // debounced slug checking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (basePropertyData.slug.trim()) {
        setSlugDebounce(basePropertyData.slug);
      } else {
        setSlugDebounce("");
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [basePropertyData.slug]);

  // debounced property name checking
  useEffect(() => {
    const timer = setTimeout(() => {
      if (basePropertyData.property_name.trim()) {
        setPropertyNameDebounce(basePropertyData.property_name);
      } else {
        setPropertyNameDebounce("");
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [basePropertyData.property_name]);

  // social media thumbnail fetching removed - not used for video thumbnail

  const nextStep = async () => {
    // force immediate property name check if property name is filled but not yet checked
    if (basePropertyData.property_name.trim() && !propertyNameDebounce) {
      setPropertyNameDebounce(basePropertyData.property_name);
    }

    // validate current step with react-hook-form/zod
    let isValid = false;
    if (currentStep === 1) {
      isValid = await mainForm.trigger("base");
    } else if (currentStep === 2) {
      isValid = await mainForm.trigger("propertyTypes");
    } else if (currentStep === 3) {
      const typeValidations = await Promise.all(
        selectedPropertyTypes.map((type) =>
          typeForms[type as keyof typeof typeForms]?.trigger(),
        ),
      );
      isValid = typeValidations.every((v) => v);
    } else if (currentStep === 4) {
      isValid = await mainForm.trigger("facilities");
    } else if (currentStep === 5) {
      isValid = true;
    } else if (currentStep === 6) {
      isValid = true;
    } else if (currentStep === 7) {
      isValid = addContact ? await mainForm.trigger("contact") : true;
    }

    if (!isValid) {
      // collect human-friendly error messages even if fields are not wrapped in FormField
      const messages: string[] = [];
      if (currentStep === 1) {
        const baseErrors = mainForm.formState.errors.base as any;
        if (baseErrors) {
          Object.keys(baseErrors).forEach((k) => {
            const e = baseErrors[k];
            if (e && typeof e.message === "string" && e.message)
              messages.push(e.message);
          });
        }
      } else if (currentStep === 2) {
        const ptErrors = mainForm.formState.errors.propertyTypes as any;
        if (
          ptErrors &&
          ptErrors.selectedTypes &&
          ptErrors.selectedTypes.message
        ) {
          messages.push(String(ptErrors.selectedTypes.message));
        } else if (selectedPropertyTypes.length === 0) {
          messages.push("Please select at least one property type");
        }
      } else if (currentStep === 3) {
        // validate each selected type using zod directly to get messages
        selectedPropertyTypes.forEach((type) => {
          const schema = TYPE_SCHEMAS[type];
          const data = typeFormData[type] || {};
          const res = schema.safeParse(data);
          if (!res.success && res.error) {
            const flat = res.error.flatten();
            Object.values(flat.fieldErrors).forEach((arr) => {
              if (Array.isArray(arr)) {
                arr.forEach((m) => {
                  if (m) messages.push(String(m));
                });
              }
            });
          }
        });
      } else if (currentStep === 4) {
        const facErrors = mainForm.formState.errors.facilities as any;
        if (facErrors) {
          Object.keys(facErrors).forEach((k) => {
            const e = facErrors[k];
            if (e && typeof e.message === "string" && e.message)
              messages.push(e.message);
          });
        }
      } else if (currentStep === 7 && addContact) {
        const cErrors = mainForm.formState.errors.contact as any;
        if (cErrors) {
          Object.keys(cErrors).forEach((k) => {
            const e = cErrors[k];
            if (e && typeof e.message === "string" && e.message)
              messages.push(e.message);
          });
        }
      }

      // scroll/focus: try rhf data-error first, else fallback to computed ids
      const firstError = document.querySelector('[data-error="true"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        const firstInvalidId = getFirstInvalidFieldId();
        if (firstInvalidId) focusAndScrollTo(firstInvalidId);
      }

      if (messages.length > 0) {
        toast.error(
          "Please fix the following issues:\n• " + messages.join("\n• "),
        );
      } else {
        toast.error("Please fix the highlighted errors before continuing.");
      }
      return;
    }

    if (currentStep < STEPS.length) {
      // skip facilities step if property type doesn't have facilities
      if (currentStep === 3 && selectedPropertyType) {
        const propertyType = PROPERTY_TYPES.find(
          (t) => t.value === selectedPropertyType,
        );
        if (!propertyType?.hasFacilities) {
          setCurrentStep(6); // skip to review
          return;
        }
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // skip facilities step when going back if property type doesn't have facilities
      if (currentStep === 5 && selectedPropertyType) {
        const propertyType = PROPERTY_TYPES.find(
          (t) => t.value === selectedPropertyType,
        );
        if (!propertyType?.hasFacilities) {
          setCurrentStep(3); // go back to type details
          return;
        }
      }
      if (currentStep === 6 && selectedPropertyType) {
        const propertyType = PROPERTY_TYPES.find(
          (t) => t.value === selectedPropertyType,
        );
        if (!propertyType?.hasFacilities) {
          setCurrentStep(3); // go back to type details
          return;
        }
      }
      setCurrentStep(currentStep - 1);
    }
  };

  // focus and scroll helpers for invalid fields
  const focusAndScrollTo = (elementId: string) => {
    try {
      const el = document.getElementById(elementId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof (el as HTMLInputElement).focus === "function") {
          (el as HTMLInputElement).focus();
        }

        // add temporary red error styling to indicate why field was focused
        const inputEl =
          el.tagName === "INPUT"
            ? el
            : el.querySelector("input, textarea, [role='combobox']");
        if (inputEl) {
          inputEl.classList.add(
            "border-red-500",
            "focus:border-red-500",
            "focus:ring-red-500",
          );
          setTimeout(() => {
            inputEl.classList.remove(
              "border-red-500",
              "focus:border-red-500",
              "focus:ring-red-500",
            );
          }, 3000); // remove after 3 seconds
        }
      }
    } catch (_e) {
      // ignore focus errors
    }
  };

  const getFirstInvalidFieldId = (): string | null => {
    if (currentStep === 1) {
      if (!basePropertyData.property_name.trim()) return "property_name";
      if (
        !basePropertyData.property_name_idn ||
        !basePropertyData.property_name_idn.trim()
      )
        return "property_name_idn";
      if (!stripHtml(basePropertyData.description).trim())
        return "description_field";
      if (!stripHtml(basePropertyData.description_idn || "").trim())
        return "description_idn_field";
      if (!basePropertyData.reff_id.trim()) return "reff_id";
      if (!basePropertyData.latitude.trim()) return "latitude";
      if (!basePropertyData.longitude.trim()) return "longitude";
      if (!basePropertyData.map_url.trim()) return "map_url";
      if (!basePropertyData.area_id) return "area_id";
      if (!basePropertyData.slug.trim()) return "slug";
      if (!(basePropertyData.shm_condition || "").trim())
        return "shm_condition";
      if (!(basePropertyData.slf_pbg_condition || "").trim())
        return "slf_pbg_condition";
      if (!(basePropertyData.zoning || "").trim()) return "zoning";

      // conflict-based focusing if fields are filled but conflicting
      const reffIdConflict =
        reffIdExists &&
        reffIdExists.exists &&
        basePropertyData.reff_id.trim() &&
        (reffIdExists.count > 1 ||
          (reffIdExists.count === 1 &&
            (!existingProperty ||
              existingProperty.baseProperty.reff_id !==
                basePropertyData.reff_id)));
      if (reffIdConflict) return "reff_id";

      const propertyNameConflict =
        propertyNameExists &&
        propertyNameExists.exists &&
        basePropertyData.property_name.trim() &&
        (propertyNameExists.count > 1 ||
          (propertyNameExists.count === 1 &&
            (!existingProperty ||
              existingProperty.baseProperty.property_name !==
                basePropertyData.property_name)));
      if (propertyNameConflict) return "property_name";

      const slugConflict =
        slugExists &&
        slugExists.exists &&
        basePropertyData.slug.trim() &&
        (slugExists.count > 1 ||
          (slugExists.count === 1 &&
            (!existingProperty ||
              existingProperty.baseProperty.slug !== basePropertyData.slug)));
      if (slugConflict) return "slug";

      return null;
    }

    if (currentStep === 2) {
      if (!selectedPropertyType) return "property_type_section";
      return null;
    }

    if (currentStep === 3) {
      if (!selectedPropertyType) return "property_type_section";
      switch (selectedPropertyType) {
        case "rentals":
          if (!rentalsData.bedrooms.trim()) return "bedrooms";
          if (!rentalsData.bathrooms.trim()) return "bathrooms";
          if (!rentalsData.land_size.trim()) return "rental_land_size";
          if (!rentalsData.build_size.trim()) return "rental_build_size";
          if (!rentalsData.price_per_year.trim()) return "price_per_year";
          if (rentalsData.minimum_duration <= 0) return "minimum_duration";
          if (!rentalsData.safety_deposit_amount.trim())
            return "safety_deposit";
          if (!rentalsData.pet_allowed.trim()) return "pet_allowed_trigger";
          return null;
        case "freehold_properties":
          if (!freeholdPropertyData.bedrooms.trim()) return "bedrooms";
          if (!freeholdPropertyData.bathrooms.trim()) return "bathrooms";
          if (!freeholdPropertyData.build_size.trim()) return "fh_build_size";
          if (!freeholdPropertyData.land_size.trim()) return "fh_land_size";
          if (!freeholdPropertyData.total_price.trim()) return "fh_total_price";
          if (!freeholdPropertyData.pks_bank.trim()) return "fh_pks_bank";
          return null;
        case "freehold_villas":
          if (!freeholdVillaData.bedrooms.trim()) return "bedrooms";
          if (!freeholdVillaData.bathrooms.trim()) return "bathrooms";
          if (!freeholdVillaData.build_size.trim()) return "fhv_build_size";
          if (!freeholdVillaData.land_size.trim()) return "fhv_land_size";
          if (!freeholdVillaData.total_price.trim()) return "fhv_total_price";
          if (!freeholdVillaData.pks_bank.trim()) return "fhv_pks_bank";
          return null;
        case "leasehold_villas":
          if (!leaseholdVillaData.bedrooms.trim()) return "bedrooms";
          if (!leaseholdVillaData.bathrooms.trim()) return "bathrooms";
          if (!leaseholdVillaData.build_size.trim()) return "lhv_build_size";
          if (!leaseholdVillaData.land_size.trim()) return "lhv_land_size";
          if (!leaseholdVillaData.price_for_duration.trim())
            return "price_for_duration";
          if (leaseholdVillaData.leasehold_duration <= 0)
            return "lhv_leasehold_duration";
          return null;
        case "freehold_lands":
          if (!freeholdLandData.land_size.trim()) return "fhl_land_size";
          if (!freeholdLandData.price_per_are.trim())
            return "fhl_price_per_are";
          if (!freeholdLandData.total_price.trim()) return "fhl_total_price";
          if (!freeholdLandData.road_access_width.trim())
            return "road_access_width";
          if (!freeholdLandData.land_zoning.trim()) return "fhl_land_zoning";
          if (!freeholdLandData.view.trim()) return "fhl_view";
          return null;
        case "leasehold_lands":
          if (!leaseholdLandData.land_size.trim()) return "lhl_land_size";
          if (!leaseholdLandData.price_per_are.trim())
            return "lhl_price_per_are";
          if (!leaseholdLandData.total_price.trim()) return "lhl_total_price";
          if (leaseholdLandData.lease_duration <= 0) return "lease_duration";
          if (!leaseholdLandData.land_zoning.trim()) return "lhl_land_zoning";
          if (!leaseholdLandData.view.trim()) return "lhl_view";
          return null;
        case "freehold_commercial":
          const freeholdCommercialData = typeFormData.freehold_commercial || {};
          // bedrooms is optional, so skip validation if empty
          if (
            freeholdCommercialData.bedrooms?.toString().trim() &&
            (isNaN(
              parseInt(freeholdCommercialData.bedrooms?.toString() || "0"),
            ) ||
              parseInt(freeholdCommercialData.bedrooms?.toString() || "0") <= 0)
          )
            return "bedrooms";
          if (!freeholdCommercialData.bathrooms?.toString().trim())
            return "bathrooms";
          if (!freeholdCommercialData.land_size?.toString().trim())
            return "land_size";
          if (!freeholdCommercialData.build_size?.toString().trim())
            return "build_size";
          if (!freeholdCommercialData.title?.toString().trim()) return "title";
          if (!freeholdCommercialData.building_permit?.toString().trim())
            return "building_permit";
          if (!freeholdCommercialData.frontage?.toString().trim())
            return "frontage";
          if (!freeholdCommercialData.orientation?.toString().trim())
            return "orientation";
          if (!freeholdCommercialData.accessibility?.toString().trim())
            return "accessibility";
          if (!freeholdCommercialData.bank_partnership?.toString().trim())
            return "bank_partnership";
          if (!freeholdCommercialData.down_payment?.toString().trim())
            return "down_payment";
          if (!freeholdCommercialData.total_price?.toString().trim())
            return "total_price";
          return null;
        case "leasehold_commercial":
          const leaseholdCommercialData =
            typeFormData.leasehold_commercial || {};
          // bedrooms is optional, so skip validation if empty
          if (
            leaseholdCommercialData.bedrooms?.toString().trim() &&
            (isNaN(
              parseInt(leaseholdCommercialData.bedrooms?.toString() || "0"),
            ) ||
              parseInt(leaseholdCommercialData.bedrooms?.toString() || "0") <=
                0)
          )
            return "bedrooms";
          if (!leaseholdCommercialData.bathrooms?.toString().trim())
            return "bathrooms";
          if (!leaseholdCommercialData.land_size?.toString().trim())
            return "land_size";
          if (!leaseholdCommercialData.build_size?.toString().trim())
            return "build_size";
          if (!leaseholdCommercialData.title?.toString().trim()) return "title";
          if (!leaseholdCommercialData.building_permit?.toString().trim())
            return "building_permit";
          if (!leaseholdCommercialData.frontage?.toString().trim())
            return "frontage";
          if (!leaseholdCommercialData.orientation?.toString().trim())
            return "orientation";
          if (!leaseholdCommercialData.accessibility?.toString().trim())
            return "accessibility";
          if (!leaseholdCommercialData.price_for_duration?.toString().trim())
            return "price_for_duration";
          if (leaseholdCommercialData.leasehold_duration <= 0)
            return "leasehold_duration";
          return null;
        case "freehold_commercial":
          // validate freehold_commercial
          const freeholdCommercialValidationData =
            typeFormData.freehold_commercial || {};
          if (
            freeholdCommercialValidationData.bedrooms?.toString().trim() &&
            (isNaN(
              parseInt(
                freeholdCommercialValidationData.bedrooms?.toString() || "0",
              ),
            ) ||
              parseInt(
                freeholdCommercialValidationData.bedrooms?.toString() || "0",
              ) <= 0)
          )
            return "bedrooms";
          if (!freeholdCommercialValidationData.bathrooms?.toString().trim())
            return "bathrooms";
          if (!freeholdCommercialValidationData.land_size?.toString().trim())
            return "land_size";
          if (!freeholdCommercialValidationData.build_size?.toString().trim())
            return "build_size";
          if (!freeholdCommercialValidationData.title?.toString().trim())
            return "title";
          if (
            !freeholdCommercialValidationData.building_permit?.toString().trim()
          )
            return "building_permit";
          if (!freeholdCommercialValidationData.frontage?.toString().trim())
            return "frontage";
          if (!freeholdCommercialValidationData.orientation?.toString().trim())
            return "orientation";
          if (
            !freeholdCommercialValidationData.accessibility?.toString().trim()
          )
            return "accessibility";
          if (
            !freeholdCommercialValidationData.bank_partnership
              ?.toString()
              .trim()
          )
            return "bank_partnership";
          if (!freeholdCommercialValidationData.down_payment?.toString().trim())
            return "down_payment";
          if (!freeholdCommercialValidationData.total_price?.toString().trim())
            return "total_price";
          return null;
      }
    }

    if (currentStep === 7) {
      if (!addContact) return null;
      if (!contactData.phone.trim()) return "c_phone";
      if (!contactData.country.trim()) return "c_country";
      return null;
    }

    return null;
  };

  const navigateToStep = (stepId: number) => {
    // when editing, allow free navigation to any step
    if (mode === "edit") {
      setCurrentStep(stepId);
      return;
    }

    // for create mode, only allow navigation to previous or current step
    if (stepId <= currentStep) {
      setCurrentStep(stepId);
      return;
    }

    // for forward navigation in create mode, ensure current step is valid
    if (!isStepValid()) {
      const errors = getValidationErrors();
      const errorMessage =
        errors.length > 0
          ? "Please fix the following issues:\n• " + errors.join("\n• ")
          : "Please complete the current step before proceeding.";
      toast.error(errorMessage);
      const firstInvalidId = getFirstInvalidFieldId();
      if (firstInvalidId) focusAndScrollTo(firstInvalidId);
      return;
    }

    setCurrentStep(stepId);
  };

  // auto-scroll the horizontal step header to keep the current step in view
  useEffect(() => {
    const container = stepsContainerRef.current;
    if (!container) return;
    const selector = '[data-step-id="' + currentStep + '"]';
    const active = container.querySelector(selector) as HTMLElement | null;
    if (!active) return;
    const itemLeft = active.offsetLeft;
    const itemWidth = active.offsetWidth;
    const target = itemLeft - (container.clientWidth - itemWidth) / 2;
    container.scrollTo({ left: target, behavior: "smooth" });
  }, [currentStep]);

  // get validation errors for current step
  const getValidationErrors = (): string[] => {
    const errors: string[] = [];

    // check for field validation errors first
    Object.values(fieldErrors).forEach((error) => {
      if (error) errors.push(error);
    });

    switch (currentStep) {
      case 1:
        if (!basePropertyData.property_name.trim())
          errors.push("Property name is required");
        // check for property name conflicts (exclude current property from duplicate check)
        else if (
          propertyNameExists &&
          propertyNameExists.exists &&
          basePropertyData.property_name.trim() &&
          (propertyNameExists.count > 1 ||
            (propertyNameExists.count === 1 &&
              (!existingProperty ||
                existingProperty.baseProperty.property_name !==
                  basePropertyData.property_name)))
        )
          errors.push(
            propertyNameExists.count > 1
              ? `Property name already exists (${propertyNameExists.count} properties found)`
              : "Property name already exists",
          );
        if (!basePropertyData.property_name_idn?.trim())
          errors.push("Property name (Indonesian) is required");
        if (!stripHtml(basePropertyData.description).trim())
          errors.push("Description is required");
        if (!stripHtml(basePropertyData.description_idn || "").trim())
          errors.push("Description (Indonesian) is required");
        if (!basePropertyData.reff_id.trim())
          errors.push("Reference ID is required");
        // check for reference ID conflicts (only against non-deleted properties)
        else if (
          reffIdExists &&
          reffIdExists.exists &&
          basePropertyData.reff_id.trim() &&
          (reffIdExists.count > 1 ||
            (reffIdExists.count === 1 &&
              (!existingProperty ||
                existingProperty.baseProperty.reff_id !==
                  basePropertyData.reff_id)))
        )
          errors.push(
            reffIdExists.count > 1
              ? `Reference ID already exists (${reffIdExists.count} properties found)`
              : "Reference ID already exists",
          );
        if (!basePropertyData.latitude.trim())
          errors.push("Latitude is required");
        if (!basePropertyData.longitude.trim())
          errors.push("Longitude is required");
        if (!basePropertyData.map_url.trim())
          errors.push("Map URL is required");
        if (!basePropertyData.area_id)
          errors.push("Area selection is required");
        if (!basePropertyData.slug.trim()) errors.push("Slug is required");
        // check for slug conflicts (only against non-deleted properties)
        else if (
          slugExists &&
          slugExists.exists &&
          basePropertyData.slug.trim() &&
          (slugExists.count > 1 ||
            (slugExists.count === 1 &&
              (!existingProperty ||
                existingProperty.baseProperty.slug !== basePropertyData.slug)))
        )
          errors.push(
            slugExists.count > 1
              ? `Slug already exists (${slugExists.count} properties found)`
              : "Slug already exists",
          );
        break;
      case 2:
        if (selectedPropertyTypes.length === 0)
          errors.push("Please select at least one property type");
        break;
      case 3:
        if (selectedPropertyTypes.length === 0) {
          errors.push(
            "Please select at least one property type before filling details",
          );
          break;
        }
        selectedPropertyTypes.forEach((type) => {
          switch (type) {
            case "rentals":
              if (!rentalsData.bedrooms.trim())
                errors.push("Rentals: Number of bedrooms is required");
              else if (
                isNaN(parseInt(rentalsData.bedrooms)) ||
                parseInt(rentalsData.bedrooms) <= 0
              )
                errors.push(
                  "Rentals: Number of bedrooms must be greater than 0",
                );
              if (!rentalsData.bathrooms.trim())
                errors.push("Rentals: Number of bathrooms is required");
              else if (
                isNaN(parseInt(rentalsData.bathrooms)) ||
                parseInt(rentalsData.bathrooms) <= 0
              )
                errors.push(
                  "Rentals: Number of bathrooms must be greater than 0",
                );
              if (!rentalsData.land_size.trim())
                errors.push("Rentals: Land size is required");
              else if (
                isNaN(parseFloat(rentalsData.land_size)) ||
                parseFloat(rentalsData.land_size) <= 0
              )
                errors.push(
                  "Rentals: Land size must be a valid positive number",
                );
              if (!rentalsData.build_size.trim())
                errors.push("Rentals: Build size is required");
              else if (
                isNaN(parseFloat(rentalsData.build_size)) ||
                parseFloat(rentalsData.build_size) <= 0
              )
                errors.push(
                  "Rentals: Build size must be a valid positive number",
                );
              if (!rentalsData.price_per_year.trim())
                errors.push("Rentals: Price per year is required");
              else if (
                isNaN(parseFloat(rentalsData.price_per_year)) ||
                parseFloat(rentalsData.price_per_year) <= 0
              )
                errors.push(
                  "Rentals: Price per year must be a valid positive number",
                );
              if (rentalsData.minimum_duration <= 0)
                errors.push("Rentals: Minimum duration must be greater than 0");
              if (!rentalsData.safety_deposit_amount.trim())
                errors.push("Rentals: Safety deposit amount is required");
              else if (
                isNaN(parseFloat(rentalsData.safety_deposit_amount)) ||
                parseFloat(rentalsData.safety_deposit_amount) < 0
              )
                errors.push(
                  "Rentals: Safety deposit amount must be a valid non-negative number",
                );
              if (!rentalsData.pet_allowed.trim())
                errors.push("Rentals: Pet policy is required");
              break;
            case "freehold_properties":
              if (!freeholdPropertyData.bedrooms.trim())
                errors.push(
                  "Freehold Property: Number of bedrooms is required",
                );
              else if (
                isNaN(parseInt(freeholdPropertyData.bedrooms)) ||
                parseInt(freeholdPropertyData.bedrooms) <= 0
              )
                errors.push(
                  "Freehold Property: Number of bedrooms must be greater than 0",
                );
              if (!freeholdPropertyData.bathrooms.trim())
                errors.push(
                  "Freehold Property: Number of bathrooms is required",
                );
              else if (
                isNaN(parseInt(freeholdPropertyData.bathrooms)) ||
                parseInt(freeholdPropertyData.bathrooms) <= 0
              )
                errors.push(
                  "Freehold Property: Number of bathrooms must be greater than 0",
                );
              if (!freeholdPropertyData.build_size.trim())
                errors.push("Freehold Property: Build size is required");
              else if (
                isNaN(parseFloat(freeholdPropertyData.build_size)) ||
                parseFloat(freeholdPropertyData.build_size) <= 0
              )
                errors.push(
                  "Freehold Property: Build size must be a valid positive number",
                );
              if (!freeholdPropertyData.land_size.trim())
                errors.push("Freehold Property: Land size is required");
              else if (
                isNaN(parseFloat(freeholdPropertyData.land_size)) ||
                parseFloat(freeholdPropertyData.land_size) <= 0
              )
                errors.push(
                  "Freehold Property: Land size must be a valid positive number",
                );
              if (
                freeholdPropertyData.year_built <= 1900 ||
                freeholdPropertyData.year_built > new Date().getFullYear() + 5
              )
                errors.push(
                  "Freehold Property: Year built must be between 1900 and " +
                    (new Date().getFullYear() + 5),
                );
              if (!freeholdPropertyData.total_price.trim())
                errors.push("Freehold Property: Total price is required");
              else if (
                isNaN(parseFloat(freeholdPropertyData.total_price)) ||
                parseFloat(freeholdPropertyData.total_price) <= 0
              )
                errors.push(
                  "Freehold Property: Total price must be a valid positive number",
                );
              if (!freeholdPropertyData.dp_minimal.trim())
                errors.push(
                  "Freehold Property: Minimal down payment is required",
                );
              else if (
                isNaN(parseFloat(freeholdPropertyData.dp_minimal)) ||
                parseFloat(freeholdPropertyData.dp_minimal) < 0
              )
                errors.push(
                  "Freehold Property: Minimal down payment must be a valid non-negative number",
                );
              if (!freeholdPropertyData.pks_bank.trim())
                errors.push("Freehold Property: PKS bank is required");
              break;
            case "freehold_villas":
              if (!freeholdVillaData.bedrooms.trim())
                errors.push("Freehold Villa: Number of bedrooms is required");
              else if (
                isNaN(parseInt(freeholdVillaData.bedrooms)) ||
                parseInt(freeholdVillaData.bedrooms) <= 0
              )
                errors.push(
                  "Freehold Villa: Number of bedrooms must be greater than 0",
                );
              if (!freeholdVillaData.bathrooms.trim())
                errors.push("Freehold Villa: Number of bathrooms is required");
              else if (
                isNaN(parseInt(freeholdVillaData.bathrooms)) ||
                parseInt(freeholdVillaData.bathrooms) <= 0
              )
                errors.push(
                  "Freehold Villa: Number of bathrooms must be greater than 0",
                );
              if (!freeholdVillaData.build_size.trim())
                errors.push("Freehold Villa: Build size is required");
              else if (
                isNaN(parseFloat(freeholdVillaData.build_size)) ||
                parseFloat(freeholdVillaData.build_size) <= 0
              )
                errors.push(
                  "Freehold Villa: Build size must be a valid positive number",
                );
              if (!freeholdVillaData.land_size.trim())
                errors.push("Freehold Villa: Land size is required");
              else if (
                isNaN(parseFloat(freeholdVillaData.land_size)) ||
                parseFloat(freeholdVillaData.land_size) <= 0
              )
                errors.push(
                  "Freehold Villa: Land size must be a valid positive number",
                );
              if (
                freeholdVillaData.year_built <= 1900 ||
                freeholdVillaData.year_built > new Date().getFullYear() + 5
              )
                errors.push(
                  "Freehold Villa: Year built must be between 1900 and " +
                    (new Date().getFullYear() + 5),
                );
              if (!freeholdVillaData.total_price.trim())
                errors.push("Freehold Villa: Total price is required");
              else if (
                isNaN(parseFloat(freeholdVillaData.total_price)) ||
                parseFloat(freeholdVillaData.total_price) <= 0
              )
                errors.push(
                  "Freehold Villa: Total price must be a valid positive number",
                );
              if (!freeholdVillaData.dp_minimal.trim())
                errors.push("Freehold Villa: Minimal down payment is required");
              else if (
                isNaN(parseFloat(freeholdVillaData.dp_minimal)) ||
                parseFloat(freeholdVillaData.dp_minimal) < 0
              )
                errors.push(
                  "Freehold Villa: Minimal down payment must be a valid non-negative number",
                );
              if (!freeholdVillaData.pks_bank.trim())
                errors.push("Freehold Villa: PKS bank is required");
              break;
            case "leasehold_villas":
              if (!leaseholdVillaData.bedrooms.trim())
                errors.push("Leasehold Villa: Number of bedrooms is required");
              else if (
                isNaN(parseInt(leaseholdVillaData.bedrooms)) ||
                parseInt(leaseholdVillaData.bedrooms) <= 0
              )
                errors.push(
                  "Leasehold Villa: Number of bedrooms must be greater than 0",
                );
              if (!leaseholdVillaData.bathrooms.trim())
                errors.push("Leasehold Villa: Number of bathrooms is required");
              else if (
                isNaN(parseInt(leaseholdVillaData.bathrooms)) ||
                parseInt(leaseholdVillaData.bathrooms) <= 0
              )
                errors.push(
                  "Leasehold Villa: Number of bathrooms must be greater than 0",
                );
              if (!leaseholdVillaData.build_size.trim())
                errors.push("Leasehold Villa: Build size is required");
              else if (
                isNaN(parseFloat(leaseholdVillaData.build_size)) ||
                parseFloat(leaseholdVillaData.build_size) <= 0
              )
                errors.push(
                  "Leasehold Villa: Build size must be a valid positive number",
                );
              if (!leaseholdVillaData.land_size.trim())
                errors.push("Leasehold Villa: Land size is required");
              else if (
                isNaN(parseFloat(leaseholdVillaData.land_size)) ||
                parseFloat(leaseholdVillaData.land_size) <= 0
              )
                errors.push(
                  "Leasehold Villa: Land size must be a valid positive number",
                );
              if (
                leaseholdVillaData.year_built <= 1900 ||
                leaseholdVillaData.year_built > new Date().getFullYear() + 5
              )
                errors.push(
                  "Leasehold Villa: Year built must be between 1900 and " +
                    (new Date().getFullYear() + 5),
                );
              if (!leaseholdVillaData.price_for_duration.trim())
                errors.push("Leasehold Villa: Price for duration is required");
              else if (
                isNaN(parseFloat(leaseholdVillaData.price_for_duration)) ||
                parseFloat(leaseholdVillaData.price_for_duration) <= 0
              )
                errors.push(
                  "Leasehold Villa: Price for duration must be a valid positive number",
                );
              if (leaseholdVillaData.leasehold_duration <= 0)
                errors.push(
                  "Leasehold Villa: Leasehold duration must be greater than 0",
                );
              break;
            case "freehold_lands":
              if (!freeholdLandData.land_size.trim())
                errors.push("Freehold Land: Land size is required");
              else if (
                isNaN(parseFloat(freeholdLandData.land_size)) ||
                parseFloat(freeholdLandData.land_size) <= 0
              )
                errors.push(
                  "Freehold Land: Land size must be a valid positive number",
                );
              if (!freeholdLandData.price_per_are.trim())
                errors.push("Freehold Land: Price per are is required");
              else if (
                isNaN(parseFloat(freeholdLandData.price_per_are)) ||
                parseFloat(freeholdLandData.price_per_are) <= 0
              )
                errors.push(
                  "Freehold Land: Price per are must be a valid positive number",
                );
              if (!freeholdLandData.total_price.trim())
                errors.push("Freehold Land: Total price is required");
              else if (
                isNaN(parseFloat(freeholdLandData.total_price)) ||
                parseFloat(freeholdLandData.total_price) <= 0
              )
                errors.push(
                  "Freehold Land: Total price must be a valid positive number",
                );
              if (!freeholdLandData.road_access_width.trim())
                errors.push("Freehold Land: Road access width is required");
              else if (
                isNaN(parseFloat(freeholdLandData.road_access_width)) ||
                parseFloat(freeholdLandData.road_access_width) <= 0
              )
                errors.push(
                  "Freehold Land: Road access width must be a valid positive number",
                );
              if (!freeholdLandData.land_zoning.trim())
                errors.push("Freehold Land: Land zoning is required");
              if (!freeholdLandData.view.trim())
                errors.push("Freehold Land: View description is required");
              break;
            case "leasehold_lands":
              if (!leaseholdLandData.total_price.trim())
                errors.push("Leasehold Land: Total price is required");
              else if (
                isNaN(parseFloat(leaseholdLandData.total_price)) ||
                parseFloat(leaseholdLandData.total_price) <= 0
              )
                errors.push(
                  "Leasehold Land: Total price must be a valid positive number",
                );
              if (leaseholdLandData.lease_duration <= 0)
                errors.push(
                  "Leasehold Land: Lease duration must be greater than 0",
                );
              if (!leaseholdLandData.land_size.trim())
                errors.push("Leasehold Land: Land size is required");
              else if (
                isNaN(parseFloat(leaseholdLandData.land_size)) ||
                parseFloat(leaseholdLandData.land_size) <= 0
              )
                errors.push(
                  "Leasehold Land: Land size must be a valid positive number",
                );
              if (!leaseholdLandData.price_per_are.trim())
                errors.push("Leasehold Land: Price per are is required");
              else if (
                isNaN(parseFloat(leaseholdLandData.price_per_are)) ||
                parseFloat(leaseholdLandData.price_per_are) <= 0
              )
                errors.push(
                  "Leasehold Land: Price per are must be a valid positive number",
                );
              if (!leaseholdLandData.land_zoning.trim())
                errors.push("Leasehold Land: Land zoning is required");
              if (!leaseholdLandData.view.trim())
                errors.push("Leasehold Land: View description is required");
              break;
            case "freehold_commercial":
              const freeholdCommercialValidationData =
                typeFormData.freehold_commercial || {};
              // bedrooms is optional, validate only if provided
              if (
                freeholdCommercialValidationData.bedrooms?.toString().trim() &&
                (isNaN(
                  parseInt(
                    freeholdCommercialValidationData.bedrooms?.toString() ||
                      "0",
                  ),
                ) ||
                  parseInt(
                    freeholdCommercialValidationData.bedrooms?.toString() ||
                      "0",
                  ) <= 0)
              )
                errors.push(
                  "Freehold Commercial: Number of bedrooms must be greater than 0 if provided",
                );
              if (
                !freeholdCommercialValidationData.bathrooms?.toString().trim()
              )
                errors.push(
                  "Freehold Commercial: Number of bathrooms is required",
                );
              else if (
                isNaN(
                  parseInt(
                    freeholdCommercialValidationData.bathrooms?.toString() ||
                      "0",
                  ),
                ) ||
                parseInt(
                  freeholdCommercialValidationData.bathrooms?.toString() || "0",
                ) <= 0
              )
                errors.push(
                  "Freehold Commercial: Number of bathrooms must be greater than 0",
                );
              if (
                !freeholdCommercialValidationData.land_size?.toString().trim()
              )
                errors.push("Freehold Commercial: Land size is required");
              else if (
                isNaN(
                  parseFloat(
                    freeholdCommercialValidationData.land_size?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  freeholdCommercialValidationData.land_size?.toString() || "0",
                ) <= 0
              )
                errors.push(
                  "Freehold Commercial: Land size must be a valid positive number",
                );
              if (
                !freeholdCommercialValidationData.build_size?.toString().trim()
              )
                errors.push("Freehold Commercial: Build size is required");
              else if (
                isNaN(
                  parseFloat(
                    freeholdCommercialValidationData.build_size?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  freeholdCommercialValidationData.build_size?.toString() ||
                    "0",
                ) <= 0
              )
                errors.push(
                  "Freehold Commercial: Build size must be a valid positive number",
                );
              if (!freeholdCommercialValidationData.title?.toString().trim())
                errors.push("Freehold Commercial: Title is required");
              if (
                !freeholdCommercialValidationData.building_permit
                  ?.toString()
                  .trim()
              )
                errors.push("Freehold Commercial: Building permit is required");
              if (!freeholdCommercialValidationData.frontage?.toString().trim())
                errors.push("Freehold Commercial: Frontage is required");
              else if (
                isNaN(
                  parseFloat(
                    freeholdCommercialValidationData.frontage?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  freeholdCommercialValidationData.frontage?.toString() || "0",
                ) <= 0
              )
                errors.push(
                  "Freehold Commercial: Frontage must be a valid positive number",
                );
              if (
                !freeholdCommercialValidationData.orientation?.toString().trim()
              )
                errors.push("Freehold Commercial: Orientation is required");
              if (
                !freeholdCommercialValidationData.accessibility
                  ?.toString()
                  .trim()
              )
                errors.push("Freehold Commercial: Accessibility is required");
              if (
                (freeholdCommercialValidationData.year_built || 0) <= 1800 ||
                (freeholdCommercialValidationData.year_built || 0) >
                  new Date().getFullYear() + 5
              )
                errors.push(
                  "Freehold Commercial: Year built must be between 1800 and " +
                    (new Date().getFullYear() + 5),
                );
              if (
                !freeholdCommercialValidationData.bank_partnership
                  ?.toString()
                  .trim()
              )
                errors.push(
                  "Freehold Commercial: Bank partnership is required",
                );
              if (
                !freeholdCommercialValidationData.down_payment
                  ?.toString()
                  .trim()
              )
                errors.push("Freehold Commercial: Down payment is required");
              else if (
                isNaN(
                  parseFloat(
                    freeholdCommercialValidationData.down_payment?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  freeholdCommercialValidationData.down_payment?.toString() ||
                    "0",
                ) < 0
              )
                errors.push(
                  "Freehold Commercial: Down payment must be a valid non-negative number",
                );
              if (
                !freeholdCommercialValidationData.total_price?.toString().trim()
              )
                errors.push("Freehold Commercial: Total price is required");
              else if (
                isNaN(
                  parseFloat(
                    freeholdCommercialValidationData.total_price?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  freeholdCommercialValidationData.total_price?.toString() ||
                    "0",
                ) <= 0
              )
                errors.push(
                  "Freehold Commercial: Total price must be a valid positive number",
                );
              break;
            case "leasehold_commercial":
              const leaseholdCommercialValidationData =
                typeFormData.leasehold_commercial || {};
              // bedrooms is optional, validate only if provided
              if (
                leaseholdCommercialValidationData.bedrooms?.toString().trim() &&
                (isNaN(
                  parseInt(
                    leaseholdCommercialValidationData.bedrooms?.toString() ||
                      "0",
                  ),
                ) ||
                  parseInt(
                    leaseholdCommercialValidationData.bedrooms?.toString() ||
                      "0",
                  ) <= 0)
              )
                errors.push(
                  "Leasehold Commercial: Number of bedrooms must be greater than 0 if provided",
                );
              if (
                !leaseholdCommercialValidationData.bathrooms?.toString().trim()
              )
                errors.push(
                  "Leasehold Commercial: Number of bathrooms is required",
                );
              else if (
                isNaN(
                  parseInt(
                    leaseholdCommercialValidationData.bathrooms?.toString() ||
                      "0",
                  ),
                ) ||
                parseInt(
                  leaseholdCommercialValidationData.bathrooms?.toString() ||
                    "0",
                ) <= 0
              )
                errors.push(
                  "Leasehold Commercial: Number of bathrooms must be greater than 0",
                );
              if (
                !leaseholdCommercialValidationData.land_size?.toString().trim()
              )
                errors.push("Leasehold Commercial: Land size is required");
              else if (
                isNaN(
                  parseFloat(
                    leaseholdCommercialValidationData.land_size?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  leaseholdCommercialValidationData.land_size?.toString() ||
                    "0",
                ) <= 0
              )
                errors.push(
                  "Leasehold Commercial: Land size must be a valid positive number",
                );
              if (
                !leaseholdCommercialValidationData.build_size?.toString().trim()
              )
                errors.push("Leasehold Commercial: Build size is required");
              else if (
                isNaN(
                  parseFloat(
                    leaseholdCommercialValidationData.build_size?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  leaseholdCommercialValidationData.build_size?.toString() ||
                    "0",
                ) <= 0
              )
                errors.push(
                  "Leasehold Commercial: Build size must be a valid positive number",
                );
              if (!leaseholdCommercialValidationData.title?.toString().trim())
                errors.push("Leasehold Commercial: Title is required");
              if (
                !leaseholdCommercialValidationData.building_permit
                  ?.toString()
                  .trim()
              )
                errors.push(
                  "Leasehold Commercial: Building permit is required",
                );
              if (
                !leaseholdCommercialValidationData.frontage?.toString().trim()
              )
                errors.push("Leasehold Commercial: Frontage is required");
              else if (
                isNaN(
                  parseFloat(
                    leaseholdCommercialValidationData.frontage?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  leaseholdCommercialValidationData.frontage?.toString() || "0",
                ) <= 0
              )
                errors.push(
                  "Leasehold Commercial: Frontage must be a valid positive number",
                );
              if (
                !leaseholdCommercialValidationData.orientation
                  ?.toString()
                  .trim()
              )
                errors.push("Leasehold Commercial: Orientation is required");
              if (
                !leaseholdCommercialValidationData.accessibility
                  ?.toString()
                  .trim()
              )
                errors.push("Leasehold Commercial: Accessibility is required");
              if (
                (leaseholdCommercialValidationData.year_built || 0) <= 1800 ||
                (leaseholdCommercialValidationData.year_built || 0) >
                  new Date().getFullYear() + 5
              )
                errors.push(
                  "Leasehold Commercial: Year built must be between 1800 and " +
                    (new Date().getFullYear() + 5),
                );
              if (
                !leaseholdCommercialValidationData.price_for_duration
                  ?.toString()
                  .trim()
              )
                errors.push(
                  "Leasehold Commercial: Price for duration is required",
                );
              else if (
                isNaN(
                  parseFloat(
                    leaseholdCommercialValidationData.price_for_duration?.toString() ||
                      "0",
                  ),
                ) ||
                parseFloat(
                  leaseholdCommercialValidationData.price_for_duration?.toString() ||
                    "0",
                ) <= 0
              )
                errors.push(
                  "Leasehold Commercial: Price for duration must be a valid positive number",
                );
              if (leaseholdCommercialValidationData.leasehold_duration <= 0)
                errors.push(
                  "Leasehold Commercial: Leasehold duration must be greater than 0",
                );
              break;
            // freehold_commercial validation is already handled above
          }
        });
        break;
    }

    return errors;
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        // validate required fields for base_properties
        // check for reference ID conflicts (only against non-deleted properties)
        const reffIdConflict =
          reffIdExists &&
          reffIdExists.exists &&
          basePropertyData.reff_id.trim() &&
          (reffIdExists.count > 1 ||
            (reffIdExists.count === 1 &&
              (!existingProperty ||
                existingProperty.baseProperty.reff_id !==
                  basePropertyData.reff_id)));

        // check for property name conflicts (exclude current property from duplicate check)
        const propertyNameConflict =
          propertyNameExists &&
          propertyNameExists.exists &&
          basePropertyData.property_name.trim() &&
          (propertyNameExists.count > 1 ||
            (propertyNameExists.count === 1 &&
              (!existingProperty ||
                existingProperty.baseProperty.property_name !==
                  basePropertyData.property_name)));

        // check for slug conflicts (only against non-deleted properties)
        const slugConflict =
          slugExists &&
          slugExists.exists &&
          basePropertyData.slug.trim() &&
          (slugExists.count > 1 ||
            (slugExists.count === 1 &&
              (!existingProperty ||
                existingProperty.baseProperty.slug !== basePropertyData.slug)));

        return (
          basePropertyData.property_name.trim() !== "" &&
          !propertyNameConflict &&
          basePropertyData.property_name_idn?.trim() !== "" &&
          stripHtml(basePropertyData.description).trim() !== "" &&
          stripHtml(basePropertyData.description_idn || "").trim() !== "" &&
          basePropertyData.reff_id.trim() !== "" &&
          !reffIdConflict &&
          basePropertyData.latitude.trim() !== "" &&
          basePropertyData.longitude.trim() !== "" &&
          basePropertyData.map_url.trim() !== "" &&
          basePropertyData.area_id !== undefined &&
          basePropertyData.slug.trim() !== "" &&
          !slugConflict
        );
      case 2:
        return selectedPropertyTypes.length > 0;
      case 3:
        if (selectedPropertyTypes.length === 0) return false;

        // validate each selected type's data
        return selectedPropertyTypes.every((type) => {
          const data = typeFormData[type];
          if (!data) return false;

          switch (type) {
            case "rentals":
              return (
                data.bedrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bedrooms?.toString() || "0")) &&
                parseInt(data.bedrooms?.toString() || "0") > 0 &&
                data.bathrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bathrooms?.toString() || "0")) &&
                parseInt(data.bathrooms?.toString() || "0") > 0 &&
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                data.build_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.build_size?.toString() || "0")) &&
                parseFloat(data.build_size?.toString() || "0") > 0 &&
                data.price_per_year?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.price_per_year?.toString() || "0")) &&
                parseFloat(data.price_per_year?.toString() || "0") > 0 &&
                (data.minimum_duration || 0) > 0 &&
                data.safety_deposit_amount?.toString().trim() !== "" &&
                !isNaN(
                  parseFloat(data.safety_deposit_amount?.toString() || "0"),
                ) &&
                parseFloat(data.safety_deposit_amount?.toString() || "0") > 0 &&
                data.pet_allowed?.toString().trim() !== ""
              );
            case "freehold_properties":
              return (
                data.bedrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bedrooms?.toString() || "0")) &&
                parseInt(data.bedrooms?.toString() || "0") > 0 &&
                data.bathrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bathrooms?.toString() || "0")) &&
                parseInt(data.bathrooms?.toString() || "0") > 0 &&
                data.build_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.build_size?.toString() || "0")) &&
                parseFloat(data.build_size?.toString() || "0") > 0 &&
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                (data.year_built || 0) > 1800 &&
                (data.year_built || 0) <= new Date().getFullYear() &&
                data.total_price?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.total_price?.toString() || "0")) &&
                parseFloat(data.total_price?.toString() || "0") > 0 &&
                data.dp_minimal?.toString().trim() !== "" &&
                data.pks_bank?.toString().trim() !== ""
              );
            case "freehold_villas":
              return (
                data.bedrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bedrooms?.toString() || "0")) &&
                parseInt(data.bedrooms?.toString() || "0") > 0 &&
                data.bathrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bathrooms?.toString() || "0")) &&
                parseInt(data.bathrooms?.toString() || "0") > 0 &&
                data.build_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.build_size?.toString() || "0")) &&
                parseFloat(data.build_size?.toString() || "0") > 0 &&
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                (data.year_built || 0) > 1800 &&
                (data.year_built || 0) <= new Date().getFullYear() &&
                data.total_price?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.total_price?.toString() || "0")) &&
                parseFloat(data.total_price?.toString() || "0") > 0 &&
                data.dp_minimal?.toString().trim() !== "" &&
                data.pks_bank?.toString().trim() !== ""
              );
            case "leasehold_villas":
              return (
                data.bedrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bedrooms?.toString() || "0")) &&
                parseInt(data.bedrooms?.toString() || "0") > 0 &&
                data.bathrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bathrooms?.toString() || "0")) &&
                parseInt(data.bathrooms?.toString() || "0") > 0 &&
                data.build_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.build_size?.toString() || "0")) &&
                parseFloat(data.build_size?.toString() || "0") > 0 &&
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                (data.year_built || 0) > 1800 &&
                (data.year_built || 0) <= new Date().getFullYear() &&
                data.price_for_duration?.toString().trim() !== "" &&
                !isNaN(
                  parseFloat(data.price_for_duration?.toString() || "0"),
                ) &&
                parseFloat(data.price_for_duration?.toString() || "0") > 0 &&
                (data.leasehold_duration || 0) > 0
              );
            case "freehold_lands":
              return (
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                data.price_per_are?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.price_per_are?.toString() || "0")) &&
                parseFloat(data.price_per_are?.toString() || "0") > 0 &&
                data.total_price?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.total_price?.toString() || "0")) &&
                parseFloat(data.total_price?.toString() || "0") > 0 &&
                data.road_access_width?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.road_access_width?.toString() || "0")) &&
                parseFloat(data.road_access_width?.toString() || "0") > 0 &&
                data.land_zoning?.toString().trim() !== "" &&
                data.view?.toString().trim() !== ""
              );
            case "leasehold_lands":
              return (
                data.total_price?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.total_price?.toString() || "0")) &&
                parseFloat(data.total_price?.toString() || "0") > 0 &&
                (data.lease_duration || 0) > 0 &&
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                data.price_per_are?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.price_per_are?.toString() || "0")) &&
                parseFloat(data.price_per_are?.toString() || "0") > 0 &&
                data.land_zoning?.toString().trim() !== "" &&
                data.view?.toString().trim() !== ""
              );
            case "freehold_commercial":
              return (
                // bedrooms is optional, validate only if provided
                (data.bedrooms === "" ||
                  data.bedrooms === undefined ||
                  data.bedrooms === null ||
                  (!isNaN(parseInt(data.bedrooms?.toString() || "0")) &&
                    parseInt(data.bedrooms?.toString() || "0") > 0)) &&
                data.bathrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bathrooms?.toString() || "0")) &&
                parseInt(data.bathrooms?.toString() || "0") > 0 &&
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                data.build_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.build_size?.toString() || "0")) &&
                parseFloat(data.build_size?.toString() || "0") > 0 &&
                data.title?.toString().trim() !== "" &&
                data.building_permit?.toString().trim() !== "" &&
                data.frontage?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.frontage?.toString() || "0")) &&
                parseFloat(data.frontage?.toString() || "0") > 0 &&
                data.orientation?.toString().trim() !== "" &&
                data.accessibility?.toString().trim() !== "" &&
                (data.year_built || 0) > 1800 &&
                (data.year_built || 0) <= new Date().getFullYear() + 5 &&
                data.bank_partnership?.toString().trim() !== "" &&
                data.down_payment?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.down_payment?.toString() || "0")) &&
                parseFloat(data.down_payment?.toString() || "0") >= 0 &&
                data.total_price?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.total_price?.toString() || "0")) &&
                parseFloat(data.total_price?.toString() || "0") > 0
              );
            case "leasehold_commercial":
              return (
                // bedrooms is optional, validate only if provided
                (data.bedrooms === "" ||
                  data.bedrooms === undefined ||
                  data.bedrooms === null ||
                  (!isNaN(parseInt(data.bedrooms?.toString() || "0")) &&
                    parseInt(data.bedrooms?.toString() || "0") > 0)) &&
                data.bathrooms?.toString().trim() !== "" &&
                !isNaN(parseInt(data.bathrooms?.toString() || "0")) &&
                parseInt(data.bathrooms?.toString() || "0") > 0 &&
                data.land_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.land_size?.toString() || "0")) &&
                parseFloat(data.land_size?.toString() || "0") > 0 &&
                data.build_size?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.build_size?.toString() || "0")) &&
                parseFloat(data.build_size?.toString() || "0") > 0 &&
                data.title?.toString().trim() !== "" &&
                data.building_permit?.toString().trim() !== "" &&
                data.frontage?.toString().trim() !== "" &&
                !isNaN(parseFloat(data.frontage?.toString() || "0")) &&
                parseFloat(data.frontage?.toString() || "0") > 0 &&
                data.orientation?.toString().trim() !== "" &&
                data.accessibility?.toString().trim() !== "" &&
                (data.year_built || 0) > 1800 &&
                (data.year_built || 0) <= new Date().getFullYear() + 5 &&
                data.price_for_duration?.toString().trim() !== "" &&
                !isNaN(
                  parseFloat(data.price_for_duration?.toString() || "0"),
                ) &&
                parseFloat(data.price_for_duration?.toString() || "0") > 0 &&
                data.leasehold_duration > 0
              );
            // legacy "commercial" type is normalized to "freehold_commercial" before reaching here
            // validation for freehold_commercial is already handled above
            default:
              return false;
          }
        });
      case 4:
        return true; // facilities are optional
      case 5:
        return true; // images are optional
      case 6:
        return true;
      case 7:
        if (!addContact) return true;
        const phoneOk =
          /^\d+$/.test(contactData.phone) &&
          contactData.phone.trim().length > 0 &&
          !fieldErrors["contact_phone"];
        const countryOk =
          contactData.country.trim().length > 0 &&
          !fieldErrors["contact_country"];
        return phoneOk && countryOk;
      default:
        return false;
    }
  };

  const validateFormatsOnly = (): string[] => {
    const validationErrors: string[] = [];

    if (
      addContact &&
      contactData.phone &&
      !/^\d+$/.test(contactData.phone.trim())
    ) {
      validationErrors.push("Phone number must contain only numbers");
    }

    if (addContact && contactData.email && contactData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactData.email.trim())) {
        validationErrors.push("Email format is invalid");
      }
    }

    selectedPropertyTypes.forEach((type) => {
      const typeForm = typeForms[type as keyof typeof typeForms];

      if (typeForm) {
        const values = typeForm.getValues();
        Object.keys(values).forEach((key) => {
          const value = (values as any)[key];
          if (value === undefined || value === null || value === "") {
            return;
          }

          if (typeof value === "number") {
            return;
          }

          if (typeof value === "string" && value.trim() === "") {
            return;
          }

          const numericFields = [
            "price",
            "size",
            "duration",
            "bedroom",
            "bathroom",
            "frontage",
            "year",
            "deposit",
            "payment",
            "power",
            "rooms",
            "parking",
            "are",
          ];

          if (
            numericFields.some((field) => key.toLowerCase().includes(field))
          ) {
            const trimmedValue = String(value).trim();
            if (trimmedValue && isNaN(parseFloat(trimmedValue))) {
              validationErrors.push(`${type}: ${key} must be a valid number`);
            }
          }
        });
      }
    });

    return validationErrors;
  };

  const handleSaveDraft = async () => {
    if (
      !basePropertyData.property_name ||
      basePropertyData.property_name.trim().length < 1
    ) {
      toast.error("Please enter at least a property name to save as draft");
      return;
    }

    const validationErrors = validateFormatsOnly();

    if (validationErrors.length > 0) {
      toast.error(`Validation errors:\n${validationErrors.join("\n")}`);
      return;
    }

    const draftData = {
      base: basePropertyData,
      types: {
        selected: selectedPropertyTypes,
        data: typeFormData,
      },
      facilities: facilitiesData,
      contact: contactData,
      step: currentStep,
    };

    setIsManuallySaving(true);
    const loadingToastId = toast.loading("Saving draft...");

    try {
      const id = await saveDraft({
        id: draftId || undefined,
        property_name: basePropertyData.property_name,
        data: JSON.stringify(draftData),
        step: currentStep,
      });
      setDraftId(id);
      setLastSaved(new Date());
      toast.success("Draft saved successfully", { id: loadingToastId });
    } catch (error) {
      console.error("Save draft failed:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to save draft. Please try again.";
      toast.error(errorMessage, { id: loadingToastId });
    } finally {
      setIsManuallySaving(false);
    }
  };

  const handleSave = async () => {
    if (!propertyId || mode !== "edit") {
      return;
    }

    const validationErrors = validateFormatsOnly();

    if (validationErrors.length > 0) {
      toast.error(`Validation errors:\n${validationErrors.join("\n")}`);
      return;
    }

    setIsSubmitting(true);
    const loadingToastId = toast.loading("Saving changes...");

    try {
      const propertyTypes = (await Promise.all(
        selectedPropertyTypes.map(async (type) => {
          const formData =
            typeForms[type as keyof typeof typeForms]?.getValues() || {};
          if (
            type === "freehold_commercial" ||
            type === "leasehold_commercial"
          ) {
            const commercialData = formData as Record<string, unknown>;
            if (
              commercialData.bedrooms === "" ||
              commercialData.bedrooms === undefined ||
              commercialData.bedrooms === null
            ) {
              const { bedrooms, ...rest } = commercialData;
              return { type, data: rest };
            }
          }
          // legacy "commercial" type is normalized to "freehold_commercial" before reaching here
          return { type, data: formData };
        }),
      )) as any;

      const needsFacilities = selectedPropertyTypes.some((type) => {
        const pt = PROPERTY_TYPES.find((t) => t.value === type);
        return pt?.hasFacilities;
      });

      const updateData: any = {
        id: propertyId,
        updated_by: currentUser?.email || "system",
      };

      if (basePropertyData.property_name) {
        updateData.property_name = basePropertyData.property_name;
      }
      if (basePropertyData.property_name_idn !== undefined) {
        updateData.property_name_idn =
          basePropertyData.property_name_idn || null;
      }
      if (basePropertyData.description) {
        updateData.description = basePropertyData.description;
      }
      if (basePropertyData.description_idn) {
        updateData.description_idn = basePropertyData.description_idn;
      }
      if (basePropertyData.subtitle !== undefined) {
        updateData.subtitle = basePropertyData.subtitle || undefined;
      }
      if (basePropertyData.subtitle_idn !== undefined) {
        updateData.subtitle_idn = basePropertyData.subtitle_idn || undefined;
      }
      if (basePropertyData.reff_id) {
        updateData.reff_id = basePropertyData.reff_id;
      }
      if (basePropertyData.latitude) {
        updateData.latitude = parseFloat(basePropertyData.latitude);
      }
      if (basePropertyData.longitude) {
        updateData.longitude = parseFloat(basePropertyData.longitude);
      }
      if (basePropertyData.map_url) {
        updateData.map_url = basePropertyData.map_url;
      }
      if (basePropertyData.area_id) {
        updateData.area_id = basePropertyData.area_id;
      }
      if (basePropertyData.slug) {
        updateData.slug = basePropertyData.slug;
      }
      if (basePropertyData.social_media_link !== undefined) {
        updateData.social_media_link =
          basePropertyData.social_media_link || undefined;
      }
      if (basePropertyData.shm_condition) {
        updateData.shm_condition = basePropertyData.shm_condition;
      }
      if (basePropertyData.slf_pbg_condition) {
        updateData.slf_pbg_condition = basePropertyData.slf_pbg_condition;
      }
      if (basePropertyData.zoning) {
        updateData.zoning = basePropertyData.zoning;
      }
      if (basePropertyData.featured !== undefined) {
        updateData.featured = basePropertyData.featured;
      }
      if (basePropertyData.draft !== undefined) {
        updateData.draft = basePropertyData.draft;
      }
      if (basePropertyData.hidden !== undefined) {
        updateData.hidden = basePropertyData.hidden;
      }
      if (basePropertyData.price_hidden !== undefined) {
        updateData.price_hidden = basePropertyData.price_hidden;
      }
      if (basePropertyData.tags) {
        updateData.tags = basePropertyData.tags as Id<"tag">[];
      }
      if (basePropertyData.images) {
        updateData.images = basePropertyData.images;
      }
      // handle thumbnail_video: if it was cleared (undefined but originally existed), pass null to clear
      // if it has a value, pass the value; if undefined and never existed, don't include it
      if (basePropertyData.thumbnail_video !== undefined) {
        updateData.thumbnail_video = basePropertyData.thumbnail_video || null;
      } else if (
        originalThumbnailVideoRef.current !== null &&
        originalThumbnailVideoRef.current !== undefined
      ) {
        // was cleared (undefined) but originally existed, so clear it
        updateData.thumbnail_video = null;
      }
      if (propertyTypes.length > 0) {
        updateData.property_types = propertyTypes;
      }
      if (needsFacilities) {
        updateData.facilities = facilitiesData;
      }

      await updateProperty(updateData);

      if (addContact && contactData.phone && contactData.country) {
        await createContact({
          name: contactData.name || undefined,
          phone: parseInt(contactData.phone),
          email: contactData.email || undefined,
          address: contactData.address || undefined,
          country: contactData.country,
          reff_id: basePropertyData.reff_id,
          qualification: (contactData.qualification as any) || undefined,
          group_id: (contactData.group_id as any) || undefined,
          type_id: (contactData.type_id as any) || undefined,
          note: contactData.note || undefined,
          created_by: currentUser?.email || "system",
        } as any);
      }

      toast.success("Changes saved successfully", { id: loadingToastId });
    } catch (error) {
      console.error("Save failed:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to save changes. Please try again.";
      toast.error(errorMessage, { id: loadingToastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (basePropertyData.property_name.trim() && !propertyNameDebounce) {
      setPropertyNameDebounce(basePropertyData.property_name);
    }

    const onSubmit = mainForm.handleSubmit(async (data) => {
      setIsSubmitting(true);
      const loadingToastId = toast.loading(
        `${mode === "edit" ? "Updating" : "Creating"} property...`,
      );
      try {
        const propertyTypes = (await Promise.all(
          selectedPropertyTypes.map(async (type) => {
            const formData =
              typeForms[type as keyof typeof typeForms]?.getValues() || {};
            // for commercial properties, remove bedrooms if empty
            if (
              type === "freehold_commercial" ||
              type === "leasehold_commercial"
            ) {
              const commercialData = formData as Record<string, unknown>;
              if (
                commercialData.bedrooms === "" ||
                commercialData.bedrooms === undefined ||
                commercialData.bedrooms === null
              ) {
                const { bedrooms, ...rest } = commercialData;
                return { type, data: rest };
              }
            }
            // legacy "commercial" type is normalized to "freehold_commercial" before reaching here
            return { type, data: formData };
          }),
        )) as any;

        const needsFacilities = selectedPropertyTypes.some((type) => {
          const pt = PROPERTY_TYPES.find((t) => t.value === type);
          return pt?.hasFacilities;
        });

        if (mode === "edit" && propertyId) {
          const { social_media_link, ...baseDataWithoutSocialMedia } =
            data.base;
          await updateProperty({
            id: propertyId,
            ...baseDataWithoutSocialMedia,
            property_name_idn: data.base.property_name_idn || null,
            latitude: parseFloat(data.base.latitude),
            longitude: parseFloat(data.base.longitude),
            area_id:
              (data.base as any).area_id ||
              ("j578fhw0r2w1f4ysym6h1kfdys7mbzgj" as Id<"area">),
            property_types: propertyTypes,
            facilities: needsFacilities ? (data.facilities as any) : undefined,
            updated_by: currentUser?.email || "system",
            tags: (basePropertyData.tags as Id<"tag">[]) || [],
            subtitle: basePropertyData.subtitle || undefined,
            subtitle_idn: basePropertyData.subtitle_idn || undefined,
            thumbnail_video: basePropertyData.thumbnail_video || undefined,
            social_media_link:
              social_media_link && social_media_link.trim()
                ? social_media_link.trim()
                : undefined,
          });
          if (addContact && data.contact) {
            await createContact({
              name: data.contact.name || undefined,
              phone: parseInt(data.contact.phone),
              email: data.contact.email || undefined,
              address: data.contact.address || undefined,
              country: data.contact.country,
              reff_id: data.base.reff_id,
              qualification: (contactData.qualification as any) || undefined,
              group_id: (contactData.group_id as any) || undefined,
              type_id: (contactData.type_id as any) || undefined,
              note: contactData.note || undefined,
              created_by: currentUser?.email || "system",
            } as any);
          }
        } else {
          const { social_media_link, ...baseDataWithoutSocialMedia } =
            data.base;
          await createProperty({
            ...baseDataWithoutSocialMedia,
            property_name_idn: data.base.property_name_idn || null,
            latitude: parseFloat(data.base.latitude),
            longitude: parseFloat(data.base.longitude),
            area_id:
              (data.base as any).area_id ||
              ("j578fhw0r2w1f4ysym6h1kfdys7mbzgj" as Id<"area">),
            property_types: propertyTypes,
            facilities: needsFacilities ? (data.facilities as any) : undefined,
            created_by: currentUser?.email || "system",
            tags: (basePropertyData.tags as Id<"tag">[]) || [],
            subtitle: basePropertyData.subtitle || undefined,
            subtitle_idn: basePropertyData.subtitle_idn || undefined,
            thumbnail_video: basePropertyData.thumbnail_video || undefined,
            social_media_link:
              social_media_link && social_media_link.trim()
                ? social_media_link.trim()
                : undefined,
          });
          if (addContact && data.contact) {
            await createContact({
              name: data.contact.name || undefined,
              phone: parseInt(data.contact.phone),
              email: data.contact.email || undefined,
              address: data.contact.address || undefined,
              country: data.contact.country,
              reff_id: data.base.reff_id,
              qualification: (contactData.qualification as any) || undefined,
              group_id: (contactData.group_id as any) || undefined,
              type_id: (contactData.type_id as any) || undefined,
              note: contactData.note || undefined,
              created_by: currentUser?.email || "system",
            } as any);
          }
        }

        if (draftId) {
          await deleteDraft({ id: draftId });
          setDraftId(null);
        }

        handleClose(true);
        toast.success(
          `Property ${mode === "edit" ? "updated" : "created"} successfully!`,
          { id: loadingToastId },
        );
      } catch (error) {
        console.error(
          `Error ${mode === "edit" ? "updating" : "creating"} property:`,
          error,
        );
        const errorMessage =
          error instanceof Error
            ? error.message
            : `Failed to ${mode === "edit" ? "update" : "create"} property. Please try again.`;
        toast.error(errorMessage, { id: loadingToastId });
      } finally {
        setIsSubmitting(false);
      }
    });

    await onSubmit();
  };

  const handleClose = (success = false) => {
    // only show cancellation message if not closing after successful save
    if (!success) {
      // check if there are unsaved changes
      const hasChanges =
        basePropertyData.property_name.trim() !== "" ||
        stripHtml(basePropertyData.description).trim() !== "" ||
        selectedPropertyType !== null;

      if (hasChanges && mode === "create") {
        if (draftId) {
          toast.info("Draft saved. You can resume editing later.");
        } else {
          toast.info("Property creation cancelled. Changes were not saved.");
        }
      }
    }

    if (success) {
      setDraftId(null);
    }

    hasInitialized.current = false;
    setCurrentStep(1);
    setSelectedPropertyType(null);
    onClose();
  };

  const handleCloseClick = () => {
    handleClose(false);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="property_name">Property Name *</Label>
                <Input
                  id="property_name"
                  value={basePropertyData.property_name}
                  onChange={(e) => {
                    const newValue = e.target.value;

                    // if user changes property name, allow auto-slug generation
                    if (basePropertyData.property_name !== newValue) {
                      slugManuallyChanged.current = false;
                    }

                    setBasePropertyData((prev) => ({
                      ...prev,
                      property_name: newValue,
                    }));
                  }}
                  placeholder="Enter property name"
                  className={`${
                    propertyNameExists &&
                    propertyNameExists.exists &&
                    basePropertyData.property_name.trim() &&
                    (propertyNameExists.count > 1 ||
                      (propertyNameExists.count === 1 &&
                        (!existingProperty ||
                          existingProperty.baseProperty.property_name !==
                            basePropertyData.property_name)))
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : propertyNameExists &&
                          !propertyNameExists.exists &&
                          basePropertyData.property_name.trim()
                        ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                        : ""
                  }`}
                />
                {propertyNameExists &&
                  propertyNameExists.exists &&
                  basePropertyData.property_name.trim() &&
                  (propertyNameExists.count > 1 ||
                    (propertyNameExists.count === 1 &&
                      (!existingProperty ||
                        existingProperty.baseProperty.property_name !==
                          basePropertyData.property_name))) && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-red-600">
                        Property name already exists
                        {propertyNameExists.property && (
                          <span className="text-red-500">
                            {" "}
                            (Property: {propertyNameExists.property.reff_id})
                          </span>
                        )}
                        {propertyNameExists.count > 1 && (
                          <span className="text-red-500">
                            {" "}
                            (+{propertyNameExists.count - 1} more)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                {propertyNameDebounce &&
                  basePropertyData.property_name.trim() &&
                  !propertyNameExists && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      <span className="text-blue-600">
                        Checking property name...
                      </span>
                    </div>
                  )}
                {propertyNameExists &&
                  !propertyNameExists.exists &&
                  basePropertyData.property_name.trim() && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-green-600">
                        Property name is available
                      </span>
                    </div>
                  )}
                {propertyNameExists &&
                  propertyNameExists.exists &&
                  basePropertyData.property_name.trim() &&
                  (propertyNameExists.count > 1 ||
                    (propertyNameExists.count === 1 &&
                      (!existingProperty ||
                        existingProperty.baseProperty.property_name !==
                          basePropertyData.property_name))) && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-red-600">
                        Property name already exists. Suggested alternatives:
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(() => {
                            const baseName = basePropertyData.property_name;
                            const suggestions = [];
                            for (let i = 1; i <= 3; i++) {
                              suggestions.push(
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => {
                                    // if user selects a suggestion, allow auto-slug generation
                                    slugManuallyChanged.current = false;
                                    setBasePropertyData((prev) => ({
                                      ...prev,
                                      property_name: `${baseName} ${i}`,
                                    }));
                                  }}
                                  className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded border border-red-200 transition-colors active:scale-95"
                                >
                                  {`${baseName} ${i}`}
                                </button>,
                              );
                            }
                            return suggestions;
                          })()}
                        </div>
                      </span>
                    </div>
                  )}
              </div>
              <div>
                <Label htmlFor="property_name_idn">
                  Property Name (Indonesian) *
                </Label>
                <Input
                  id="property_name_idn"
                  value={basePropertyData.property_name_idn || ""}
                  onChange={(e) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      property_name_idn: e.target.value,
                    }))
                  }
                  placeholder="Enter Indonesian property name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="description">Description *</Label>
                <div id="description_field">
                  <TiptapEditor
                    content={basePropertyData.description}
                    onChange={(content) =>
                      setBasePropertyData((prev) => ({
                        ...prev,
                        description: content,
                      }))
                    }
                    placeholder="Enter property description"
                    minHeight="300px"
                    className="md:min-h-[400px]"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description_idn">
                  Description (Indonesian) *
                </Label>
                <div id="description_idn_field">
                  <TiptapEditor
                    content={basePropertyData.description_idn || ""}
                    onChange={(content) =>
                      setBasePropertyData((prev) => ({
                        ...prev,
                        description_idn: content,
                      }))
                    }
                    placeholder="Enter Indonesian description"
                    minHeight="300px"
                    className="md:min-h-[400px]"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="subtitle">Subtitle (English)</Label>
                <div className="flex gap-2">
                  <Input
                    id="subtitle"
                    value={basePropertyData.subtitle || ""}
                    onChange={(e) =>
                      setBasePropertyData((prev) => ({
                        ...prev,
                        subtitle: e.target.value,
                      }))
                    }
                    placeholder="Enter subtitle or generate with AI"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      if (!propertyId && mode === "create") {
                        toast.error(
                          "Please save the property first before generating subtitle",
                        );
                        return;
                      }
                      setIsGeneratingEnglishSubtitle(true);
                      try {
                        console.log(
                          "Generating subtitle for property:",
                          propertyId || existingProperty?.baseProperty._id,
                        );
                        const result = await generateSubtitle({
                          propertyId:
                            propertyId ||
                            (existingProperty?.baseProperty
                              ._id as Id<"base_properties">),
                        });
                        console.log("Subtitle generation result:", result);
                        if (result && result.success) {
                          console.log(
                            "Updating subtitle state with:",
                            result.subtitle,
                          );
                          setBasePropertyData((prev) => ({
                            ...prev,
                            subtitle: result.subtitle || "",
                          }));
                          // sync to form
                          mainForm.setValue(
                            "base.subtitle",
                            result.subtitle || "",
                            {
                              shouldValidate: false,
                              shouldDirty: true,
                            },
                          );
                          toast.success("Subtitle generated successfully!");
                        } else {
                          console.warn(
                            "Subtitle generation returned non-success:",
                            result,
                          );
                          toast.error(
                            "Failed to generate subtitle: " +
                              (result?.message || "Unknown error"),
                          );
                        }
                      } catch (error) {
                        console.error("Failed to generate subtitle:", error);
                        toast.error(
                          error instanceof Error
                            ? error.message
                            : "Failed to generate subtitle",
                        );
                      } finally {
                        setIsGeneratingEnglishSubtitle(false);
                      }
                    }}
                    disabled={
                      isGeneratingEnglishSubtitle ||
                      isGeneratingIndonesianSubtitle ||
                      (!propertyId && mode === "create")
                    }
                  >
                    {isGeneratingEnglishSubtitle ? "Generating..." : "Generate"}
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="subtitle_idn">Subtitle (Indonesian)</Label>
                <div className="flex gap-2">
                  <Input
                    id="subtitle_idn"
                    value={basePropertyData.subtitle_idn || ""}
                    onChange={(e) =>
                      setBasePropertyData((prev) => ({
                        ...prev,
                        subtitle_idn: e.target.value,
                      }))
                    }
                    placeholder="Enter Indonesian subtitle or generate with AI"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      if (!propertyId && mode === "create") {
                        toast.error(
                          "Please save the property first before generating subtitle",
                        );
                        return;
                      }
                      setIsGeneratingIndonesianSubtitle(true);
                      try {
                        console.log(
                          "Generating Indonesian subtitle for property:",
                          propertyId || existingProperty?.baseProperty._id,
                        );
                        const result = await generateSubtitle({
                          propertyId:
                            propertyId ||
                            (existingProperty?.baseProperty
                              ._id as Id<"base_properties">),
                        });
                        console.log("Subtitle generation result:", result);
                        if (result && result.success) {
                          console.log(
                            "Updating Indonesian subtitle state with:",
                            result.subtitleIdn,
                          );
                          setBasePropertyData((prev) => ({
                            ...prev,
                            subtitle_idn: result.subtitleIdn || "",
                          }));
                          // sync to form
                          mainForm.setValue(
                            "base.subtitle_idn",
                            result.subtitleIdn || "",
                            {
                              shouldValidate: false,
                              shouldDirty: true,
                            },
                          );
                          toast.success(
                            "Indonesian subtitle generated successfully!",
                          );
                        } else {
                          console.warn(
                            "Subtitle generation returned non-success:",
                            result,
                          );
                          toast.error(
                            "Failed to generate subtitle: " +
                              (result?.message || "Unknown error"),
                          );
                        }
                      } catch (error) {
                        console.error("Failed to generate subtitle:", error);
                        toast.error(
                          error instanceof Error
                            ? error.message
                            : "Failed to generate subtitle",
                        );
                      } finally {
                        setIsGeneratingIndonesianSubtitle(false);
                      }
                    }}
                    disabled={
                      isGeneratingEnglishSubtitle ||
                      isGeneratingIndonesianSubtitle ||
                      (!propertyId && mode === "create")
                    }
                  >
                    {isGeneratingIndonesianSubtitle
                      ? "Generating..."
                      : "Generate"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reff_id">Reference ID *</Label>
                <Input
                  id="reff_id"
                  value={basePropertyData.reff_id}
                  onChange={(e) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      reff_id: e.target.value,
                    }))
                  }
                  placeholder="Enter reference ID"
                  className={`${
                    reffIdExists &&
                    reffIdExists.exists &&
                    basePropertyData.reff_id.trim() &&
                    (reffIdExists.count > 1 ||
                      (reffIdExists.count === 1 &&
                        (!existingProperty ||
                          existingProperty.baseProperty.reff_id !==
                            basePropertyData.reff_id)))
                      ? "border-orange-300 focus:border-orange-500 focus:ring-orange-500"
                      : reffIdExists &&
                          !reffIdExists.exists &&
                          basePropertyData.reff_id.trim()
                        ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                        : ""
                  }`}
                />
                {reffIdExists &&
                  reffIdExists.exists &&
                  basePropertyData.reff_id.trim() &&
                  (!existingProperty ||
                    existingProperty.baseProperty.reff_id !==
                      basePropertyData.reff_id) && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                      <span className="text-orange-600">
                        Reference ID already exists
                        {reffIdExists.property && (
                          <span className="text-orange-500">
                            {" "}
                            (Property: {reffIdExists.property.property_name})
                          </span>
                        )}
                        {reffIdExists.count > 1 && (
                          <span className="text-orange-500">
                            {" "}
                            (+{reffIdExists.count - 1} more)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                {reffIdDebounce &&
                  basePropertyData.reff_id.trim() &&
                  !reffIdExists && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      <span className="text-blue-600">
                        Checking reference ID...
                      </span>
                    </div>
                  )}
                {reffIdExists &&
                  !reffIdExists.exists &&
                  basePropertyData.reff_id.trim() && (
                    <div className="mt-1 flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-green-600">
                        Reference ID is available
                      </span>
                    </div>
                  )}
              </div>
              <div>
                <Label htmlFor="area_id">Area *</Label>
                <Select
                  value={basePropertyData.area_id || ""}
                  onValueChange={(value) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      area_id: value as Id<"area">,
                    }))
                  }
                >
                  <SelectTrigger id="area_id">
                    <SelectValue placeholder="Select an area" />
                  </SelectTrigger>
                  <SelectContent>
                    {areas?.map((area) => (
                      <SelectItem key={area._id} value={area._id}>
                        {area.area_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Latitude *</Label>
                <Input
                  id="latitude"
                  value={basePropertyData.latitude}
                  onChange={(e) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      latitude: e.target.value,
                    }))
                  }
                  placeholder="Enter latitude"
                />
              </div>
              <div>
                <Label htmlFor="longitude">Longitude *</Label>
                <Input
                  id="longitude"
                  value={basePropertyData.longitude}
                  onChange={(e) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      longitude: e.target.value,
                    }))
                  }
                  placeholder="Enter longitude"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="map_url">Map URL *</Label>
              <Input
                id="map_url"
                value={basePropertyData.map_url}
                onChange={(e) =>
                  setBasePropertyData((prev) => ({
                    ...prev,
                    map_url: e.target.value,
                  }))
                }
                placeholder="Enter Google Maps URL"
              />
            </div>

            {/* legal and zoning checklist: required before a listing can be posted */}
            <div className="space-y-4 rounded-lg border p-4">
              <div>
                <h4 className="text-sm font-semibold">Legal & zoning checklist</h4>
                <p className="text-xs text-muted-foreground">
                  Every listing needs these filled in before it can be posted.
                </p>
              </div>
              <div>
                <Label htmlFor="shm_condition">Kondisi SHM *</Label>
                <Textarea
                  id="shm_condition"
                  value={basePropertyData.shm_condition || ""}
                  onChange={(e) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      shm_condition: e.target.value,
                    }))
                  }
                  placeholder="SHM No. 123, atas nama Budi, tidak dijadikan agunan"
                  rows={2}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Status, atas nama, agunan, and anything else about the
                  certificate.
                </p>
              </div>
              <div>
                <Label htmlFor="slf_pbg_condition">Kondisi SLF/PBG *</Label>
                <Textarea
                  id="slf_pbg_condition"
                  value={basePropertyData.slf_pbg_condition || ""}
                  onChange={(e) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      slf_pbg_condition: e.target.value,
                    }))
                  }
                  placeholder="PBG terbit 2022, SLF belum ada"
                  rows={2}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Status of the building permit (PBG/IMB) and the SLF.
                </p>
              </div>
              <div>
                <Label htmlFor="zoning">Zonasi *</Label>
                <Input
                  id="zoning"
                  value={basePropertyData.zoning || ""}
                  onChange={(e) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      zoning: e.target.value,
                    }))
                  }
                  placeholder="Zona pariwisata / perumahan / hijau"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="slug">Slug (auto-generated) *</Label>
              <Input
                id="slug"
                value={basePropertyData.slug}
                onChange={(e) => {
                  slugManuallyChanged.current = true;
                  setBasePropertyData((prev) => ({
                    ...prev,
                    slug: toUrlSafeSlug(e.target.value),
                  }));
                }}
                placeholder="property-slug"
                className={`${
                  slugExists &&
                  slugExists.exists &&
                  basePropertyData.slug.trim() &&
                  (!existingProperty ||
                    existingProperty.baseProperty.slug !==
                      basePropertyData.slug)
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : slugExists &&
                        !slugExists.exists &&
                        basePropertyData.slug.trim()
                      ? "border-green-300 focus:border-green-500 focus:ring-green-500"
                      : ""
                }`}
              />
              {slugExists &&
                slugExists.exists &&
                basePropertyData.slug.trim() &&
                (!existingProperty ||
                  existingProperty.baseProperty.slug !==
                    basePropertyData.slug) && (
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-red-600">
                      Slug already exists
                      {slugExists.property && (
                        <span className="text-red-500">
                          {" "}
                          (Property: {slugExists.property.reff_id})
                        </span>
                      )}
                      {slugExists.count > 1 && (
                        <span className="text-red-500">
                          {" "}
                          (+{slugExists.count - 1} more)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              {slugDebounce && basePropertyData.slug.trim() && !slugExists && (
                <div className="mt-1 flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <span className="text-blue-600">Checking slug...</span>
                </div>
              )}
              {slugExists &&
                !slugExists.exists &&
                basePropertyData.slug.trim() && (
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-600">Slug is available</span>
                  </div>
                )}
              {slugExists &&
                slugExists.exists &&
                basePropertyData.slug.trim() &&
                (!existingProperty ||
                  existingProperty.baseProperty.slug !==
                    basePropertyData.slug) && (
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-red-600">
                      Slug already exists. Suggested alternatives:
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(() => {
                          const baseSlug = basePropertyData.slug;
                          const suggestions = [];
                          for (let i = 1; i <= 3; i++) {
                            suggestions.push(
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  slugManuallyChanged.current = true;
                                  setBasePropertyData((prev) => ({
                                    ...prev,
                                    slug: `${baseSlug}-${i}`,
                                  }));
                                }}
                                className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded border border-red-200 transition-colors active:scale-95"
                              >
                                {`${baseSlug}-${i}`}
                              </button>,
                            );
                          }
                          return suggestions;
                        })()}
                      </div>
                    </span>
                  </div>
                )}
            </div>

            {/* video thumbnail upload */}
            <div>
              <Label htmlFor="thumbnail_video">
                Video Thumbnail (for "From Our Contents" section)
              </Label>
              <p className="mt-1 mb-3 text-xs text-muted-foreground">
                upload a video file (mp4, mov, webm, avi - max 100MB). it will
                be converted to an animated GIF format (like TikTok thumbnails).
                <br />
                <strong className="text-blue-600">
                  Only the GIF will be uploaded, not the original video file.
                </strong>
              </p>

              {/* show existing video or preview */}
              {(basePropertyData.thumbnail_video || videoPreviewUrl) &&
                !isVideoUploading && (
                  <VideoThumbnailDisplay
                    thumbnailVideo={basePropertyData.thumbnail_video}
                    previewUrl={videoPreviewUrl}
                    onRemove={async () => {
                      // revoke object url to prevent memory leak
                      if (videoPreviewUrlRef.current) {
                        URL.revokeObjectURL(videoPreviewUrlRef.current);
                        videoPreviewUrlRef.current = null;
                      }

                      // update state immediately
                      setBasePropertyData((prev) => ({
                        ...prev,
                        thumbnail_video: undefined,
                      }));
                      setVideoPreviewUrl(null);
                      setSelectedVideoFile(null);
                      resetVideoUpload();

                      // if in edit mode, immediately update the database
                      if (mode === "edit" && propertyId) {
                        try {
                          await updateProperty({
                            id: propertyId,
                            thumbnail_video: null,
                            updated_by: currentUser?.email || "unknown",
                          });
                          // clear the original reference so it's marked as deleted
                          originalThumbnailVideoRef.current = null;
                          toast.success("GIF thumbnail deleted successfully");
                        } catch (error) {
                          console.error("Failed to delete thumbnail:", error);
                          toast.error(
                            "Failed to delete thumbnail. Please try again.",
                          );
                          // revert state on error - restore from original ref
                          if (originalThumbnailVideoRef.current) {
                            setBasePropertyData((prev) => ({
                              ...prev,
                              thumbnail_video:
                                originalThumbnailVideoRef.current,
                            }));
                          }
                        }
                      }
                    }}
                  />
                )}

              {/* video upload progress */}
              {isVideoUploading && (
                <div className="mb-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    <span className="text-sm font-medium">
                      {conversionProgress < 90
                        ? "Converting video..."
                        : "Uploading..."}
                    </span>
                  </div>
                  <Progress value={conversionProgress} className="h-2" />
                  <p className="mt-2 text-xs text-muted-foreground">
                    {conversionProgress < 50
                      ? "Converting to WebM format..."
                      : conversionProgress < 90
                        ? "Converting to GIF format..."
                        : "Uploading files..."}
                  </p>
                </div>
              )}

              {/* video upload error */}
              {videoUploadError && (
                <div className="mb-3 p-3 border border-red-200 rounded-lg bg-red-50 text-red-700 text-sm">
                  {videoUploadError}
                </div>
              )}

              {/* video file input */}
              {!basePropertyData.thumbnail_video &&
                !videoPreviewUrl &&
                !isVideoUploading && (
                  <div className="relative">
                    <input
                      type="file"
                      id="thumbnail_video"
                      accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={!basePropertyData.reff_id}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // revoke previous preview url if exists
                          if (videoPreviewUrlRef.current) {
                            URL.revokeObjectURL(videoPreviewUrlRef.current);
                          }
                          // create preview url
                          const previewUrl = URL.createObjectURL(file);
                          videoPreviewUrlRef.current = previewUrl;
                          setVideoPreviewUrl(previewUrl);
                          setSelectedVideoFile(file);

                          // start upload
                          try {
                            await uploadVideo(file);
                          } catch {
                            // error handled by hook
                            // revoke object url to prevent memory leak
                            if (videoPreviewUrlRef.current) {
                              URL.revokeObjectURL(videoPreviewUrlRef.current);
                              videoPreviewUrlRef.current = null;
                            }
                            setVideoPreviewUrl(null);
                            setSelectedVideoFile(null);
                          }
                        }
                        // reset input
                        e.target.value = "";
                      }}
                    />
                    <div
                      className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg transition-colors ${
                        basePropertyData.reff_id
                          ? "border-gray-300 hover:border-gray-400 cursor-pointer"
                          : "border-gray-200 bg-gray-50 cursor-not-allowed"
                      }`}
                    >
                      <Upload className="w-8 h-8 mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {basePropertyData.reff_id
                          ? "Click to upload video"
                          : "Enter Reference ID first to enable upload"}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        MP4, MOV, WebM, AVI (max 100MB)
                      </p>
                    </div>
                  </div>
                )}
            </div>

            {/* social media link (kept for reference) */}
            <div>
              <Label htmlFor="social_media_link">
                Social Media Link (optional - for reference only)
              </Label>
              <Input
                id="social_media_link"
                value={basePropertyData.social_media_link || ""}
                onChange={(e) => {
                  setBasePropertyData((prev) => ({
                    ...prev,
                    social_media_link: e.target.value,
                  }));
                }}
                placeholder="https://www.tiktok.com/@username/video/1234567890"
                type="url"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                optional: paste link to tiktok reel, youtube short, or instagram
                reel for reference
              </p>
            </div>

            <div>
              <Label htmlFor="tags">Tags</Label>
              <MultiSelectTags
                tags={tags || []}
                selectedTagIds={basePropertyData.tags}
                onSelectionChange={(selectedIds) =>
                  setBasePropertyData((prev) => ({
                    ...prev,
                    tags: selectedIds,
                  }))
                }
                placeholder="Select tags for this property..."
                searchPlaceholder="Search tags..."
                emptyText="No tags found."
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Switch
                  id="featured"
                  checked={basePropertyData.featured}
                  onCheckedChange={(checked) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      featured: checked,
                    }))
                  }
                />
                <Label htmlFor="featured">Featured Property</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="draft"
                  checked={basePropertyData.draft}
                  onCheckedChange={(checked) =>
                    setBasePropertyData((prev) => ({ ...prev, draft: checked }))
                  }
                />
                <Label htmlFor="draft">Save as Draft</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="hidden"
                  checked={basePropertyData.hidden}
                  onCheckedChange={(checked) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      hidden: checked,
                    }))
                  }
                />
                <Label htmlFor="hidden">Hidden</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="price_hidden"
                  checked={basePropertyData.price_hidden}
                  onCheckedChange={(checked) =>
                    setBasePropertyData((prev) => ({
                      ...prev,
                      price_hidden: checked,
                    }))
                  }
                />
                <Label htmlFor="price_hidden">Hide Price</Label>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4" id="property_type_section">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold">Select Property Types</h3>
              <p className="text-sm text-muted-foreground">
                {mode === "edit" && selectedPropertyTypes.length > 0
                  ? "Property types loaded from existing data. You can change them if needed."
                  : "Choose one or more categories that describe your property"}
              </p>
              {mode === "edit" && selectedPropertyTypes.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    Currently selected:{" "}
                    {selectedPropertyTypes
                      .map(
                        (type) =>
                          PROPERTY_TYPES.find((t) => t.value === type)?.label,
                      )
                      .join(", ")}
                  </p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PROPERTY_TYPES.map((type) => {
                const isSelected = selectedPropertyTypes.includes(
                  type.value as PropertyType,
                );
                return (
                  <Card
                    key={type.value}
                    className={`cursor-pointer transition-all hover:shadow-md ${
                      isSelected
                        ? "ring-2 ring-primary border-primary"
                        : "border-border"
                    }`}
                    onClick={() => {
                      console.log("Property type toggled:", type.value);
                      if (isSelected) {
                        setSelectedPropertyTypes((prev) =>
                          prev.filter((t) => t !== type.value),
                        );
                      } else {
                        setSelectedPropertyTypes((prev) => [
                          ...prev,
                          type.value as PropertyType,
                        ]);
                      }
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <h4 className="font-medium">{type.label}</h4>
                          <p className="text-sm text-muted-foreground">
                            {type.description}
                          </p>
                          {type.hasFacilities && (
                            <Badge variant="secondary" className="text-xs">
                              Includes Facilities
                            </Badge>
                          )}
                        </div>
                        {isSelected && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {selectedPropertyTypes.length === 0 && (
              <div className="text-center text-sm text-muted-foreground">
                Please select at least one property type to continue.
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold">
                Property Type Details {mode === "edit" ? "(Editing)" : ""}
              </h3>
              <p className="text-sm text-muted-foreground">
                Enter specific information for each selected property type
              </p>
            </div>
            {selectedPropertyTypes.map((type, index) => {
              const propertyType = PROPERTY_TYPES.find((t) => t.value === type);
              return (
                <div key={type} className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-sm">
                      {propertyType?.label}
                    </Badge>
                    {propertyType?.hasFacilities && (
                      <Badge variant="secondary" className="text-xs">
                        Includes Facilities
                      </Badge>
                    )}
                  </div>
                  <Card>
                    <CardContent className="p-6">
                      {renderTypeSpecificFormForType(type)}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        );

      case 4:
        // check if any selected type needs facilities
        const needsFacilities = selectedPropertyTypes.some((type) => {
          const propertyType = PROPERTY_TYPES.find((t) => t.value === type);
          return propertyType?.hasFacilities;
        });

        if (!needsFacilities) {
          return null; // this step should be skipped
        }

        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold">Property Facilities</h3>
              <p className="text-sm text-muted-foreground">
                Add details about the property's facilities and amenities
              </p>
            </div>
            {renderFacilitiesForm()}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold">Property Images</h3>
              <p className="text-sm text-muted-foreground">
                Upload photos to showcase your property
              </p>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 <strong>Image Order Matters:</strong> The first image will
                  be used as the thumbnail. Drag and drop images to reorder them
                  after upload.
                </p>
              </div>
            </div>
            <ImageUpload
              value={basePropertyData.images}
              onChange={(images) =>
                setBasePropertyData((prev) => ({ ...prev, images }))
              }
              reffId={basePropertyData.reff_id}
              maxFiles={50}
              maxSize={5 * 1024 * 1024} // 5MB
            />
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold">Review Property Details</h3>
              <p className="text-sm text-muted-foreground">
                Please review all information before submitting
              </p>
            </div>
            {renderReviewSection()}
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Contact (Optional)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <Checkbox
                    id="add_contact"
                    checked={addContact}
                    onCheckedChange={(v) => setAddContact(Boolean(v))}
                  />
                  <Label htmlFor="add_contact">
                    Add a contact linked to this property reff ID
                  </Label>
                </div>
                {addContact && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="c_name">Name</Label>
                      <Input
                        id="c_name"
                        value={contactData.name}
                        onChange={(e) =>
                          setContactData((p) => ({
                            ...p,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Enter contact name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="c_phone">Phone *</Label>
                      <Input
                        id="c_phone"
                        value={contactData.phone}
                        onChange={(e) =>
                          setContactData((p) => ({
                            ...p,
                            phone: e.target.value,
                          }))
                        }
                        onBlur={(e) =>
                          setContactData((p) => ({
                            ...p,
                            phone: e.target.value.trim(),
                          }))
                        }
                        placeholder="Numbers only"
                        className={
                          fieldErrors["contact_phone"] ? "border-red-500" : ""
                        }
                      />
                      {fieldErrors["contact_phone"] && (
                        <p className="text-red-500 text-sm mt-1">
                          {fieldErrors["contact_phone"]}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="c_country">Country *</Label>
                      <Input
                        id="c_country"
                        value={contactData.country}
                        onChange={(e) =>
                          setContactData((p) => ({
                            ...p,
                            country: e.target.value,
                          }))
                        }
                        onBlur={(e) =>
                          setContactData((p) => ({
                            ...p,
                            country: e.target.value.trim(),
                          }))
                        }
                        placeholder="Enter country"
                        className={
                          fieldErrors["contact_country"] ? "border-red-500" : ""
                        }
                      />
                      {fieldErrors["contact_country"] && (
                        <p className="text-red-500 text-sm mt-1">
                          {fieldErrors["contact_country"]}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="c_qualification">Qualification</Label>
                      <Select
                        value={contactData.qualification || ""}
                        onValueChange={(v) =>
                          setContactData((p) => ({
                            ...p,
                            qualification: v as any,
                          }))
                        }
                      >
                        <SelectTrigger id="c_qualification">
                          <SelectValue placeholder="Select qualification" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="unqualified">
                            Unqualified
                          </SelectItem>
                          <SelectItem value="not approve">
                            Not Approve
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="c_group">Group</Label>
                      <Select
                        value={contactData.group_id || ""}
                        onValueChange={(v) =>
                          setContactData((p) => ({ ...p, group_id: v }))
                        }
                      >
                        <SelectTrigger id="c_group">
                          <SelectValue placeholder="Select group" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(contactGroups) &&
                            contactGroups.map(
                              (g: { _id: string; group_name: string }) => (
                                <SelectItem key={g._id} value={g._id}>
                                  {g.group_name}
                                </SelectItem>
                              ),
                            )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="c_type">Type</Label>
                      <Select
                        value={contactData.type_id || ""}
                        onValueChange={(v) =>
                          setContactData((p) => ({ ...p, type_id: v }))
                        }
                      >
                        <SelectTrigger id="c_type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(contactTypes) &&
                            contactTypes.map(
                              (t: { _id: string; type_name: string }) => (
                                <SelectItem key={t._id} value={t._id}>
                                  {t.type_name}
                                </SelectItem>
                              ),
                            )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="c_email">Email</Label>
                      <Input
                        id="c_email"
                        type="email"
                        value={contactData.email}
                        onChange={(e) =>
                          setContactData((p) => ({
                            ...p,
                            email: e.target.value,
                          }))
                        }
                        placeholder="Enter email"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="c_address">Address</Label>
                      <Textarea
                        id="c_address"
                        value={contactData.address}
                        onChange={(e) =>
                          setContactData((p) => ({
                            ...p,
                            address: e.target.value,
                          }))
                        }
                        rows={2}
                        placeholder="Enter address"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="c_note">Note</Label>
                      <Textarea
                        id="c_note"
                        value={contactData.note}
                        onChange={(e) =>
                          setContactData((p) => ({
                            ...p,
                            note: e.target.value,
                          }))
                        }
                        rows={2}
                        placeholder="Additional notes"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  const renderTypeSpecificFormForType = (type: PropertyType) => {
    const currentData = typeFormData[type];
    const updateData = (updates: Record<string, unknown>) => {
      setTypeFormData((prev) => ({
        ...prev,
        [type]: { ...prev[type], ...updates },
      }));
    };

    switch (type) {
      case "rentals": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"bedrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bedrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bathrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bathrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bathrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"build_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Build Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 300" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"price_per_year"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price per Year *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 50000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"minimum_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum Duration (months) *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 12"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"safety_deposit_amount"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Safety Deposit Amount *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 10000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"pet_allowed"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pet Allowed *</FormLabel>
                      <FormControl>
                        <Select
                          value={String(field.value || "")}
                          onValueChange={(v) => field.onChange(v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select pet policy" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                            <SelectItem value="negotiable">
                              Negotiable
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }

      case "freehold_properties":
      case "freehold_villas": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"bedrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bedrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bathrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bathrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bathrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"build_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Build Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 300" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"year_built"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 2020"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"total_price"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Price *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 5000000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"dp_minimal"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Down Payment Minimal *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 30%" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"pks_bank"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PKS Bank *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., BCA, Mandiri, etc."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }

      case "leasehold_villas": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"bedrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bedrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bathrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bathrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bathrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"build_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Build Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 300" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"year_built"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 2020"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"price_for_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price for Duration *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 5000000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"extension_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Extension Duration (optional)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 30"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"leasehold_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Leasehold Duration (years) *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 25"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }

      case "freehold_lands": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"price_per_are"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price per Are *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 10000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"total_price"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Price *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 5000000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"road_access_width"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Road Access Width *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 6" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_zoning"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Zoning *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Residential" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"view"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>View *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Ocean view, Mountain view"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }

      case "leasehold_lands": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"total_price"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Price *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 5000000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"lease_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lease Duration (years) *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 25"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"price_per_are"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price per Are *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 10000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_zoning"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Zoning *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Residential" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"view"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>View *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Ocean view, Mountain view"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }

      case "freehold_commercial": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"bedrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrooms (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bedrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bathrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bathrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bathrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"build_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Build Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 300" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"title"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title *</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SHM">SHM</SelectItem>
                            <SelectItem value="HGB">HGB</SelectItem>
                            <SelectItem value="Sewa">Sewa</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"building_permit"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Building Permit *</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select building permit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Ruko">Ruko</SelectItem>
                            <SelectItem value="Rumah Tinggal">
                              Rumah Tinggal
                            </SelectItem>
                            <SelectItem value="Gudang">Gudang</SelectItem>
                            <SelectItem value="No Permit">No Permit</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"frontage"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lebar Muka/Frontage *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 10" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={f.control as any}
                  name={"accessibility"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Aksesibilitas/Accessibility *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Main Road, Perumahan"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"year_built"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 2020"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bank_partnership"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bank Partnership *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., BCA, BNI" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"down_payment"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Down Payment *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"total_price"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Price *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 5000000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }
      case "leasehold_commercial": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"bedrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrooms (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bedrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bathrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bathrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bathrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"build_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Build Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 300" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"title"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title *</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SHM">SHM</SelectItem>
                            <SelectItem value="HGB">HGB</SelectItem>
                            <SelectItem value="Sewa">Sewa</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"building_permit"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Building Permit *</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select building permit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Ruko">Ruko</SelectItem>
                            <SelectItem value="Rumah Tinggal">
                              Rumah Tinggal
                            </SelectItem>
                            <SelectItem value="Gudang">Gudang</SelectItem>
                            <SelectItem value="No Permit">No Permit</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"frontage"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lebar Muka/Frontage *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 10" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={f.control as any}
                  name={"accessibility"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Aksesibilitas/Accessibility *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Main Road, Perumahan"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={f.control as any}
                  name={"year_built"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 2020"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={f.control as any}
                  name={"price_for_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price for Duration *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 5000000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"leasehold_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Leasehold Duration (Years) *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 25"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"extension_duration"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Extension Duration (Years)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" placeholder="e.g., 5" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }
      case "freehold_commercial": {
        const form = typeForms[type as keyof typeof typeForms];
        if (!form) return null;
        const f = form;
        return (
          <FormProvider key={`${type}-${formKey}`} {...(f as any)}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"bedrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bedrooms (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bedrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bathrooms"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bathrooms *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Number of bathrooms"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"land_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Land Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"build_size"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Build Size *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 300" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"title"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title *</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SHM">SHM</SelectItem>
                            <SelectItem value="HGB">HGB</SelectItem>
                            <SelectItem value="Sewa">Sewa</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"building_permit"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Building Permit *</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select building permit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Ruko">Ruko</SelectItem>
                            <SelectItem value="Rumah Tinggal">
                              Rumah Tinggal
                            </SelectItem>
                            <SelectItem value="Gudang">Gudang</SelectItem>
                            <SelectItem value="No Permit">No Permit</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"frontage"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lebar Muka/Frontage *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 10" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"orientation"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Arah Hadap/Orientation *</FormLabel>
                      <FormControl>
                        <MultiSelectOrientation
                          value={toOrientationArray(field.value)}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={f.control as any}
                  name={"accessibility"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Aksesibilitas/Accessibility *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Main Road, Perumahan"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"year_built"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="e.g., 2020"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"bank_partnership"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bank Partnership *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., BCA, BNI" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={f.control as any}
                  name={"down_payment"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Down Payment *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 500000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={f.control as any}
                  name={"total_price"}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Price *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., 5000000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </FormProvider>
        );
      }

      default:
        return <div>Unknown property type: {type}</div>;
    }
  };

  const renderTypeSpecificForm = () => {
    switch (selectedPropertyType) {
      case "rentals":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bedrooms">Bedrooms *</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={rentalsData.bedrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setRentalsData((prev) => ({
                        ...prev,
                        bedrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        rental_bedrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        rental_bedrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bedrooms"
                  className={
                    fieldErrors["rental_bedrooms"] ? "border-red-500" : ""
                  }
                />
                {fieldErrors["rental_bedrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["rental_bedrooms"]}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="bathrooms">Bathrooms *</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  value={rentalsData.bathrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setRentalsData((prev) => ({
                        ...prev,
                        bathrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        rental_bathrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        rental_bathrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bathrooms"
                  className={
                    fieldErrors["rental_bathrooms"] ? "border-red-500" : ""
                  }
                />
                {fieldErrors["rental_bathrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["rental_bathrooms"]}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rental_land_size">Land Size *</Label>
                <Input
                  id="rental_land_size"
                  value={rentalsData.land_size}
                  onChange={(e) =>
                    setRentalsData((prev) => ({
                      ...prev,
                      land_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 500 sqm"
                />
              </div>
              <div>
                <Label htmlFor="rental_build_size">Build Size *</Label>
                <Input
                  id="rental_build_size"
                  value={rentalsData.build_size}
                  onChange={(e) =>
                    setRentalsData((prev) => ({
                      ...prev,
                      build_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 300 sqm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price_per_year">Price per Year *</Label>
                <Input
                  id="price_per_year"
                  value={rentalsData.price_per_year}
                  onChange={(e) =>
                    setRentalsData((prev) => ({
                      ...prev,
                      price_per_year: e.target.value,
                    }))
                  }
                  placeholder="e.g., $24,000"
                />
              </div>
              <div>
                <Label htmlFor="minimum_duration">
                  Minimum Duration (months) *
                </Label>
                <Input
                  id="minimum_duration"
                  type="number"
                  value={rentalsData.minimum_duration}
                  onChange={(e) =>
                    setRentalsData((prev) => ({
                      ...prev,
                      minimum_duration: parseInt(e.target.value) || 0,
                    }))
                  }
                  placeholder="e.g., 12"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="safety_deposit">Safety Deposit Amount *</Label>
                <Input
                  id="safety_deposit"
                  value={rentalsData.safety_deposit_amount}
                  onChange={(e) =>
                    setRentalsData((prev) => ({
                      ...prev,
                      safety_deposit_amount: e.target.value,
                    }))
                  }
                  placeholder="e.g., $2,000"
                />
              </div>
              <div>
                <Label htmlFor="pet_allowed">Pet Allowed *</Label>
                <Select
                  value={rentalsData.pet_allowed}
                  onValueChange={(value) =>
                    setRentalsData((prev) => ({ ...prev, pet_allowed: value }))
                  }
                >
                  <SelectTrigger id="pet_allowed_trigger">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="negotiable">Negotiable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case "freehold_properties":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bedrooms">Bedrooms *</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={freeholdPropertyData.bedrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setFreeholdPropertyData((prev) => ({
                        ...prev,
                        bedrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_bedrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_bedrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bedrooms"
                  className={
                    fieldErrors["freehold_bedrooms"] ? "border-red-500" : ""
                  }
                />
                {fieldErrors["freehold_bedrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["freehold_bedrooms"]}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="bathrooms">Bathrooms *</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  value={freeholdPropertyData.bathrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setFreeholdPropertyData((prev) => ({
                        ...prev,
                        bathrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_bathrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_bathrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bathrooms"
                  className={
                    fieldErrors["freehold_bathrooms"] ? "border-red-500" : ""
                  }
                />
                {fieldErrors["freehold_bathrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["freehold_bathrooms"]}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fh_build_size">Build Size *</Label>
                <Input
                  id="fh_build_size"
                  value={freeholdPropertyData.build_size}
                  onChange={(e) =>
                    setFreeholdPropertyData((prev) => ({
                      ...prev,
                      build_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 300 sqm"
                />
              </div>
              <div>
                <Label htmlFor="fh_land_size">Land Size *</Label>
                <Input
                  id="fh_land_size"
                  value={freeholdPropertyData.land_size}
                  onChange={(e) =>
                    setFreeholdPropertyData((prev) => ({
                      ...prev,
                      land_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 500 sqm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fh_year_built">Year Built *</Label>
                <Input
                  id="fh_year_built"
                  type="number"
                  value={freeholdPropertyData.year_built}
                  onChange={(e) =>
                    setFreeholdPropertyData((prev) => ({
                      ...prev,
                      year_built:
                        parseInt(e.target.value) || new Date().getFullYear(),
                    }))
                  }
                  placeholder="e.g., 2020"
                />
              </div>
              <div>
                <Label htmlFor="fh_total_price">Total Price *</Label>
                <Input
                  id="fh_total_price"
                  value={freeholdPropertyData.total_price}
                  onChange={(e) =>
                    setFreeholdPropertyData((prev) => ({
                      ...prev,
                      total_price: e.target.value,
                    }))
                  }
                  placeholder="e.g., $350,000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dp_minimal">Down Payment Minimal *</Label>
                <Input
                  id="dp_minimal"
                  value={freeholdPropertyData.dp_minimal}
                  onChange={(e) =>
                    setFreeholdPropertyData((prev) => ({
                      ...prev,
                      dp_minimal: e.target.value,
                    }))
                  }
                  placeholder="e.g., $70,000"
                />
              </div>
              <div>
                <Label htmlFor="fh_pks_bank">PKS Bank *</Label>
                <Input
                  id="fh_pks_bank"
                  value={freeholdPropertyData.pks_bank}
                  onChange={(e) =>
                    setFreeholdPropertyData((prev) => ({
                      ...prev,
                      pks_bank: e.target.value,
                    }))
                  }
                  placeholder="Bank name"
                />
              </div>
            </div>
          </div>
        );

      case "freehold_villas":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bedrooms">Bedrooms *</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={freeholdVillaData.bedrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setFreeholdVillaData((prev) => ({
                        ...prev,
                        bedrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_villa_bedrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_villa_bedrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bedrooms"
                  className={
                    fieldErrors["freehold_villa_bedrooms"]
                      ? "border-red-500"
                      : ""
                  }
                />
                {fieldErrors["freehold_villa_bedrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["freehold_villa_bedrooms"]}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="bathrooms">Bathrooms *</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  value={freeholdVillaData.bathrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setFreeholdVillaData((prev) => ({
                        ...prev,
                        bathrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_villa_bathrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        freehold_villa_bathrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bathrooms"
                  className={
                    fieldErrors["freehold_villa_bathrooms"]
                      ? "border-red-500"
                      : ""
                  }
                />
                {fieldErrors["freehold_villa_bathrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["freehold_villa_bathrooms"]}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fhv_build_size">Build Size *</Label>
                <Input
                  id="fhv_build_size"
                  value={freeholdVillaData.build_size}
                  onChange={(e) =>
                    setFreeholdVillaData((prev) => ({
                      ...prev,
                      build_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 300 sqm"
                />
              </div>
              <div>
                <Label htmlFor="fhv_land_size">Land Size *</Label>
                <Input
                  id="fhv_land_size"
                  value={freeholdVillaData.land_size}
                  onChange={(e) =>
                    setFreeholdVillaData((prev) => ({
                      ...prev,
                      land_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 500 sqm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fhv_year_built">Year Built *</Label>
                <Input
                  id="fhv_year_built"
                  type="number"
                  value={freeholdVillaData.year_built}
                  onChange={(e) =>
                    setFreeholdVillaData((prev) => ({
                      ...prev,
                      year_built:
                        parseInt(e.target.value) || new Date().getFullYear(),
                    }))
                  }
                  placeholder="e.g., 2020"
                />
              </div>
              <div>
                <Label htmlFor="fhv_total_price">Total Price *</Label>
                <Input
                  id="fhv_total_price"
                  value={freeholdVillaData.total_price}
                  onChange={(e) =>
                    setFreeholdVillaData((prev) => ({
                      ...prev,
                      total_price: e.target.value,
                    }))
                  }
                  placeholder="e.g., $500,000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dp_minimal">Down Payment Minimal *</Label>
                <Input
                  id="dp_minimal"
                  value={freeholdVillaData.dp_minimal}
                  onChange={(e) =>
                    setFreeholdVillaData((prev) => ({
                      ...prev,
                      dp_minimal: e.target.value,
                    }))
                  }
                  placeholder="e.g., $100,000"
                />
              </div>
              <div>
                <Label htmlFor="fhv_pks_bank">PKS Bank *</Label>
                <Input
                  id="fhv_pks_bank"
                  value={freeholdVillaData.pks_bank}
                  onChange={(e) =>
                    setFreeholdVillaData((prev) => ({
                      ...prev,
                      pks_bank: e.target.value,
                    }))
                  }
                  placeholder="Bank name"
                />
              </div>
            </div>
          </div>
        );

      case "leasehold_villas":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bedrooms">Bedrooms *</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={leaseholdVillaData.bedrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setLeaseholdVillaData((prev) => ({
                        ...prev,
                        bedrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        leasehold_villa_bedrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        leasehold_villa_bedrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bedrooms"
                  className={
                    fieldErrors["leasehold_villa_bedrooms"]
                      ? "border-red-500"
                      : ""
                  }
                />
                {fieldErrors["leasehold_villa_bedrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["leasehold_villa_bedrooms"]}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="bathrooms">Bathrooms *</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  value={leaseholdVillaData.bathrooms}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      (!isNaN(Number(value)) && Number(value) >= 0)
                    ) {
                      setLeaseholdVillaData((prev) => ({
                        ...prev,
                        bathrooms: value,
                      }));
                      setFieldErrors((prev) => ({
                        ...prev,
                        leasehold_villa_bathrooms: "",
                      }));
                    } else {
                      setFieldErrors((prev) => ({
                        ...prev,
                        leasehold_villa_bathrooms: "Only numbers allowed",
                      }));
                    }
                  }}
                  placeholder="Number of bathrooms"
                  className={
                    fieldErrors["leasehold_villa_bathrooms"]
                      ? "border-red-500"
                      : ""
                  }
                />
                {fieldErrors["leasehold_villa_bathrooms"] && (
                  <p className="text-red-500 text-sm mt-1">
                    {fieldErrors["leasehold_villa_bathrooms"]}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lhv_build_size">Build Size *</Label>
                <Input
                  id="lhv_build_size"
                  value={leaseholdVillaData.build_size}
                  onChange={(e) =>
                    setLeaseholdVillaData((prev) => ({
                      ...prev,
                      build_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 300 sqm"
                />
              </div>
              <div>
                <Label htmlFor="lhv_land_size">Land Size *</Label>
                <Input
                  id="lhv_land_size"
                  value={leaseholdVillaData.land_size}
                  onChange={(e) =>
                    setLeaseholdVillaData((prev) => ({
                      ...prev,
                      land_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 500 sqm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lhv_year_built">Year Built *</Label>
                <Input
                  id="lhv_year_built"
                  type="number"
                  value={leaseholdVillaData.year_built}
                  onChange={(e) =>
                    setLeaseholdVillaData((prev) => ({
                      ...prev,
                      year_built:
                        parseInt(e.target.value) || new Date().getFullYear(),
                    }))
                  }
                  placeholder="e.g., 2020"
                />
              </div>
              <div>
                <Label htmlFor="price_for_duration">Price for Duration *</Label>
                <Input
                  id="price_for_duration"
                  value={leaseholdVillaData.price_for_duration}
                  onChange={(e) =>
                    setLeaseholdVillaData((prev) => ({
                      ...prev,
                      price_for_duration: e.target.value,
                    }))
                  }
                  placeholder="e.g., $300,000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lhv_leasehold_duration">
                  Leasehold Duration (years) *
                </Label>
                <Input
                  id="lhv_leasehold_duration"
                  type="number"
                  value={leaseholdVillaData.leasehold_duration}
                  onChange={(e) =>
                    setLeaseholdVillaData((prev) => ({
                      ...prev,
                      leasehold_duration: parseInt(e.target.value) || 0,
                    }))
                  }
                  placeholder="e.g., 25"
                />
              </div>
              <div>
                <Label htmlFor="lhv_extension_duration">
                  Extension Duration (years)
                </Label>
                <Input
                  id="lhv_extension_duration"
                  type="number"
                  value={leaseholdVillaData.extension_duration || ""}
                  onChange={(e) =>
                    setLeaseholdVillaData((prev) => ({
                      ...prev,
                      extension_duration: parseInt(e.target.value) || undefined,
                    }))
                  }
                  placeholder="e.g., 25"
                />
              </div>
            </div>
          </div>
        );

      case "freehold_lands":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fhl_land_size">Land Size *</Label>
                <Input
                  id="fhl_land_size"
                  value={freeholdLandData.land_size}
                  onChange={(e) =>
                    setFreeholdLandData((prev) => ({
                      ...prev,
                      land_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 1000 sqm"
                />
              </div>
              <div>
                <Label htmlFor="fhl_price_per_are">Price per Are *</Label>
                <Input
                  id="fhl_price_per_are"
                  value={freeholdLandData.price_per_are}
                  onChange={(e) =>
                    setFreeholdLandData((prev) => ({
                      ...prev,
                      price_per_are: e.target.value,
                    }))
                  }
                  placeholder="e.g., $25,000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fhl_total_price">Total Price *</Label>
                <Input
                  id="fhl_total_price"
                  value={freeholdLandData.total_price}
                  onChange={(e) =>
                    setFreeholdLandData((prev) => ({
                      ...prev,
                      total_price: e.target.value,
                    }))
                  }
                  placeholder="e.g., $250,000"
                />
              </div>
              <div>
                <Label htmlFor="road_access_width">Road Access Width *</Label>
                <Input
                  id="road_access_width"
                  value={freeholdLandData.road_access_width}
                  onChange={(e) =>
                    setFreeholdLandData((prev) => ({
                      ...prev,
                      road_access_width: e.target.value,
                    }))
                  }
                  placeholder="e.g., 6 meters"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fhl_land_zoning">Land Zoning *</Label>
                <Input
                  id="fhl_land_zoning"
                  value={freeholdLandData.land_zoning}
                  onChange={(e) =>
                    setFreeholdLandData((prev) => ({
                      ...prev,
                      land_zoning: e.target.value,
                    }))
                  }
                  placeholder="e.g., Residential"
                />
              </div>
              <div>
                <Label htmlFor="fhl_view">View *</Label>
                <Input
                  id="fhl_view"
                  value={freeholdLandData.view}
                  onChange={(e) =>
                    setFreeholdLandData((prev) => ({
                      ...prev,
                      view: e.target.value,
                    }))
                  }
                  placeholder="e.g., Rice field view"
                />
              </div>
            </div>
          </div>
        );

      case "leasehold_lands":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lhl_land_size">Land Size *</Label>
                <Input
                  id="lhl_land_size"
                  value={leaseholdLandData.land_size}
                  onChange={(e) =>
                    setLeaseholdLandData((prev) => ({
                      ...prev,
                      land_size: e.target.value,
                    }))
                  }
                  placeholder="e.g., 1000 sqm"
                />
              </div>
              <div>
                <Label htmlFor="lhl_price_per_are">Price per Are *</Label>
                <Input
                  id="lhl_price_per_are"
                  value={leaseholdLandData.price_per_are}
                  onChange={(e) =>
                    setLeaseholdLandData((prev) => ({
                      ...prev,
                      price_per_are: e.target.value,
                    }))
                  }
                  placeholder="e.g., $15,000"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lhl_total_price">Total Price *</Label>
                <Input
                  id="lhl_total_price"
                  value={leaseholdLandData.total_price}
                  onChange={(e) =>
                    setLeaseholdLandData((prev) => ({
                      ...prev,
                      total_price: e.target.value,
                    }))
                  }
                  placeholder="e.g., $150,000"
                />
              </div>
              <div>
                <Label htmlFor="lease_duration">Lease Duration (years) *</Label>
                <Input
                  id="lease_duration"
                  type="number"
                  value={leaseholdLandData.lease_duration}
                  onChange={(e) =>
                    setLeaseholdLandData((prev) => ({
                      ...prev,
                      lease_duration: parseInt(e.target.value) || 0,
                    }))
                  }
                  placeholder="e.g., 25"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lhl_land_zoning">Land Zoning *</Label>
                <Input
                  id="lhl_land_zoning"
                  value={leaseholdLandData.land_zoning}
                  onChange={(e) =>
                    setLeaseholdLandData((prev) => ({
                      ...prev,
                      land_zoning: e.target.value,
                    }))
                  }
                  placeholder="e.g., Commercial"
                />
              </div>
              <div>
                <Label htmlFor="lhl_view">View *</Label>
                <Input
                  id="lhl_view"
                  value={leaseholdLandData.view}
                  onChange={(e) =>
                    setLeaseholdLandData((prev) => ({
                      ...prev,
                      view: e.target.value,
                    }))
                  }
                  placeholder="e.g., Ocean view"
                />
              </div>
            </div>
          </div>
        );

      default:
        return <div>Please select a property type first.</div>;
    }
  };

  const renderFacilitiesForm = () => {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="living_room_type">Living Room Type</Label>
            <Input
              id="living_room_type"
              value={facilitiesData.living_room_type || ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({
                  ...prev,
                  living_room_type: e.target.value,
                }))
              }
              placeholder="e.g., Open concept"
            />
          </div>
          <div>
            <Label htmlFor="dining_rooms">Dining Rooms</Label>
            <Input
              id="dining_rooms"
              type="number"
              value={facilitiesData.dining_rooms || ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({
                  ...prev,
                  dining_rooms: parseInt(e.target.value) || 0,
                }))
              }
              placeholder="Number of dining rooms"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="kitchens">Kitchens</Label>
            <Input
              id="kitchens"
              type="number"
              value={facilitiesData.kitchens || ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({
                  ...prev,
                  kitchens: parseInt(e.target.value) || 0,
                }))
              }
              placeholder="Number of kitchens"
            />
          </div>
          <div>
            <Label htmlFor="extra_rooms">Extra Rooms</Label>
            <Input
              id="extra_rooms"
              type="number"
              value={facilitiesData.extra_rooms ?? ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({
                  ...prev,
                  extra_rooms: parseInt(e.target.value) || 0,
                }))
              }
              placeholder="Number of extra rooms"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="electricity_power">Electricity Power (KVA)</Label>
            <Input
              id="electricity_power"
              type="number"
              value={facilitiesData.electricity_power || ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({
                  ...prev,
                  electricity_power: parseInt(e.target.value) || 0,
                }))
              }
              placeholder="e.g., 3300"
            />
          </div>
          <div>
            <Label htmlFor="water_source">Water Source</Label>
            <Input
              id="water_source"
              value={facilitiesData.water_source || ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({
                  ...prev,
                  water_source: e.target.value,
                }))
              }
              placeholder="e.g., Well water"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="parking">Parking Spaces</Label>
            <Input
              id="parking"
              type="number"
              value={facilitiesData.parking || ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({
                  ...prev,
                  parking: parseInt(e.target.value) || 0,
                }))
              }
              placeholder="Number of parking spaces"
            />
          </div>
          <div>
            <Label htmlFor="view">View</Label>
            <Input
              id="view"
              value={facilitiesData.view || ""}
              onChange={(e) =>
                setFacilitiesData((prev) => ({ ...prev, view: e.target.value }))
              }
              placeholder="e.g., Garden view"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="furniture">Furniture</Label>
          <Select
            value={facilitiesData.furniture || ""}
            onValueChange={(value) =>
              setFacilitiesData((prev) => ({ ...prev, furniture: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select furniture option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fully_furnished">Fully Furnished</SelectItem>
              <SelectItem value="semi_furnished">Semi Furnished</SelectItem>
              <SelectItem value="unfurnished">Unfurnished</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator />

        <div>
          <Label className="text-base font-medium">Amenities</Label>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="internet"
                checked={facilitiesData.internet || false}
                onCheckedChange={(checked) =>
                  setFacilitiesData((prev) => ({
                    ...prev,
                    internet: !!checked,
                  }))
                }
              />
              <Label htmlFor="internet">Internet</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="swimming_pool"
                checked={facilitiesData.swimming_pool || false}
                onCheckedChange={(checked) =>
                  setFacilitiesData((prev) => ({
                    ...prev,
                    swimming_pool: !!checked,
                  }))
                }
              />
              <Label htmlFor="swimming_pool">Swimming Pool</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="storage"
                checked={facilitiesData.storage || false}
                onCheckedChange={(checked) =>
                  setFacilitiesData((prev) => ({ ...prev, storage: !!checked }))
                }
              />
              <Label htmlFor="storage">Storage</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="garden"
                checked={facilitiesData.garden || false}
                onCheckedChange={(checked) =>
                  setFacilitiesData((prev) => ({ ...prev, garden: !!checked }))
                }
              />
              <Label htmlFor="garden">Garden</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="security"
                checked={facilitiesData.security || false}
                onCheckedChange={(checked) =>
                  setFacilitiesData((prev) => ({
                    ...prev,
                    security: !!checked,
                  }))
                }
              />
              <Label htmlFor="security">Security</Label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewSection = () => {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Property Name:</span>{" "}
                {basePropertyData.property_name}
              </div>
              <div>
                <span className="font-medium">Reference ID:</span>{" "}
                {basePropertyData.reff_id}
              </div>
              <div>
                <span className="font-medium">Slug:</span>{" "}
                {basePropertyData.slug}
              </div>
              <div>
                <span className="font-medium">Location:</span>{" "}
                {basePropertyData.latitude}, {basePropertyData.longitude}
              </div>
              <div className="col-span-2">
                <span className="font-medium">Description:</span>
                <div
                  className="mt-2 p-3 bg-muted rounded-md [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_em]:italic [&_ul]:list-disc [&_ul]:list-outside [&_ul]:mb-3 [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:list-outside [&_ol]:mb-3 [&_ol]:pl-6 [&_li]:mb-1 [&_li]:leading-normal"
                  dangerouslySetInnerHTML={{
                    __html: basePropertyData.description,
                  }}
                />
              </div>
              <div className="col-span-2">
                <span className="font-medium">Tags:</span>{" "}
                <div className="flex flex-wrap gap-1 mt-1">
                  {basePropertyData.tags.length > 0 ? (
                    basePropertyData.tags.map((tagId) => {
                      const tag = tags?.find((t) => t._id === tagId);
                      return tag ? (
                        <Badge
                          key={tag._id}
                          variant="secondary"
                          className="text-xs"
                        >
                          {tag.tag_name}
                        </Badge>
                      ) : null;
                    })
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      No tags selected
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Property Types Section */}
        <Card>
          <CardHeader>
            <CardTitle>
              Property Types ({selectedPropertyTypes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {selectedPropertyTypes.map((type) => {
                const propertyType = PROPERTY_TYPES.find(
                  (t) => t.value === type,
                );
                // read from form values directly to ensure latest data is shown
                const form = typeForms[type as keyof typeof typeForms];
                const data = form?.getValues() || typeFormData[type];

                return (
                  <div key={type} className="border rounded-lg p-4 bg-muted/30">
                    <div className="flex items-center gap-2 mb-4">
                      <Badge variant="outline" className="text-sm font-medium">
                        {propertyType?.label}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {type === "rentals" && data && (
                        <>
                          <div>
                            <span className="font-medium">Bedrooms:</span>{" "}
                            {data.bedrooms}
                          </div>
                          <div>
                            <span className="font-medium">Bathrooms:</span>{" "}
                            {data.bathrooms}
                          </div>
                          <div>
                            <span className="font-medium">Land Size:</span>{" "}
                            {data.land_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Build Size:</span>{" "}
                            {data.build_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Price per Year:</span>{" "}
                            {data.price_per_year}
                          </div>
                          <div>
                            <span className="font-medium">
                              Minimum Duration:
                            </span>{" "}
                            {data.minimum_duration} months
                          </div>
                          <div>
                            <span className="font-medium">Safety Deposit:</span>{" "}
                            {data.safety_deposit_amount}
                          </div>
                          <div>
                            <span className="font-medium">Pet Allowed:</span>{" "}
                            {data.pet_allowed}
                          </div>
                          {data.orientation ? (
                            <div>
                              <span className="font-medium">
                                Orientation/Hadap:
                              </span>{" "}
                              {data.orientation}
                            </div>
                          ) : null}
                        </>
                      )}

                      {(type === "freehold_properties" ||
                        type === "freehold_villas") &&
                        data && (
                          <>
                            <div>
                              <span className="font-medium">Bedrooms:</span>{" "}
                              {data.bedrooms}
                            </div>
                            <div>
                              <span className="font-medium">Bathrooms:</span>{" "}
                              {data.bathrooms}
                            </div>
                            <div>
                              <span className="font-medium">Build Size:</span>{" "}
                              {data.build_size} m²
                            </div>
                            <div>
                              <span className="font-medium">Land Size:</span>{" "}
                              {data.land_size} m²
                            </div>
                            <div>
                              <span className="font-medium">Year Built:</span>{" "}
                              {data.year_built}
                            </div>
                            <div>
                              <span className="font-medium">Total Price:</span>{" "}
                              {data.total_price}
                            </div>
                            <div>
                              <span className="font-medium">DP Minimal:</span>{" "}
                              {data.dp_minimal}
                            </div>
                            <div>
                              <span className="font-medium">PKS Bank:</span>{" "}
                              {data.pks_bank}
                            </div>
                            {data.orientation ? (
                              <div>
                                <span className="font-medium">
                                  Orientation/Hadap:
                                </span>{" "}
                                {data.orientation}
                              </div>
                            ) : null}
                          </>
                        )}

                      {type === "leasehold_villas" && data && (
                        <>
                          <div>
                            <span className="font-medium">Bedrooms:</span>{" "}
                            {data.bedrooms}
                          </div>
                          <div>
                            <span className="font-medium">Bathrooms:</span>{" "}
                            {data.bathrooms}
                          </div>
                          <div>
                            <span className="font-medium">Build Size:</span>{" "}
                            {data.build_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Land Size:</span>{" "}
                            {data.land_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Year Built:</span>{" "}
                            {data.year_built}
                          </div>
                          <div>
                            <span className="font-medium">
                              Price for Duration:
                            </span>{" "}
                            {data.price_for_duration}
                          </div>
                          <div>
                            <span className="font-medium">
                              Leasehold Duration:
                            </span>{" "}
                            {data.leasehold_duration} years
                          </div>
                          {data.orientation ? (
                            <div>
                              <span className="font-medium">
                                Orientation/Hadap:
                              </span>{" "}
                              {data.orientation}
                            </div>
                          ) : null}
                        </>
                      )}

                      {(type === "freehold_lands" ||
                        type === "leasehold_lands") &&
                        data && (
                          <>
                            <div>
                              <span className="font-medium">Land Size:</span>{" "}
                              {data.land_size} m²
                            </div>
                            <div>
                              <span className="font-medium">
                                Price per Are:
                              </span>{" "}
                              {data.price_per_are}
                            </div>
                            <div>
                              <span className="font-medium">Total Price:</span>{" "}
                              {data.total_price}
                            </div>
                            <div>
                              <span className="font-medium">
                                Road Access Width:
                              </span>{" "}
                              {data.road_access_width} m
                            </div>
                            <div>
                              <span className="font-medium">Land Zoning:</span>{" "}
                              {data.land_zoning}
                            </div>
                            <div>
                              <span className="font-medium">View:</span>{" "}
                              {data.view}
                            </div>
                            {type === "leasehold_lands" && (
                              <div>
                                <span className="font-medium">
                                  Lease Duration:
                                </span>{" "}
                                {data.lease_duration} years
                              </div>
                            )}
                            {data.orientation ? (
                              <div>
                                <span className="font-medium">
                                  Orientation/Hadap:
                                </span>{" "}
                                {data.orientation}
                              </div>
                            ) : null}
                          </>
                        )}

                      {type === "freehold_commercial" && data && (
                        <>
                          <div>
                            <span className="font-medium">Bedrooms:</span>{" "}
                            {data.bedrooms}
                          </div>
                          <div>
                            <span className="font-medium">Bathrooms:</span>{" "}
                            {data.bathrooms}
                          </div>
                          <div>
                            <span className="font-medium">Land Size:</span>{" "}
                            {data.land_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Build Size:</span>{" "}
                            {data.build_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Title:</span>{" "}
                            {data.title}
                          </div>
                          <div>
                            <span className="font-medium">
                              Building Permit:
                            </span>{" "}
                            {data.building_permit}
                          </div>
                          <div>
                            <span className="font-medium">Frontage:</span>{" "}
                            {data.frontage} m
                          </div>
                          <div>
                            <span className="font-medium">Orientation:</span>{" "}
                            {data.orientation}
                          </div>
                          <div>
                            <span className="font-medium">Accessibility:</span>{" "}
                            {data.accessibility}
                          </div>
                          <div>
                            <span className="font-medium">Year Built:</span>{" "}
                            {data.year_built}
                          </div>
                          <div>
                            <span className="font-medium">
                              Bank Partnership:
                            </span>{" "}
                            {data.bank_partnership}
                          </div>
                          <div>
                            <span className="font-medium">Down Payment:</span>{" "}
                            {data.down_payment}
                          </div>
                          <div>
                            <span className="font-medium">Total Price:</span>{" "}
                            {data.total_price}
                          </div>
                        </>
                      )}
                      {type === "leasehold_commercial" && data && (
                        <>
                          <div>
                            <span className="font-medium">Bedrooms:</span>{" "}
                            {data.bedrooms}
                          </div>
                          <div>
                            <span className="font-medium">Bathrooms:</span>{" "}
                            {data.bathrooms}
                          </div>
                          <div>
                            <span className="font-medium">Land Size:</span>{" "}
                            {data.land_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Build Size:</span>{" "}
                            {data.build_size} m²
                          </div>
                          <div>
                            <span className="font-medium">Title:</span>{" "}
                            {data.title}
                          </div>
                          <div>
                            <span className="font-medium">
                              Building Permit:
                            </span>{" "}
                            {data.building_permit}
                          </div>
                          <div>
                            <span className="font-medium">Frontage:</span>{" "}
                            {data.frontage} m
                          </div>
                          <div>
                            <span className="font-medium">Orientation:</span>{" "}
                            {data.orientation}
                          </div>
                          <div>
                            <span className="font-medium">Accessibility:</span>{" "}
                            {data.accessibility}
                          </div>
                          <div>
                            <span className="font-medium">Year Built:</span>{" "}
                            {data.year_built}
                          </div>
                          <div>
                            <span className="font-medium">
                              Price for Duration:
                            </span>{" "}
                            {data.price_for_duration}
                          </div>
                          <div>
                            <span className="font-medium">
                              Leasehold Duration:
                            </span>{" "}
                            {data.leasehold_duration} years
                          </div>
                          {data.extension_duration && (
                            <div>
                              <span className="font-medium">
                                Extension Duration:
                              </span>{" "}
                              {data.extension_duration} years
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Facilities Section - only show if any selected type has facilities */}
        {selectedPropertyTypes.some((type) => {
          const propertyType = PROPERTY_TYPES.find((t) => t.value === type);
          return propertyType?.hasFacilities;
        }) && (
          <Card>
            <CardHeader>
              <CardTitle>Facilities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {facilitiesData.living_room_type && (
                  <div>
                    <span className="font-medium">Living Room:</span>{" "}
                    {facilitiesData.living_room_type}
                  </div>
                )}
                {facilitiesData.parking && facilitiesData.parking > 0 && (
                  <div>
                    <span className="font-medium">Parking:</span>{" "}
                    {facilitiesData.parking} spaces
                  </div>
                )}
                {facilitiesData.electricity_power &&
                  facilitiesData.electricity_power > 0 && (
                    <div>
                      <span className="font-medium">Electricity:</span>{" "}
                      {facilitiesData.electricity_power} KVA
                    </div>
                  )}
                {facilitiesData.water_source && (
                  <div>
                    <span className="font-medium">Water Source:</span>{" "}
                    {facilitiesData.water_source}
                  </div>
                )}
              </div>
              <div className="mt-4">
                <span className="font-medium">Amenities: </span>
                {[
                  facilitiesData.internet && "Internet",
                  facilitiesData.swimming_pool && "Swimming Pool",
                  facilitiesData.garden && "Garden",
                  facilitiesData.security && "Security",
                  facilitiesData.storage && "Storage",
                ]
                  .filter(Boolean)
                  .join(", ") || "None"}
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Images ({basePropertyData.images.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {basePropertyData.images.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {basePropertyData.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <div className="min-h-[200px] max-h-[300px] overflow-hidden rounded-lg border bg-muted flex items-center justify-center">
                      <ImageDisplay
                        imageKey={image.key}
                        alt={image.filename}
                        className="max-w-full max-h-full"
                        objectFit="contain"
                        fallback={
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <ImageIcon className="h-8 w-8 text-muted-foreground" />
                          </div>
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {image.filename}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No images uploaded
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  if (!open) return null;

  // show loading state when in edit mode and property data is still loading
  if (mode === "edit" && propertyId && !existingProperty) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg shadow-lg w-full max-w-md p-6">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span>Loading property data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex-shrink-0">
          <div className="flex items-center">
            <h2 className="text-xl font-semibold mb-4 mr-2">
              {mode === "edit" ? "Edit Property" : "Add New Property"}
            </h2>
            {mode === "create" && (draftId || isAutoSaving) && (
              <div className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground animate-in fade-in slide-in-from-left-2">
                {isAutoSaving ? (
                  <>
                    <div className="h-2 w-2 rounded-full bg-yellow-400 animate-pulse" />
                    Saving...
                  </>
                ) : (
                  <>
                    <div className="h-2 w-2 rounded-full bg-green-400" />
                    Draft saved{" "}
                    {lastSaved
                      ? lastSaved.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Minimal Step Indicator */}
          <div className="relative">
            <div
              ref={stepsContainerRef}
              className="flex items-center gap-3 overflow-x-auto py-1 pr-4 scroll-snap-type-x-mandatory"
              style={{
                mask: "linear-gradient(to right, #0000, #ffff var(--left-fade) calc(100% - var(--right-fade)), #0000)",
                WebkitMask:
                  "linear-gradient(to right, #0000, #ffff var(--left-fade) calc(100% - var(--right-fade)), #0000)",
                animation: "scrollfade",
                animationTimeline: "--scrollfade",
                scrollTimeline: "--scrollfade x",
              }}
            >
              {STEPS.map((step, index) => {
                const isActive = step.id === currentStep;
                const isCompleted = step.id < currentStep;
                const isSkipped =
                  step.id === 4 &&
                  selectedPropertyType &&
                  !PROPERTY_TYPES.find((t) => t.value === selectedPropertyType)
                    ?.hasFacilities;
                // in edit mode, all steps are clickable; in create mode, only previous/current steps
                const isClickable =
                  mode === "edit" || step.id <= currentStep || isCompleted;

                return (
                  <div key={step.id} className="flex items-center shrink-0">
                    <button
                      data-step-id={step.id}
                      onClick={() =>
                        !isSkipped && isClickable && navigateToStep(step.id)
                      }
                      disabled={isSkipped || !isClickable}
                      className={`
                        flex items-center space-x-2 py-2 px-3 rounded-lg transition-colors
                        ${
                          isActive
                            ? "bg-primary/10 text-primary"
                            : isCompleted
                              ? "text-green-600 hover:bg-green-50"
                              : isSkipped
                                ? "text-gray-400 cursor-not-allowed"
                                : isClickable
                                  ? "text-gray-600 hover:bg-gray-50"
                                  : "text-gray-400 cursor-not-allowed"
                        }
                      `}
                    >
                      <div
                        className={`
                          w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
                          ${
                            isActive
                              ? "bg-primary text-white"
                              : isCompleted
                                ? "bg-green-500 text-white"
                                : isSkipped
                                  ? "bg-gray-200 text-gray-400"
                                  : "bg-gray-200 text-gray-600"
                          }
                        `}
                      >
                        {isCompleted ? (
                          <Check className="h-3 w-3" />
                        ) : isSkipped ? (
                          "–"
                        ) : (
                          step.id
                        )}
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-medium text-sm">{step.title}</div>
                        <div className="text-xs opacity-70">
                          {isSkipped ? "Skipped" : step.description}
                        </div>
                      </div>
                    </button>
                    {index < STEPS.length - 1 && (
                      <div className="w-4 flex justify-center">
                        <div
                          className={`w-px h-6 ${
                            isCompleted ? "bg-green-300" : "bg-gray-200"
                          }`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">{renderStepContent()}</div>

        <div className="p-6 border-t flex items-center justify-between flex-shrink-0">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? handleCloseClick : prevStep}
            disabled={isSubmitting}
          >
            {currentStep === 1 ? (
              "Cancel"
            ) : (
              <>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </>
            )}
          </Button>

          <div className="flex gap-2">
            {mode === "create" && (
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={
                  isSubmitting ||
                  isManuallySaving ||
                  isAutoSaving ||
                  !basePropertyData.property_name?.trim()
                }
              >
                {isManuallySaving ? "Saving..." : "Save Draft"}
              </Button>
            )}
            {mode === "edit" && (
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={isSubmitting || !propertyId}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            )}
            {currentStep < STEPS.length ? (
              <Button onClick={nextStep} disabled={isSubmitting}>
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting
                  ? mode === "edit"
                    ? "Updating..."
                    : "Creating..."
                  : mode === "edit"
                    ? "Update Property"
                    : "Create Property"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
